// ========================================
// COMPONENTE DE CONSULTA DE PEDIDOS
// src/ConsultaPedidos.js
// Design atualizado para seguir padrão do NovoPedido.jsx
// ========================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Search, Filter, X, ChevronDown, ChevronUp, Calendar, MapPin, FileText, 
  DollarSign, Clock, CheckCircle, XCircle, AlertTriangle, AlertCircle, User, Eye, 
  ArrowLeft, Printer, ShieldCheck, Trophy, ArrowRight, Check, ClipboardList, 
  Building2, Phone, Mail, ShoppingCart, CreditCard, Wallet, BarChart3, 
  Plus, Trash2, RotateCcw, TrendingUp, TrendingDown, Package, FileCheck, ClipboardPen, PackageCheck, Edit2, Receipt, Banknote, Settings,
  Archive, Ban, Copy, Share2, Link, CheckCheck, Repeat
} from 'lucide-react';
import { pedidoAPI, produtoAPI, custoAPI, usuarioAPI, clienteAPI } from './services/api';
import { getStatusConfig, getStatusByScore, STATUS_CONFIG, StatusBadge, getFaseConfig, FASE_CONFIG, FaseBadge, FaseCard } from './config/statusConfig';
import Loading from './components/Loading';
import { imprimirProposta as imprimirPropostaService } from './services/impressaoService';
import { useAuth } from './contexts/AuthContext';
import { useModal } from './contexts/ModalContext';
import { montarModalErro } from './utils/modalErro';

// Lista de formas de pagamento disponíveis (igual NovoPedido)
const FORMAS_PAGAMENTO = [
  { id: 'avista', label: 'À Vista (Dinheiro)', icon: 'Banknote', prazo: 0, exigeObservacao: false },
  { id: 'pix', label: 'PIX', icon: 'Wallet', prazo: 0, exigeObservacao: false },
  { id: 'cartao_debito', label: 'Cartão de Débito', icon: 'CreditCard', prazo: 0, exigeObservacao: false },
  { id: 'cartao_credito', label: 'Cartão de Crédito', icon: 'CreditCard', prazo: 30, exigeObservacao: true },
  { id: 'boleto_avista', label: 'Boleto à Vista', icon: 'Receipt', prazo: 0, exigeObservacao: true },
  { id: 'boleto_prazo', label: 'Boleto à Prazo', icon: 'Receipt', prazo: 30, exigeObservacao: true },
  { id: 'ted_doc', label: 'TED/DOC', icon: 'Wallet', prazo: 0, exigeObservacao: true },
  { id: 'deposito', label: 'Depósito Bancário', icon: 'Wallet', prazo: 0, exigeObservacao: true },
  { id: 'cheque_avista', label: 'Cheque à Vista', icon: 'Receipt', prazo: 0, exigeObservacao: true },
  { id: 'cheque_prazo', label: 'Cheque à Prazo', icon: 'Receipt', prazo: 30, exigeObservacao: true },
  { id: 'entrada_parcelas', label: 'Entrada + Parcelas', icon: 'CreditCard', prazo: 30, exigeObservacao: true },
  { id: 'outro', label: 'Outro (especificar)', icon: 'FileText', prazo: 0, exigeObservacao: true },
];

// ========================================
// FUNÇÃO PARA OBTER STATUS DE PONTUAÇÃO CORRETO
// Se o status salvo for de aprovação (APROVADO, RECUSADO, REANÁLISE),
// recalcula baseado na pontuação
// ========================================
const getStatusPontuacaoCorreto = (statusSalvo, pontuacao) => {
  // Status de aprovação que NÃO são de pontuação
  const statusAprovacao = ['APROVADO', 'RECUSADO', 'REANÁLISE'];
  
  // Se o status salvo é um status de aprovação, recalcula pela pontuação
  if (statusAprovacao.includes(statusSalvo)) {
    return getStatusByScore(pontuacao || 0);
  }
  
  // Se o status já é de pontuação, retorna ele mesmo
  return statusSalvo;
};

const ConsultaPedidos = () => {
  const { usuario: usuarioLogado } = useAuth();
  const { showModal } = useModal();
  const { numeroPedido } = useParams();
  const navigate = useNavigate();
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pedidoSelecionado, setPedidoSelecionado] = useState(null);
  const [dadosCompletos, setDadosCompletos] = useState(null);
  const [loadingDetalhes, setLoadingDetalhes] = useState(false);
  const [processandoFazerPedido, setProcessandoFazerPedido] = useState(false);
  const [processandoCancelar, setProcessandoCancelar] = useState(false);
  const [linkCopiado, setLinkCopiado] = useState(false);
  
  // Estados para edição do pedido (quando solicitada edição)
  const [modoEdicao, setModoEdicao] = useState(false);
  const [processandoEdicao, setProcessandoEdicao] = useState(false);
  const [dadosEdicao, setDadosEdicao] = useState({
    mesmo_endereco_cliente: true,
    endereco_entrega: '',
    bairro_entrega: '',
    cidade_entrega: '',
    estado_entrega: 'Parana',
    tem_taxa_entrega: false,
    valor_taxa_entrega: '',
    observacao_pedido: '',
    forma_pagamento_1: '',
    observacao_pagamento_1: '',
    forma_pagamento_2: '',
    observacao_pagamento_2: '',
    forma_pagamento_3: '',
    observacao_pagamento_3: '',
    forma_pagamento_4: '',
    observacao_pagamento_4: ''
  });
  
  // Estados para edição de itens (forros e acessórios)
  const [forrosEdicao, setForrosEdicao] = useState([]);
  const [acessoriosEdicao, setAcessoriosEdicao] = useState([]);
  const [produtosForro, setProdutosForro] = useState([]);
  const [acessoriosLista, setAcessoriosLista] = useState([]);
  const [custosLista, setCustosLista] = useState([]);
  const [carregandoProdutos, setCarregandoProdutos] = useState(false);
  
  // Estados para confirmação de edição
  const [mostrarConfirmacaoEdicao, setMostrarConfirmacaoEdicao] = useState(false);
  const [dadosOriginais, setDadosOriginais] = useState(null); // Para comparar alterações
  const [forrosOriginais, setForrosOriginais] = useState([]);
  const [acessoriosOriginais, setAcessoriosOriginais] = useState([]);
  
  const [filtros, setFiltros] = useState({ busca: '', tipo: '', fase: '', scoreVenda: '', responsavel: '', dataInicio: '', dataFim: '', dataFaturamentoInicio: '', dataFaturamentoFim: '', produtosEspeciais: '' });
  // Texto da busca com atraso: evita refiltrar a lista inteira a cada tecla
  const [buscaDebounced, setBuscaDebounced] = useState('');
  // Mensagem de falha ao carregar — sem isso um erro virava lista vazia
  // e o usuário lia "Nenhum pedido encontrado" achando que os pedidos sumiram
  const [erroCarregamento, setErroCarregamento] = useState(null);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [listaResponsaveis, setListaResponsaveis] = useState([]);
  const [quantidadeVisivel, setQuantidadeVisivel] = useState(10);
  
  // Estados para alteração de responsável
  const [listaUsuariosAtivos, setListaUsuariosAtivos] = useState([]);
  const [alterandoResponsavel, setAlterandoResponsavel] = useState(false);
  
  // Estados para alteração de status (Admin/Diretoria)
  const [mostrarModalStatus, setMostrarModalStatus] = useState(false);
  const [novoStatusSelecionado, setNovoStatusSelecionado] = useState('');
  const [alterandoStatus, setAlterandoStatus] = useState(false);

  // Estados para troca de cliente do pedido
  const [mostrarTrocaCliente, setMostrarTrocaCliente] = useState(false);
  const [cnpjTroca, setCnpjTroca] = useState('');
  const [clienteEncontrado, setClienteEncontrado] = useState(null);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [clienteTrocado, setClienteTrocado] = useState(false);
  const [novoClienteId, setNovoClienteId] = useState(null);
  const [clienteOriginalNome, setClienteOriginalNome] = useState(null);

  // Estados de expansão das seções (mesmo padrão do NovoPedido)
  const [expandido, setExpandido] = useState({
    proposta: true,
    cliente: true,
    entrega: true,
    pedido: true,
    resumo: true,
    pagamento: true,
    analiseCredito: true,
    admin: true,
    observacao: true
  });

  const toggleExpandir = (secao) => {
    setExpandido(prev => ({ ...prev, [secao]: !prev[secao] }));
  };

  const podeVerTodos = () => {
    if (!usuarioLogado) return true;
    const departamento = (usuarioLogado.departamento || '').toLowerCase().trim();
    // Admin, Diretoria, Administrativo ou departamento vazio = vê tudo
    const deptAcessoTotal = ['admin', 'diretoria', 'administrador', 'administrativo', 'todos', ''];
    return deptAcessoTotal.includes(departamento);
  };

  // Função para verificar se pode alterar o responsável do pedido
  const podeAlterarResponsavel = () => {
    if (!usuarioLogado) return false;
    return usuarioLogado.permissoes?.pode_alterar_responsavel === true;
  };

  const podeVerVisaoAdmin = () => {
    if (!usuarioLogado) return false;
    const dep = (usuarioLogado.departamento || '').toLowerCase().trim();
    // Somente Admin e Diretoria vêem a visão administrativa (custos, lucro, margem)
    return dep === 'admin' || dep === 'diretoria';
  };

  // Função para verificar se pode ver métricas (% acabamento, pontuação por tabela)
  // Admin, Diretoria, Vendas e Administrativo podem ver
  const podeVerMetricas = () => {
    if (!usuarioLogado) return false;
    const dep = (usuarioLogado.departamento || '').toLowerCase().trim();
    return dep === 'admin' || dep === 'diretoria' || dep === 'vendas' || dep === 'administrador' || dep === 'administrativo';
  };

  // Função para verificar se pode ver a tag de limite excedido
  const podeVerLimiteExcedido = () => {
    if (!usuarioLogado) return false;
    const dep = (usuarioLogado.departamento || '').toLowerCase().trim();
    // Admin, Diretoria e Administrativo podem ver
    return dep === 'admin' || dep === 'diretoria' || dep === 'administrador' || dep === 'administrativo';
  };

  // Função para verificar se pode aprovar rápido (fluxo automático + valor < R$ 10.000)
  const podeAprovarRapido = () => {
    if (!usuarioLogado || !dadosCompletos) return false;
    
    // Verificar se tem fluxo automático habilitado
    const temFluxoAutomatico = usuarioLogado.fluxo_automatico === true || usuarioLogado.fluxo_automatico === 'true';
    if (!temFluxoAutomatico) return false;
    
    // Calcular valor total do pedido
    const valorTotal = (parseFloat(dadosCompletos.valor_tabela1) || 0) + 
                       (parseFloat(dadosCompletos.valor_tabela2) || 0) + 
                       (parseFloat(dadosCompletos.valor_produtos_especiais) || 0);
    
    // Limite de R$ 10.000
    return valorTotal < 10000;
  };

  // Verifica se o pedido é exclusivamente de Produtos Especiais (sem itens nas tabelas normais)
  const isVendaSomenteEspeciais = () => {
    if (!dadosCompletos) return false;
    const tab1 = parseFloat(dadosCompletos.valor_tabela1) || 0;
    const tab2 = parseFloat(dadosCompletos.valor_tabela2) || 0;
    const especiais = parseFloat(dadosCompletos.valor_produtos_especiais) || 0;
    return tab1 === 0 && tab2 === 0 && especiais > 0;
  };

  // Função para verificar se pode gerenciar pedido (aprovar, arquivar, cancelar orçamento)
  const podeGerenciarOrcamento = () => {
    return podeVerLimiteExcedido() || podeAprovarRapido();
  };

  const getNomeUsuario = () => {
    if (!usuarioLogado) return '';
    return usuarioLogado.nome || usuarioLogado.nome_completo || usuarioLogado.username || usuarioLogado.username_ldap || '';
  };

  useEffect(() => {
    // usuário vem do AuthContext; quando estiver disponível, carrega pedidos
    if (usuarioLogado) {
      carregarPedidos();
      // Carregar custos para cálculo de margem exigida e pontuação (necessário no modo VIEW)
      custoAPI.listarTodos().then(data => setCustosLista(data)).catch(err => console.error('Erro ao carregar custos:', err));
      // Carregar lista de usuários para quem pode alterar responsável
      if (podeVerTodos() || podeAlterarResponsavel()) {
        carregarUsuariosAtivos();
      }
    }
  }, [usuarioLogado]);
  
  const carregarUsuariosAtivos = async () => {
    try {
      const usuarios = await usuarioAPI.listarAtivos();
      setListaUsuariosAtivos(usuarios);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  // Verificar se tem pedido para abrir automaticamente (via URL ou localStorage)
  useEffect(() => {
    // Prioridade 1: número do pedido vindo da URL (ex: /consulta-pedidos/2026-02-0002)
    if (numeroPedido && pedidos.length > 0) {
      // Tentar encontrar pelo numero_proposta primeiro
      let pedidoEncontrado = pedidos.find(p => p.numero_proposta === numeroPedido);
      
      // Se não encontrou, tentar pelo ID (caso venha de links antigos)
      if (!pedidoEncontrado) {
        pedidoEncontrado = pedidos.find(p => String(p.id) === String(numeroPedido));
      }
      
      if (pedidoEncontrado) {
        carregarDetalhesPedido(pedidoEncontrado);
      }
      return;
    }
    
    // Prioridade 2: pedido armazenado no localStorage (legado)
    const pedidoParaAbrir = localStorage.getItem('pedidoParaAbrir');
    if (pedidoParaAbrir && pedidos.length > 0) {
      // Comparar como string para garantir compatibilidade
      const pedidoEncontrado = pedidos.find(p => String(p.id) === String(pedidoParaAbrir));
      if (pedidoEncontrado) {
        carregarDetalhesPedido(pedidoEncontrado);
        // Atualizar URL para refletir o pedido aberto
        navigate(`/consulta-pedidos/${pedidoEncontrado.numero_proposta}`, { replace: true });
      }
      localStorage.removeItem('pedidoParaAbrir');
    }
  }, [pedidos, numeroPedido]);

  const carregarPedidos = async () => {
    setLoading(true);
    setErroCarregamento(null);
    const usuario = usuarioLogado;

    try {
      const data = await pedidoAPI.listar();

      if (!data) {
        // Resposta sem lista é falha de servidor, não "nenhum pedido"
        throw new Error('O servidor não retornou a lista de pedidos.');
      }

      const responsaveisUnicos = [...new Set(data.map(p => p.vendedor).filter(v => v))];
      setListaResponsaveis(responsaveisUnicos.sort());

      const departamento = (usuario?.departamento || '').toLowerCase().trim();
      const cargo = (usuario?.cargo || '').toLowerCase().trim();
      const deptAcessoTotal = ['admin', 'diretoria', 'administrador', 'administrativo', 'todos', ''];
      const cargoAcessoTotal = ['admin', 'administrador', 'diretor', 'diretoria', 'gerente'];
      const temAcessoTotal = deptAcessoTotal.includes(departamento) || cargoAcessoTotal.includes(cargo);

      if (temAcessoTotal || !usuario) {
        setPedidos(data);
      } else {
        const nomeUsuario = (usuario.nome || usuario.nome_completo || '').trim().toLowerCase();
        const usernameUsuario = (usuario.username || usuario.username_ldap || '').trim().toLowerCase();

        // Se não dá para identificar o usuário, confiar no filtro que o backend
        // já aplicou. Antes o filtro local comparava com string vazia e zerava a
        // lista, fazendo parecer que o vendedor não tinha nenhum pedido.
        if (!nomeUsuario && !usernameUsuario) {
          setPedidos(data);
        } else {
          setPedidos(data.filter(p => {
            const vendedorPedido = (p.vendedor || '').trim().toLowerCase();
            return vendedorPedido === nomeUsuario || vendedorPedido === usernameUsuario;
          }));
        }
      }
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
      setPedidos([]);
      setErroCarregamento(error?.message || 'Não foi possível carregar os pedidos.');
    }
    setLoading(false);
  };

  const carregarDetalhesPedido = async (pedido) => {
    setLoadingDetalhes(true);
    setPedidoSelecionado(pedido);
    
    // Scroll para o topo ao abrir detalhes do pedido
    window.scrollTo({ top: 0, behavior: 'instant' });
    
    // Atualizar a URL com o número do pedido
    if (pedido.numero_proposta) {
      navigate(`/consulta-pedidos/${pedido.numero_proposta}`, { replace: true });
    }
    
    try {
      // As duas chamadas em paralelo — antes eram sequenciais e somavam a espera.
      // A lista traz só os campos do card, então os dados completos do pedido
      // precisam obrigatoriamente vir de buscarPorId: se ela falhar, mostramos
      // erro em vez de abrir a tela de detalhes com campos faltando.
      const [itensResult, dadosAtualizados] = await Promise.all([
        pedidoAPI.buscarItens(pedido.id),
        pedidoAPI.buscarPorId(pedido.id)
      ]);

      if (!dadosAtualizados) {
        throw new Error('Pedido não encontrado.');
      }

      setDadosCompletos({
        ...dadosAtualizados,
        forros: itensResult.forros || [],
        acessorios: itensResult.acessorios || [],
        podeVerCustos: itensResult.podeVerCustos,
        produtosEspeciais: dadosAtualizados.produtosEspeciais || []
      });
    } catch (error) {
      console.error('Erro ao abrir pedido:', error);
      setPedidoSelecionado(null);
      setDadosCompletos(null);
      navigate('/consulta-pedidos', { replace: true });
      showModal({
        type: 'error',
        title: 'Não foi possível abrir o pedido',
        message: error?.message || 'Tente novamente em instantes.',
        confirmText: 'OK',
        showCancel: false
      });
    }
    setLoadingDetalhes(false);
  };

  // ========================================
  // ALTERAR RESPONSÁVEL DO PEDIDO
  // ========================================
  const alterarResponsavel = async (novoResponsavel) => {
    if (!dadosCompletos) return;
    
    setAlterandoResponsavel(true);
    try {
      const response = await pedidoAPI.atualizarResponsavel(dadosCompletos.id, novoResponsavel);
      if (response.success) {
        // Atualiza os dados locais
        setDadosCompletos(prev => ({ ...prev, vendedor: novoResponsavel }));
        setPedidos(prev => prev.map(p => 
          p.id === dadosCompletos.id ? { ...p, vendedor: novoResponsavel } : p
        ));
        showModal({
          type: 'success',
          title: 'Sucesso!',
          message: 'Responsável alterado com sucesso.',
          confirmText: 'OK',
          showCancel: false
        });
      } else {
        throw new Error(response.message || 'Erro ao alterar responsável');
      }
    } catch (error) {
      console.error('Erro ao alterar responsável:', error);
      showModal({
        type: 'error',
        title: 'Erro',
        message: error.message || 'Erro ao alterar responsável.',
        confirmText: 'OK',
        showCancel: false
      });
    } finally {
      setAlterandoResponsavel(false);
    }
  };

  // ========================================
  // LISTA DE STATUS/FASES DISPONÍVEIS
  // ========================================
  const LISTA_STATUS_DISPONIVEIS = [
    { value: 'AGUARDANDO_PROXIMA_ETAPA', label: 'Aguardando Próxima Etapa' },
    { value: 'ORCAMENTO_APROVADO', label: 'Orçamento Aprovado' },
    { value: 'ORCAMENTO_APROVADO_AUTOMATICAMENTE', label: 'Aprovado Automaticamente' },
    { value: 'APROVADO_COM_AJUSTES', label: 'Aprovado com Ajustes' },
    { value: 'SOLICITAR_EDICAO', label: 'Solicitada Edição' },
    { value: 'ORCAMENTO_RECUSADO', label: 'Orçamento Recusado' },
    { value: 'ORCAMENTO_CANCELADO', label: 'Orçamento Cancelado' },
    { value: 'EM_PROCESSAMENTO', label: 'Em Processamento' },
    { value: 'PEDIDO_FATURADO', label: 'Pedido Faturado' },
    { value: 'PEDIDO_CANCELADO', label: 'Pedido Cancelado' },
    { value: 'PEDIDO_CANCELADO_POS_FATURAMENTO', label: 'Cancelado Após Faturamento' }
  ];

  // ========================================
  // ALTERAR STATUS DO PEDIDO (Admin/Diretoria)
  // ========================================
  const alterarStatusPedido = async () => {
    if (!dadosCompletos || !novoStatusSelecionado) return;
    
    setAlterandoStatus(true);
    try {
      const response = await pedidoAPI.atualizarStatus(dadosCompletos.id, novoStatusSelecionado);
      if (response.success) {
        // AGUARDANDO_PROXIMA_ETAPA - reseta TODO o fluxo
        const deveResetarTudo = novoStatusSelecionado === 'AGUARDANDO_PROXIMA_ETAPA';
        
        // Status que voltam para orçamento mas mantém dados de aprovação
        const statusVoltaParaOrcamento = ['ORCAMENTO_APROVADO', 'APROVADO_COM_AJUSTES', 'SOLICITAR_EDICAO', 'ORCAMENTO_RECUSADO'];
        const deveVoltarParaOrcamento = statusVoltaParaOrcamento.includes(novoStatusSelecionado);

        // Atualiza os dados locais
        if (deveResetarTudo) {
          // AGUARDANDO_PROXIMA_ETAPA: Reseta TODO o fluxo - volta ao início
          // Limpa TODOS os dados de aprovação, cancelamento, edição e histórico
          setDadosCompletos(prev => ({ 
            ...prev, 
            fase_atual: novoStatusSelecionado,
            tipo_documento: 'ORCAMENTO',
            pedido_confirmado_por: null,
            pedido_confirmado_data: null,
            aprovacao_orcamento_por: null,
            aprovacao_orcamento_data: null,
            aprovacao_orcamento_observacao: null,
            aprovacao_orcamento_parecer: null,
            cancelado_por: null,
            cancelado_data: null,
            foi_editado: false,
            historico_edicoes: [],
            ultima_edicao_data: null,
            ultima_edicao_usuario: null
          }));
          setPedidos(prev => prev.map(p => 
            p.id === dadosCompletos.id ? { 
              ...p, 
              fase_atual: novoStatusSelecionado,
              tipo_documento: 'ORCAMENTO'
            } : p
          ));
        } else if (deveVoltarParaOrcamento) {
          // Outros status de orçamento: volta tipo_documento, limpa cancelamento e edição
          setDadosCompletos(prev => ({ 
            ...prev, 
            fase_atual: novoStatusSelecionado,
            tipo_documento: 'ORCAMENTO',
            pedido_confirmado_por: null,
            pedido_confirmado_data: null,
            cancelado_por: null,
            cancelado_data: null,
            foi_editado: false,
            historico_edicoes: [],
            ultima_edicao_data: null,
            ultima_edicao_usuario: null
          }));
          setPedidos(prev => prev.map(p => 
            p.id === dadosCompletos.id ? { 
              ...p, 
              fase_atual: novoStatusSelecionado,
              tipo_documento: 'ORCAMENTO'
            } : p
          ));
        } else {
          // Cancelamento ou outros - apenas muda o status
          setDadosCompletos(prev => ({ ...prev, fase_atual: novoStatusSelecionado }));
          setPedidos(prev => prev.map(p => 
            p.id === dadosCompletos.id ? { ...p, fase_atual: novoStatusSelecionado } : p
          ));
        }
        
        setMostrarModalStatus(false);
        setNovoStatusSelecionado('');
        showModal({
          type: 'success',
          title: 'Sucesso!',
          message: 'Status do pedido alterado com sucesso.',
          confirmText: 'OK',
          showCancel: false
        });
      } else {
        throw new Error(response.message || 'Erro ao alterar status');
      }
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      showModal(montarModalErro(error, { titulo: 'Erro' }));
    } finally {
      setAlterandoStatus(false);
    }
  };

  // ========================================
  // CONFIRMAR ALTERAÇÃO DE STATUS (Com mensagem de aviso)
  // ========================================
  const confirmarAlteracaoStatus = () => {
    if (!dadosCompletos || !novoStatusSelecionado) return;

    // Status de cancelamento - apenas fecha o fluxo, não reseta
    const statusDeCancelamento = ['ORCAMENTO_CANCELADO', 'PEDIDO_CANCELADO', 'PEDIDO_CANCELADO_POS_FATURAMENTO'];
    const isCancelamento = statusDeCancelamento.includes(novoStatusSelecionado);

    // Obter o label do status selecionado
    const statusLabel = LISTA_STATUS_DISPONIVEIS.find(s => s.value === novoStatusSelecionado)?.label || novoStatusSelecionado;

    if (isCancelamento) {
      // Para cancelamento, apenas confirma o fechamento do fluxo
      showModal({
        type: 'warning',
        title: 'Confirmar Cancelamento',
        message: `Deseja realmente alterar o status para "${statusLabel}"? O fluxo será encerrado.`,
        confirmText: 'Sim, Cancelar',
        cancelText: 'Não',
        showCancel: true,
        onConfirm: alterarStatusPedido
      });
    } else {
      // Para outros status, avisa que vai resetar o fluxo
      showModal({
        type: 'warning',
        title: 'Confirmar Alteração de Status',
        message: `Deseja realmente alterar o status do pedido para "${statusLabel}"? Isso vai resetar todo o fluxo.`,
        confirmText: 'Sim, Alterar',
        cancelText: 'Não',
        showCancel: true,
        onConfirm: alterarStatusPedido
      });
    }
  };

  // ========================================
  // AÇÕES RÁPIDAS - Confirmar Pedido, Arquivar, Cancelar
  // ========================================
  const [executandoAcao, setExecutandoAcao] = useState(false);
  const [modalCancelarPedido, setModalCancelarPedido] = useState(false);
  const [observacaoCancelamento, setObservacaoCancelamento] = useState('');
  // Tipo do cancelamento aberto no modal: 'normal' (proposta/pedido confirmado) ou 'pos_faturamento'
  const [tipoCancelamentoModal, setTipoCancelamentoModal] = useState('normal');

  // ===== Modal Finalizar Pedido (faturamento) =====
  // Helpers para obter hoje/agora no fuso de São Paulo no formato exigido pelos inputs
  const obterDataHojeSP = () => {
    const partes = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(new Date());
    const obj = {};
    partes.forEach(p => { obj[p.type] = p.value; });
    return `${obj.year}-${obj.month}-${obj.day}`; // YYYY-MM-DD
  };
  const obterHoraAgoraSP = () => {
    const partes = new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      hour: '2-digit', minute: '2-digit', hour12: false
    }).formatToParts(new Date());
    const obj = {};
    partes.forEach(p => { obj[p.type] = p.value; });
    return `${obj.hour}:${obj.minute}`; // HH:mm
  };

  const [modalFinalizarPedido, setModalFinalizarPedido] = useState(false);
  const [dataFaturamento, setDataFaturamento] = useState('');
  const [horaFaturamento, setHoraFaturamento] = useState('');
  const [observacaoFaturamento, setObservacaoFaturamento] = useState('');

  // ===== Modal de Seleção de Pedidos para Faturamento em Lote =====
  const [modalSelecionarLote, setModalSelecionarLote] = useState(false);
  const [carregandoPedidosCliente, setCarregandoPedidosCliente] = useState(false);
  const [pedidosConfirmadosCliente, setPedidosConfirmadosCliente] = useState([]);
  const [pedidosLoteSelecionados, setPedidosLoteSelecionados] = useState([]); // ids

  // Confirmar Pedido (transforma em Pedido)
  const handleConfirmarPedido = () => {
    if (!dadosCompletos) return;
    
    showModal({
      type: 'warning',
      title: 'Confirmar Pedido',
      message: 'Deseja realmente confirmar este orçamento como PEDIDO? O tipo será alterado para "Pedido" e o status será "Pedido Confirmado".',
      confirmText: 'Sim, Confirmar',
      cancelText: 'Cancelar',
      showCancel: true,
      onConfirm: async () => {
        setExecutandoAcao(true);
        try {
          const response = await pedidoAPI.confirmarPedido(dadosCompletos.id);
          if (response.success) {
            // Pegar nome do usuário logado para exibir imediatamente
            const nomeConfirmador = usuarioLogado?.nome || usuarioLogado?.nome_completo || usuarioLogado?.username || 'Sistema';
            
            setDadosCompletos(prev => ({
              ...prev,
              tipo_documento: 'PEDIDO',
              fase_atual: 'PEDIDO_CONFIRMADO',
              pedido_confirmado_por: response.pedido?.pedido_confirmado_por || nomeConfirmador,
              pedido_confirmado_data: response.pedido?.pedido_confirmado_data || new Date().toISOString()
            }));
            showModal({
              type: 'success',
              title: 'Sucesso!',
              message: 'Pedido confirmado com sucesso!',
              confirmText: 'OK',
              showCancel: false
            });
            carregarPedidos();
          }
        } catch (error) {
          showModal({
            type: 'error',
            title: 'Erro',
            message: error.message || 'Erro ao confirmar pedido.',
            confirmText: 'OK',
            showCancel: false
          });
        } finally {
          setExecutandoAcao(false);
        }
      }
    });
  };

  // Arquivar Proposta
  const handleArquivarProposta = () => {
    if (!dadosCompletos) return;
    
    showModal({
      type: 'warning',
      title: 'Arquivar Proposta',
      message: 'Deseja realmente arquivar esta proposta? O status será alterado para "Arquivado".',
      confirmText: 'Sim, Arquivar',
      cancelText: 'Cancelar',
      showCancel: true,
      onConfirm: async () => {
        setExecutandoAcao(true);
        try {
          const response = await pedidoAPI.arquivarProposta(dadosCompletos.id);
          if (response.success) {
            // Atualizar com todos os dados do arquivamento
            setDadosCompletos(prev => ({
              ...prev,
              tipo_documento: 'ORCAMENTO',
              fase_atual: 'ARQUIVADO',
              arquivado_por: usuarioLogado?.nome || 'Sistema',
              data_arquivamento: new Date().toISOString()
            }));
            showModal({
              type: 'success',
              title: 'Sucesso!',
              message: 'Proposta arquivada com sucesso!',
              confirmText: 'OK',
              showCancel: false
            });
            carregarPedidos();
          }
        } catch (error) {
          showModal({
            type: 'error',
            title: 'Erro',
            message: error.message || 'Erro ao arquivar proposta.',
            confirmText: 'OK',
            showCancel: false
          });
        } finally {
          setExecutandoAcao(false);
        }
      }
    });
  };

  // Cancelar Proposta - Abre modal com observação obrigatória
  const handleCancelarProposta = () => {
    if (!dadosCompletos) return;
    setObservacaoCancelamento('');
    setModalCancelarPedido(true);
  };

  // Finalizar Pedido (quando já é PEDIDO_CONFIRMADO):
  // 1) busca todos os pedidos PEDIDO_CONFIRMADO do mesmo cliente
  // 2) se houver outros além do atual → abre modal de seleção
  // 3) se só houver o atual → vai direto para o modal de data/hora
  const handleFinalizarPedido = async () => {
    if (!dadosCompletos) return;

    const clienteId = dadosCompletos.cliente_id || dadosCompletos.clientes?.id;
    if (!clienteId) {
      // Fallback: sem cliente_id não dá para listar outros pedidos — abre direto o modal de data/hora
      setPedidosLoteSelecionados([dadosCompletos.id]);
      setPedidosConfirmadosCliente([]);
      setDataFaturamento(obterDataHojeSP());
      setHoraFaturamento(obterHoraAgoraSP());
      setObservacaoFaturamento('');
      setModalFinalizarPedido(true);
      return;
    }

    setCarregandoPedidosCliente(true);
    try {
      const response = await pedidoAPI.listarConfirmadosPorCliente(clienteId);
      const lista = (response?.pedidos || []).filter(p => p.fase_atual === 'PEDIDO_CONFIRMADO');

      // Garante que o atual está na lista (caso o backend não o devolva por algum filtro)
      const temAtual = lista.some(p => p.id === dadosCompletos.id);
      const listaFinal = temAtual ? lista : [
        {
          id: dadosCompletos.id,
          numero_proposta: dadosCompletos.numero_proposta,
          vendedor: dadosCompletos.vendedor,
          valor_tabela1: dadosCompletos.valor_tabela1,
          valor_tabela2: dadosCompletos.valor_tabela2,
          valor_produtos_especiais: dadosCompletos.valor_produtos_especiais,
          valor_total: (parseFloat(dadosCompletos.valor_tabela1) || 0)
                     + (parseFloat(dadosCompletos.valor_tabela2) || 0)
                     + (parseFloat(dadosCompletos.valor_produtos_especiais) || 0),
          pontuacao_tabela1: dadosCompletos.pontuacao_tabela1,
          pontuacao_tabela2: dadosCompletos.pontuacao_tabela2,
          status_geral: dadosCompletos.status_geral,
          pontuacao_geral: dadosCompletos.pontuacao_geral,
          created_at: dadosCompletos.created_at,
          fase_atual: 'PEDIDO_CONFIRMADO'
        },
        ...lista
      ];

      setPedidosConfirmadosCliente(listaFinal);
      setPedidosLoteSelecionados([dadosCompletos.id]); // atual sempre marcado

      if (listaFinal.length > 1) {
        // Tem outros pedidos confirmados → abre modal de seleção primeiro
        setModalSelecionarLote(true);
      } else {
        // Só o atual → pula direto para o modal de data/hora
        setDataFaturamento(obterDataHojeSP());
        setHoraFaturamento(obterHoraAgoraSP());
        setObservacaoFaturamento('');
        setModalFinalizarPedido(true);
      }
    } catch (error) {
      showModal({
        type: 'error',
        title: 'Erro',
        message: error.message || 'Erro ao carregar pedidos do cliente.',
        confirmText: 'OK',
        showCancel: false
      });
    } finally {
      setCarregandoPedidosCliente(false);
    }
  };

  // Alterna seleção de um pedido (o atual nunca pode ser desmarcado)
  const togglePedidoLote = (pedidoId) => {
    if (pedidoId === dadosCompletos?.id) return;
    setPedidosLoteSelecionados(prev =>
      prev.includes(pedidoId)
        ? prev.filter(id => id !== pedidoId)
        : [...prev, pedidoId]
    );
  };

  // Avança da seleção de pedidos para o modal de data/hora
  const avancarParaDataHora = () => {
    if (pedidosLoteSelecionados.length === 0) return;
    setModalSelecionarLote(false);
    setDataFaturamento(obterDataHojeSP());
    setHoraFaturamento(obterHoraAgoraSP());
    setObservacaoFaturamento('');
    setModalFinalizarPedido(true);
  };

  const confirmarFinalizarPedido = async () => {
    const hoje = obterDataHojeSP();
    const dataDiferenteDeHoje = dataFaturamento && dataFaturamento !== hoje;

    if (dataDiferenteDeHoje && !observacaoFaturamento.trim()) {
      showModal({
        type: 'error',
        title: 'Observação Obrigatória',
        message: 'Como a data de faturamento é diferente de hoje, é obrigatório informar o motivo.',
        confirmText: 'OK',
        showCancel: false
      });
      return;
    }

    // Garante que ao menos o pedido atual esteja na lista
    const idsParaFaturar = pedidosLoteSelecionados.length > 0
      ? pedidosLoteSelecionados
      : [dadosCompletos.id];

    setExecutandoAcao(true);
    try {
      const response = await pedidoAPI.faturarLote({
        pedido_ids: idsParaFaturar,
        data_faturamento: dataFaturamento,
        hora_faturamento: horaFaturamento,
        observacao: observacaoFaturamento.trim()
      });
      if (response.success) {
        // Atualiza o pedido atual na tela com os dados retornados (se presente)
        const pedidoAtualAtualizado = (response.pedidos || []).find(p => p.id === dadosCompletos.id);
        setDadosCompletos(prev => ({
          ...prev,
          fase_atual: 'PEDIDO_FATURADO',
          status: 'Pedido Faturado',
          faturado_por: pedidoAtualAtualizado?.faturado_por || prev.faturado_por,
          faturado_data: pedidoAtualAtualizado?.faturado_data || new Date().toISOString(),
          faturamento_observacao: pedidoAtualAtualizado?.faturamento_observacao || null
        }));
        setModalFinalizarPedido(false);
        setPedidosLoteSelecionados([]);
        setPedidosConfirmadosCliente([]);
        showModal({
          type: 'success',
          title: 'Sucesso!',
          message: idsParaFaturar.length === 1
            ? 'Pedido faturado com sucesso!'
            : `${idsParaFaturar.length} pedidos faturados com sucesso!`,
          confirmText: 'OK',
          showCancel: false
        });
        carregarPedidos();
      }
    } catch (error) {
      // Mensagem específica quando o status de algum pedido foi alterado em paralelo
      const isStatusAlterado = error?.response?.data?.code === 'STATUS_ALTERADO'
        || error?.code === 'STATUS_ALTERADO'
        || /status.*alterad/i.test(error?.message || '');
      showModal({
        type: 'error',
        title: isStatusAlterado ? 'Tente Novamente' : 'Erro',
        message: isStatusAlterado
          ? 'O status de um ou mais pedidos foi alterado durante o faturamento. Atualize a tela e tente novamente.'
          : (error?.message || 'Erro ao finalizar pedido.'),
        confirmText: 'OK',
        showCancel: false
      });
    } finally {
      setExecutandoAcao(false);
    }
  };

  // Cancelar Pedido (quando já é PEDIDO_CONFIRMADO) - Abre modal com observação obrigatória
  const handleCancelarPedido = () => {
    if (!dadosCompletos) return;
    setTipoCancelamentoModal('normal');
    setObservacaoCancelamento('');
    setModalCancelarPedido(true);
  };

  // Permite cancelar pedido FATURADO (apenas Admin/Diretoria/Administrativo)
  const podeCancelarFaturado = () => {
    if (!usuarioLogado) return false;
    const dep = (usuarioLogado.departamento || '').toLowerCase().trim();
    return ['admin', 'administrador', 'diretoria', 'administrativo'].includes(dep);
  };

  // Cancelar Pedido APÓS o faturamento - Abre modal com observação obrigatória
  const handleCancelarPedidoFaturado = () => {
    if (!dadosCompletos) return;
    if (!podeCancelarFaturado()) {
      showModal({
        type: 'warning',
        title: 'Sem permissão',
        message: 'Apenas Admin, Diretoria ou Administrativo podem cancelar pedidos faturados.',
        confirmText: 'OK',
        showCancel: false
      });
      return;
    }
    setTipoCancelamentoModal('pos_faturamento');
    setObservacaoCancelamento('');
    setModalCancelarPedido(true);
  };

  // Confirmar cancelamento de pedido/proposta com observação
  const confirmarCancelamentoPedido = async () => {
    if (!observacaoCancelamento.trim()) {
      showModal({
        type: 'error',
        title: 'Observação Obrigatória',
        message: 'Por favor, informe o motivo do cancelamento.',
        confirmText: 'OK',
        showCancel: false
      });
      return;
    }

    setExecutandoAcao(true);
    try {
      // Detectar tipo do cancelamento
      const isPosFaturamento = tipoCancelamentoModal === 'pos_faturamento';
      const isPedidoConfirmado = dadosCompletos.tipo_documento === 'PEDIDO' && dadosCompletos.fase_atual === 'PEDIDO_CONFIRMADO';

      let response;
      let novaFase;
      let mensagemSucesso;

      if (isPosFaturamento) {
        response = await pedidoAPI.cancelarPedidoFaturado(dadosCompletos.id, observacaoCancelamento);
        novaFase = 'PEDIDO_CANCELADO_POS_FATURAMENTO';
        mensagemSucesso = 'Pedido cancelado após faturamento com sucesso!';
      } else if (isPedidoConfirmado) {
        response = await pedidoAPI.cancelarPedidoConfirmado(dadosCompletos.id, observacaoCancelamento);
        novaFase = 'PEDIDO_CANCELADO';
        mensagemSucesso = 'Pedido cancelado com sucesso!';
      } else {
        response = await pedidoAPI.cancelarProposta(dadosCompletos.id, observacaoCancelamento);
        novaFase = 'CANCELADO';
        mensagemSucesso = 'Proposta cancelada com sucesso!';
      }

      if (response.success) {
        // Atualizar com todos os dados do cancelamento
        setDadosCompletos(prev => ({
          ...prev,
          fase_atual: novaFase,
          observacao_cancelamento: observacaoCancelamento.trim(),
          cancelado_por: usuarioLogado?.nome || 'Sistema',
          data_cancelamento: new Date().toISOString()
        }));
        setModalCancelarPedido(false);
        showModal({
          type: 'success',
          title: 'Sucesso!',
          message: mensagemSucesso,
          confirmText: 'OK',
          showCancel: false
        });
        carregarPedidos();
      }
    } catch (error) {
      showModal(montarModalErro(error, { titulo: 'Erro' }));
    } finally {
      setExecutandoAcao(false);
    }
  };

  // ========================================
  // FAZER PEDIDO - Transforma orçamento aprovado em pedido
  // ========================================
  const fazerPedido = async () => {
    if (!dadosCompletos || !usuarioLogado) return;
    
    // Verificar se pode fazer pedido (fase deve ser ORCAMENTO_APROVADO ou APROVADO_COM_AJUSTES)
    const fasesPermitidas = ['ORCAMENTO_APROVADO', 'APROVADO_COM_AJUSTES'];
    if (!fasesPermitidas.includes(dadosCompletos.fase_atual)) {
      showModal({
        type: 'warning',
        title: 'Ação não permitida',
        message: 'Este orçamento não está aprovado para virar pedido.',
        confirmText: 'OK',
        showCancel: false
      });
      return;
    }

    // Verificar se é o responsável pelo orçamento
    const nomeUsuario = (usuarioLogado.nome || usuarioLogado.nome_completo || '').trim().toLowerCase();
    const vendedorPedido = (dadosCompletos.vendedor || '').trim().toLowerCase();
    const isResponsavel = nomeUsuario === vendedorPedido;
    
    // Ou Admin/Diretoria/Administrativo
    const dep = (usuarioLogado.departamento || '').toLowerCase().trim();
    const temPermissaoAdmin = dep === 'admin' || dep === 'diretoria' || dep === 'administrativo';
    
    if (!isResponsavel && !temPermissaoAdmin) {
      showModal({
        type: 'warning',
        title: 'Ação não permitida',
        message: 'Apenas o responsável pelo orçamento ou Admin/Diretoria pode fazer o pedido.',
        confirmText: 'OK',
        showCancel: false
      });
      return;
    }

    setProcessandoFazerPedido(true);
    const nomeConfirmador = usuarioLogado.nome || usuarioLogado.nome_completo || usuarioLogado.username;

    try {
      const updateData = {
        tipo_documento: 'PEDIDO',
        fase_atual: 'EM_PROCESSAMENTO',
        pedido_confirmado_por: nomeConfirmador,
        pedido_confirmado_data: new Date().toLocaleString('sv-SE', { timeZone: 'America/Sao_Paulo' }).replace(' ', 'T'),
        status: 'Em Processamento'
      };

      await pedidoAPI.atualizar(dadosCompletos.id, updateData);

      showModal({
        type: 'success',
        title: 'Pedido Confirmado!',
        message: `O orçamento ${dadosCompletos.numero_proposta} foi transformado em PEDIDO e enviado para processamento!`,
        confirmText: 'OK',
        showCancel: false
      });

      // Atualizar dados locais
      setDadosCompletos(prev => ({
        ...prev,
        ...updateData
      }));

      // Recarregar lista
      carregarPedidos();

    } catch (error) {
      console.error('Erro ao fazer pedido:', error);
      showModal({
        type: 'error',
        title: 'Erro ao fazer pedido',
        message: 'Ocorreu um erro ao transformar o orçamento em pedido: ' + error.message,
        confirmText: 'OK',
        showCancel: false
      });
    }
    setProcessandoFazerPedido(false);
  };

  // ========================================
  // CANCELAR ORÇAMENTO - Cancela o orçamento aprovado
  // ========================================
  const cancelarOrcamento = () => {
    if (!dadosCompletos || !usuarioLogado) return;

    showModal({
      type: 'warning',
      title: 'Cancelar Orçamento',
      message: `Você deseja realmente cancelar o orçamento ${dadosCompletos.numero_proposta}? Esta ação não poderá ser desfeita.`,
      confirmText: 'Sim, Cancelar',
      cancelText: 'Não',
      showCancel: true,
      onConfirm: async () => {
        setProcessandoCancelar(true);
        const nomeCancelador = usuarioLogado.nome || usuarioLogado.nome_completo || usuarioLogado.username;

        try {
          const updateData = {
            fase_atual: 'ORCAMENTO_CANCELADO',
            status: 'Orçamento Cancelado',
            cancelado_por: nomeCancelador,
            cancelado_data: new Date().toLocaleString('sv-SE', { timeZone: 'America/Sao_Paulo' }).replace(' ', 'T')
          };

          await pedidoAPI.atualizar(dadosCompletos.id, updateData);

          showModal({
            type: 'info',
            title: 'Orçamento Cancelado',
            message: `O orçamento ${dadosCompletos.numero_proposta} foi cancelado com sucesso.`,
            confirmText: 'OK',
            showCancel: false
          });

          // Atualizar dados locais
          setDadosCompletos(prev => ({
            ...prev,
            ...updateData
          }));

          // Recarregar lista
          carregarPedidos();

        } catch (error) {
          console.error('Erro ao cancelar orçamento:', error);
          showModal(montarModalErro(error, {
            titulo: 'Erro ao cancelar',
            prefixo: 'Ocorreu um erro ao cancelar o orçamento: '
          }));
        }
        setProcessandoCancelar(false);
      }
    });
  };

  // ========================================
  // FUNÇÕES DE EDIÇÃO DO PEDIDO - Quando solicitada edição
  // ========================================
  
  // Carregar produtos para edição
  const carregarProdutosParaEdicao = async () => {
    setCarregandoProdutos(true);
    try {
      // Carrega apenas produtos do grupo "Acessórios" (rodaforro, etc.), não alçapões/parafusos
      const [forros, acessorios, custosData] = await Promise.all([
        produtoAPI.listarForros(),
        produtoAPI.listarPorGrupo('Acessórios'),
        custoAPI.listarTodos()
      ]);
      setProdutosForro(forros);
      setAcessoriosLista(acessorios);
      setCustosLista(custosData);
      setCarregandoProdutos(false);
      return { forros, acessorios, custos: custosData };
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      setCarregandoProdutos(false);
      return { forros: [], acessorios: [], custos: [] };
    }
  };

  // Gerar ID único para novos itens
  const gerarId = () => `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Obter custo do produto (igual NovoPedido)
  const getCusto = (familia_produto, estado, tabela) => {
    const tipoTabela = tabela === 'Tabela 1' ? 'Tabela 1' : 'Tabela 2';
    
    // Busca exata primeiro
    let c = custosLista.find(x => x.familia_produto === familia_produto && x.estado === estado && x.tipo_tabela === tipoTabela);
    
    // Se não encontrou, tenta busca case-insensitive e sem espaços extras
    if (!c) {
      const familiaNorm = (familia_produto || '').toLowerCase().replace(/\s+/g, ' ').trim();
      const estadoNorm = (estado || '').toLowerCase().trim();
      const tabelaNorm = tipoTabela.toLowerCase().trim();
      c = custosLista.find(x => 
        (x.familia_produto || '').toLowerCase().replace(/\s+/g, ' ').trim() === familiaNorm &&
        (x.estado || '').toLowerCase().trim() === estadoNorm &&
        (x.tipo_tabela || '').toLowerCase().trim() === tabelaNorm
      );
    }
    
    return c ? parseFloat(c.custo_producao) : 0;
  };
  
  // Obter margem exigida do produto
  const getMargemExigida = (familia_produto, estado, tabela) => {
    const tipoTabela = tabela === 'Tabela 1' ? 'Tabela 1' : 'Tabela 2';
    
    let c = custosLista.find(x => x.familia_produto === familia_produto && x.estado === estado && x.tipo_tabela === tipoTabela);
    
    if (!c) {
      const familiaNorm = (familia_produto || '').toLowerCase().replace(/\s+/g, ' ').trim();
      const estadoNorm = (estado || '').toLowerCase().trim();
      const tabelaNorm = tipoTabela.toLowerCase().trim();
      c = custosLista.find(x => 
        (x.familia_produto || '').toLowerCase().replace(/\s+/g, ' ').trim() === familiaNorm &&
        (x.estado || '').toLowerCase().trim() === estadoNorm &&
        (x.tipo_tabela || '').toLowerCase().trim() === tabelaNorm
      );
    }
    
    return c ? parseFloat(c.margem_exigida) : 15;
  };

  // Obter valor mínimo de venda
  const getValorMinimo = (familia_produto, estado, tabela) => {
    const tipoTabela = tabela === 'Tabela 1' ? 'Tabela 1' : 'Tabela 2';
    
    let c = custosLista.find(x => x.familia_produto === familia_produto && x.estado === estado && x.tipo_tabela === tipoTabela);
    
    if (!c) {
      const familiaNorm = (familia_produto || '').toLowerCase().replace(/\s+/g, ' ').trim();
      const estadoNorm = (estado || '').toLowerCase().trim();
      const tabelaNorm = tipoTabela.toLowerCase().trim();
      c = custosLista.find(x => 
        (x.familia_produto || '').toLowerCase().replace(/\s+/g, ' ').trim() === familiaNorm &&
        (x.estado || '').toLowerCase().trim() === estadoNorm &&
        (x.tipo_tabela || '').toLowerCase().trim() === tabelaNorm
      );
    }
    
    return c ? parseFloat(c.valor_minimo_venda) : 0;
  };

  // Função para verificar se houve alterações no pedido
  const verificarAlteracoes = () => {
    if (!dadosOriginais) return false;
    
    // Comparar dados de edição
    const camposParaComparar = [
      'mesmo_endereco_cliente', 'endereco_entrega', 'bairro_entrega', 'cidade_entrega', 
      'estado_entrega', 'tem_taxa_entrega', 'valor_taxa_entrega', 'observacao_pedido',
      'forma_pagamento_1', 'observacao_pagamento_1', 'forma_pagamento_2', 'observacao_pagamento_2',
      'forma_pagamento_3', 'observacao_pagamento_3', 'forma_pagamento_4', 'observacao_pagamento_4'
    ];
    
    for (const campo of camposParaComparar) {
      const valorOriginal = dadosOriginais[campo] ?? '';
      const valorAtual = dadosEdicao[campo] ?? '';
      if (String(valorOriginal) !== String(valorAtual)) {
        return true;
      }
    }
    
    // Comparar forros
    if (forrosEdicao.length !== forrosOriginais.length) return true;
    for (let i = 0; i < forrosEdicao.length; i++) {
      const forroAtual = forrosEdicao[i];
      const forroOriginal = forrosOriginais[i];
      if (!forroOriginal) return true;
      if (forroAtual.produto !== forroOriginal.produto ||
          forroAtual.quantidade !== forroOriginal.quantidade ||
          forroAtual.preco !== forroOriginal.preco ||
          forroAtual.comprimento !== forroOriginal.comprimento) {
        return true;
      }
    }
    
    // Comparar acessórios
    if (acessoriosEdicao.length !== acessoriosOriginais.length) return true;
    for (let i = 0; i < acessoriosEdicao.length; i++) {
      const acessorioAtual = acessoriosEdicao[i];
      const acessorioOriginal = acessoriosOriginais[i];
      if (!acessorioOriginal) return true;
      if (acessorioAtual.produto !== acessorioOriginal.produto ||
          acessorioAtual.quantidade !== acessorioOriginal.quantidade ||
          acessorioAtual.preco !== acessorioOriginal.preco) {
        return true;
      }
    }
    
    return false;
  };

  // Função para solicitar confirmação antes de editar
  const solicitarConfirmacaoEdicao = () => {
    setMostrarConfirmacaoEdicao(true);
  };

  // Função para confirmar e iniciar edição
  const confirmarEIniciarEdicao = async () => {
    setMostrarConfirmacaoEdicao(false);
    await iniciarEdicaoReal();
  };

  // Iniciar modo de edição (função real)
  const iniciarEdicaoReal = async () => {
    if (!dadosCompletos) return;
    
    // Carregar produtos - sempre usa os dados retornados para garantir que estejam disponíveis
    let forrosListaLocal = produtosForro;
    let acessoriosListaLocal = acessoriosLista;
    let custosListaLocal = custosLista;
    
    if (produtosForro.length === 0 || custosLista.length === 0) {
      const dados = await carregarProdutosParaEdicao();
      forrosListaLocal = dados.forros;
      acessoriosListaLocal = dados.acessorios;
      custosListaLocal = dados.custos;
    }
    
    // Função local para buscar custo usando a lista carregada
    const getCustoLocal = (familia_produto, estado, tabela) => {
      const tipoTabela = tabela === 'Tabela 1' ? 'Tabela 1' : 'Tabela 2';
      let c = custosListaLocal.find(x => x.familia_produto === familia_produto && x.estado === estado && x.tipo_tabela === tipoTabela);
      if (!c) {
        const familiaNorm = (familia_produto || '').toLowerCase().replace(/\s+/g, ' ').trim();
        const estadoNorm = (estado || '').toLowerCase().trim();
        const tabelaNorm = tipoTabela.toLowerCase().trim();
        c = custosListaLocal.find(x => 
          (x.familia_produto || '').toLowerCase().replace(/\s+/g, ' ').trim() === familiaNorm &&
          (x.estado || '').toLowerCase().trim() === estadoNorm &&
          (x.tipo_tabela || '').toLowerCase().trim() === tabelaNorm
        );
      }
      return c ? parseFloat(c.custo_producao) : 0;
    };
    
    // Função local para buscar valor mínimo usando a lista carregada
    const getValorMinimoLocal = (familia_produto, estado, tabela) => {
      const tipoTabela = tabela === 'Tabela 1' ? 'Tabela 1' : 'Tabela 2';
      let c = custosListaLocal.find(x => x.familia_produto === familia_produto && x.estado === estado && x.tipo_tabela === tipoTabela);
      if (!c) {
        const familiaNorm = (familia_produto || '').toLowerCase().replace(/\s+/g, ' ').trim();
        const estadoNorm = (estado || '').toLowerCase().trim();
        const tabelaNorm = tipoTabela.toLowerCase().trim();
        c = custosListaLocal.find(x => 
          (x.familia_produto || '').toLowerCase().replace(/\s+/g, ' ').trim() === familiaNorm &&
          (x.estado || '').toLowerCase().trim() === estadoNorm &&
          (x.tipo_tabela || '').toLowerCase().trim() === tabelaNorm
        );
      }
      return c ? parseFloat(c.valor_minimo_venda) : 0;
    };
    
    // Carregar dados atuais nos campos de edição
    const dadosIniciais = {
      mesmo_endereco_cliente: dadosCompletos.mesmo_endereco_cliente ?? true,
      endereco_entrega: dadosCompletos.endereco_entrega || '',
      bairro_entrega: dadosCompletos.bairro_entrega || '',
      cidade_entrega: dadosCompletos.cidade_entrega || '',
      estado_entrega: dadosCompletos.estado_entrega || 'Parana',
      tem_taxa_entrega: dadosCompletos.tem_taxa_entrega ?? false,
      valor_taxa_entrega: dadosCompletos.valor_taxa_entrega || '',
      observacao_pedido: dadosCompletos.observacao_pedido || dadosCompletos.observacao || '',
      forma_pagamento_1: dadosCompletos.forma_pagamento_1 || '',
      observacao_pagamento_1: dadosCompletos.observacao_pagamento_1 || '',
      forma_pagamento_2: dadosCompletos.forma_pagamento_2 || '',
      observacao_pagamento_2: dadosCompletos.observacao_pagamento_2 || '',
      forma_pagamento_3: dadosCompletos.forma_pagamento_3 || '',
      observacao_pagamento_3: dadosCompletos.observacao_pagamento_3 || '',
      forma_pagamento_4: dadosCompletos.forma_pagamento_4 || '',
      observacao_pagamento_4: dadosCompletos.observacao_pagamento_4 || ''
    };
    setDadosEdicao(dadosIniciais);
    setDadosOriginais({ ...dadosIniciais }); // Guardar cópia dos dados originais
    
    // Carregar forros existentes para edição
    const estadoCliente = dadosCompletos?.clientes?.estado || 'Parana';
    
    // Carregar forros existentes para edição
    const forrosParaEditar = (dadosCompletos.forros || []).map((f, idx) => {
      // Primeiro tenta pegar família do banco, se não existir, busca na lista de produtos pelo nome
      let familia = f.familia || f.familia_produto || '';
      if (!familia && f.produto_nome) {
        // Buscar na lista de produtos pelo nome (campo 'produto' na tabela)
        const produtoEncontrado = forrosListaLocal.find(p => 
          p.produto === f.produto_nome
        );
        if (produtoEncontrado) {
          familia = produtoEncontrado.familia_produto || produtoEncontrado.familia || '';
        }
      }
      const tabela = f.tipo_tabela || 'Tabela 1';
      // Tentar pegar custo do banco, se não tiver, buscar da tabela de custos
      let custo = parseFloat(f.custo_unitario || f.custo) || 0;
      if (custo === 0 && familia) {
        custo = getCustoLocal(familia, estadoCliente, tabela);
      }
      // Calcular valor mínimo e verificar se está abaixo
      const valorMinimo = getValorMinimoLocal(familia, estadoCliente, tabela);
      const preco = parseFloat(f.preco_unitario) || 0;
      const abaixoMinimo = preco < valorMinimo && valorMinimo > 0;
      
      return {
        id: f.id || gerarId(),  // Manter ID original do banco
        idOriginal: f.id, // Guardar ID original para referência
        produto: f.produto_nome || '',
        codigo: f.produto_codigo || '',
        tipo: f.tipo || '',
        familia: familia,
        tabela: tabela,
        quantidade: parseInt(f.quantidade) || 0,
        comprimento: parseFloat(f.comprimento) || 6,
        m2: parseFloat(f.m2_total || f.m2) || 0,
        preco: preco,
        total: parseFloat(f.valor_total || f.total) || 0,
        custo: custo,
        valorMinimo: valorMinimo,
        abaixoMinimo: abaixoMinimo
      };
    });
    setForrosEdicao(forrosParaEditar);
    setForrosOriginais(forrosParaEditar.map(f => ({ ...f }))); // Guardar cópia dos forros originais
    
    
    // Carregar acessórios existentes para edição
    const acessoriosParaEditar = (dadosCompletos.acessorios || []).map((a, idx) => {
      // Primeiro tenta pegar família do banco, se não existir, busca na lista de acessórios pelo nome
      let familia = a.familia || a.familia_produto || '';
      if (!familia && a.produto_nome) {
        // Buscar na lista de acessórios pelo nome (campo 'produto' na tabela)
        const acessorioEncontrado = acessoriosListaLocal.find(p => 
          p.produto === a.produto_nome
        );
        if (acessorioEncontrado) {
          familia = acessorioEncontrado.familia_produto || acessorioEncontrado.familia || '';
        }
      }
      const tabela = a.tipo_tabela || 'Tabela 1';
      // Tentar pegar custo do banco, se não tiver, buscar da tabela de custos
      let custo = parseFloat(a.custo_unitario || a.custo) || 0;
      if (custo === 0 && familia) {
        custo = getCustoLocal(familia, estadoCliente, tabela);
      }
      // Calcular valor mínimo e verificar se está abaixo
      const valorMinimo = getValorMinimoLocal(familia, estadoCliente, tabela);
      const preco = parseFloat(a.preco_unitario) || 0;
      const abaixoMinimo = preco < valorMinimo && valorMinimo > 0;
      
      // Usar o forro_id diretamente do banco
      // Como os forros mantêm seus IDs originais do banco (id: f.id || gerarId()),
      // o forro_id do acessório deve bater diretamente com o id do forro
      const forroIdOriginal = a.forro_id;
      
      
      return {
        id: a.id || gerarId(),
        forroId: forroIdOriginal,  // Usar diretamente, sem mapeamento
        forroIdOriginal: forroIdOriginal, // Guardar para referência
        produto: a.produto_nome || '',
        codigo: a.produto_codigo || '',
        tipo: a.tipo || '',
        familia: familia,
        tabela: tabela,
        quantidade: parseInt(a.quantidade) || 0,
        preco: preco,
        total: parseFloat(a.valor_total || a.total) || 0,
        custo: custo,
        valorMinimo: valorMinimo,
        abaixoMinimo: abaixoMinimo
      };
    });
    

    // NÃO AGRUPAR ACESSÓRIOS - manter cada linha separada como foi confeccionado
    // Cada acessório mantém sua quantidade original
    

    // Fallback: vincular acabamentos sem forroId/forroIdOriginal por ordem dentro da mesma tabela
    const acessoriosComVinculo = acessoriosParaEditar.map(a => ({ ...a }));
    const forrosPorTabela = {
      'Tabela 1': forrosParaEditar.filter(f => f.tabela === 'Tabela 1'),
      'Tabela 2': forrosParaEditar.filter(f => f.tabela === 'Tabela 2')
    };

    ['Tabela 1', 'Tabela 2'].forEach((tabela) => {
      const forrosTabela = forrosPorTabela[tabela];
      if (!forrosTabela.length) return;
      let idx = 0;
      acessoriosComVinculo.forEach((a) => {
        const semVinculo = !a.forroId && !a.forroIdOriginal;
        if (!semVinculo) return;
        if (a.tabela !== tabela) return;
        if (a.vinculoForroId) return;
        const forro = forrosTabela[idx % forrosTabela.length];
        a.vinculoForroId = forro.id;
        idx += 1;
      });
    });

    setAcessoriosEdicao(acessoriosComVinculo);
    setAcessoriosOriginais(acessoriosComVinculo.map(a => ({ ...a }))); // Guardar cópia dos acessórios originais
    
    setModoEdicao(true);
  };
  
  // Cancelar edição
  const cancelarEdicao = () => {
    setModoEdicao(false);
    setForrosEdicao([]);
    setAcessoriosEdicao([]);
    setDadosEdicao({
      mesmo_endereco_cliente: true,
      endereco_entrega: '',
      bairro_entrega: '',
      cidade_entrega: '',
      estado_entrega: 'Parana',
      tem_taxa_entrega: false,
      valor_taxa_entrega: '',
      observacao_pedido: '',
      forma_pagamento_1: '',
      observacao_pagamento_1: '',
      forma_pagamento_2: '',
      observacao_pagamento_2: '',
      forma_pagamento_3: '',
      observacao_pagamento_3: '',
      forma_pagamento_4: '',
      observacao_pagamento_4: ''
    });
    
    // Limpar estados de troca de cliente
    setClienteTrocado(false);
    setNovoClienteId(null);
    setClienteOriginalNome(null);
    setMostrarTrocaCliente(false);
    setCnpjTroca('');
    setClienteEncontrado(null);
    
    // Restaurar dados originais do cliente se houve troca
    if (clienteTrocado && dadosOriginais) {
      setDadosCompletos(prev => ({
        ...prev,
        clientes: dadosOriginais.clientes || prev.clientes
      }));
    }
  };
  
  // ========================================
  // FUNÇÕES PARA EDIÇÃO DE ITENS
  // ========================================
  
  // Adicionar novo forro
  const adicionarForro = () => {
    setForrosEdicao(prev => [...prev, {
      id: gerarId(),
      produto: '',
      codigo: '',
      tipo: '',
      familia: '',
      tabela: 'Tabela 1',
      quantidade: 0,
      comprimento: 6,
      m2: 0,
      preco: 0,
      total: 0,
      custo: 0
    }]);
  };
  
  // Remover forro
  const removerForro = (id) => {
    // Primeiro, encontrar o forro para pegar o idOriginal também
    const forroRemovido = forrosEdicao.find(f => f.id === id);
    const idOriginal = forroRemovido?.idOriginal;
    const idStr = String(id);
    const idOriginalStr = idOriginal != null ? String(idOriginal) : null;
    
    setForrosEdicao(prev => prev.filter(f => f.id !== id));
    // Remover acessórios vinculados - verificando todos os possíveis campos de vínculo
    setAcessoriosEdicao(prev => prev.filter(a => {
      const aForroIdStr = a.forroId != null ? String(a.forroId) : null;
      const aForroIdOriginalStr = a.forroIdOriginal != null ? String(a.forroIdOriginal) : null;
      const aVinculoForroIdStr = a.vinculoForroId != null ? String(a.vinculoForroId) : null;
      
      // Se qualquer ID do acessório bate com o ID do forro removido, remover o acessório
      const deveRemover = 
        (aForroIdStr && (aForroIdStr === idStr || aForroIdStr === idOriginalStr)) ||
        (aForroIdOriginalStr && (aForroIdOriginalStr === idStr || aForroIdOriginalStr === idOriginalStr)) ||
        (aVinculoForroIdStr && (aVinculoForroIdStr === idStr || aVinculoForroIdStr === idOriginalStr));
      
      return !deveRemover;
    }));
  };
  
  // Atualizar forro
  const atualizarForro = (id, campo, valor) => {
    const estadoCliente = dadosCompletos?.clientes?.estado || 'Parana';
    
    setForrosEdicao(prev => {
      let arr = prev.map(f => ({ ...f }));
      const idx = arr.findIndex(f => f.id === id);
      if (idx === -1) return prev;
      
      arr[idx][campo] = valor;
      
      // Se mudou o produto, atualizar dados relacionados
      if (campo === 'produto' && valor) {
        const produtoSelecionado = produtosForro.find(p => p.produto === valor);
        if (produtoSelecionado) {
          arr[idx].codigo = produtoSelecionado.codigo;
          arr[idx].tipo = produtoSelecionado.tipo;
          arr[idx].familia = produtoSelecionado.familia_produto;
          arr[idx].custo = getCusto(produtoSelecionado.familia_produto, estadoCliente, arr[idx].tabela);
          arr[idx].valorMinimo = getValorMinimo(produtoSelecionado.familia_produto, estadoCliente, arr[idx].tabela);
          
          // Verificar se já existe outro produto da mesma família no pedido
          const familiaAtual = arr[idx].familia;
          const produtoMesmaFamilia = arr.find((f, i) => i !== idx && f.familia === familiaAtual && f.preco > 0);
          
          if (produtoMesmaFamilia) {
            // Usar o mesmo preço do produto existente da mesma família
            arr[idx].preco = produtoMesmaFamilia.preco;
          }
        }
      }
      
      // Se mudou tabela, atualizar custo e valor mínimo
      if (campo === 'tabela' && arr[idx].familia) {
        arr[idx].custo = getCusto(arr[idx].familia, estadoCliente, valor);
        arr[idx].valorMinimo = getValorMinimo(arr[idx].familia, estadoCliente, valor);
        
        // Verificar se já existe outro produto da mesma família no pedido
        const familiaAtual = arr[idx].familia;
        const produtoMesmaFamilia = arr.find((f, i) => i !== idx && f.familia === familiaAtual && f.preco > 0);
        
        if (produtoMesmaFamilia) {
          // Manter o mesmo preço do produto existente da mesma família
          arr[idx].preco = produtoMesmaFamilia.preco;
        }
      }
      
      // Recalcular m2 e total (m2 = qtde × comprimento × 0.2 largura do forro)
      const qtde = parseFloat(arr[idx].quantidade) || 0;
      const comp = parseFloat(arr[idx].comprimento) || 0;
      const preco = parseFloat(arr[idx].preco) || 0;
      arr[idx].m2 = qtde * comp * 0.2;
      arr[idx].total = arr[idx].m2 * preco;
      
      // Sempre verificar se preço está abaixo do mínimo
      const valorMinimo = arr[idx].valorMinimo || getValorMinimo(arr[idx].familia, estadoCliente, arr[idx].tabela);
      arr[idx].valorMinimo = valorMinimo;
      arr[idx].abaixoMinimo = preco < valorMinimo && valorMinimo > 0;
      
      // SINCRONIZAÇÃO DE PREÇO POR FAMÍLIA: 
      // Ao alterar o preço de um produto, sincroniza com todos os outros produtos da mesma família
      if (campo === 'preco') {
        const familiaAtual = arr[idx].familia;
        if (familiaAtual) {
          const novoPreco = parseFloat(valor) || 0;
          arr.forEach((f, i) => {
            if (i !== idx && f.familia === familiaAtual) {
              f.preco = novoPreco;
              const valorMinimoOutro = f.valorMinimo || getValorMinimo(f.familia, estadoCliente, f.tabela);
              f.abaixoMinimo = novoPreco < valorMinimoOutro && valorMinimoOutro > 0;
              // Recalcular total do item sincronizado
              f.total = (f.m2 || 0) * novoPreco;
            }
          });
        }
      }
      
      return arr;
    });
    
    // Se mudou a tabela do forro, também atualizar a tabela dos acabamentos vinculados
    if (campo === 'tabela') {
      setAcessoriosEdicao(prev => prev.map(a => {
        // Verificar se o acessório pertence a este forro
        const aForroId = a.forroId !== undefined && a.forroId !== null ? String(a.forroId) : null;
        const aForroIdOriginal = a.forroIdOriginal !== undefined && a.forroIdOriginal !== null ? String(a.forroIdOriginal) : null;
        const aVinculoForroId = a.vinculoForroId !== undefined && a.vinculoForroId !== null ? String(a.vinculoForroId) : null;

        // Buscar o forro para comparar IDs
        const forroAtual = forrosEdicao.find(f => f.id === id);
        const forroId = String(id);
        const forroIdOriginal = forroAtual?.idOriginal !== undefined && forroAtual?.idOriginal !== null ? String(forroAtual.idOriginal) : null;

        const pertenceAoForro = (
          (aVinculoForroId && aVinculoForroId === forroId) ||
          (aForroId && (aForroId === forroId || (forroIdOriginal && aForroId === forroIdOriginal))) ||
          (aForroIdOriginal && (aForroIdOriginal === forroId || (forroIdOriginal && aForroIdOriginal === forroIdOriginal)))
        );
        
        if (pertenceAoForro) {
          // Atualizar tabela do acessório para a nova tabela do forro
          const novoAcessorio = { ...a, tabela: valor };
          // Recalcular custo e valor mínimo com a nova tabela
          if (novoAcessorio.familia) {
            novoAcessorio.custo = getCusto(novoAcessorio.familia, dadosCompletos?.clientes?.estado, valor);
            novoAcessorio.valorMinimo = getValorMinimo(novoAcessorio.familia, dadosCompletos?.clientes?.estado, valor);
            novoAcessorio.abaixoMinimo = (novoAcessorio.preco || 0) < novoAcessorio.valorMinimo && novoAcessorio.valorMinimo > 0;
          }
          return novoAcessorio;
        }
        return a;
      }));
    }
  };

  // Manter tabela dos acabamentos sincronizada com o forro vinculado
  useEffect(() => {
    setAcessoriosEdicao(prev => {
      let mudou = false;
      const estadoCliente = dadosCompletos?.clientes?.estado;

      const atualizados = prev.map(a => {
        const aForroId = a.forroId !== undefined && a.forroId !== null ? String(a.forroId) : null;
        const aForroIdOriginal = a.forroIdOriginal !== undefined && a.forroIdOriginal !== null ? String(a.forroIdOriginal) : null;

        const forroVinculado = forrosEdicao.find(f => {
          const fId = String(f.id);
          const fIdOriginal = f.idOriginal !== undefined && f.idOriginal !== null ? String(f.idOriginal) : null;
          return (
            (aForroId && (aForroId === fId || (fIdOriginal && aForroId === fIdOriginal))) ||
            (aForroIdOriginal && (aForroIdOriginal === fId || (fIdOriginal && aForroIdOriginal === fIdOriginal)))
          );
        });

        if (forroVinculado && a.tabela !== forroVinculado.tabela) {
          const novo = { ...a, tabela: forroVinculado.tabela };
          if (novo.familia) {
            novo.custo = getCusto(novo.familia, estadoCliente, novo.tabela);
            novo.valorMinimo = getValorMinimo(novo.familia, estadoCliente, novo.tabela);
            const preco = parseFloat(novo.preco) || 0;
            novo.abaixoMinimo = preco < (novo.valorMinimo || 0) && (novo.valorMinimo || 0) > 0;
          }
          mudou = true;
          return novo;
        }

        return a;
      });

      return mudou ? atualizados : prev;
    });
  }, [forrosEdicao, dadosCompletos?.clientes?.estado]);
  
  // Adicionar acessório vinculado a um forro específico
  const adicionarAcessorioParaForro = (forroId, tabelaForro) => {
    setAcessoriosEdicao(prev => [...prev, {
      id: gerarId(),
      forroId: forroId,
      produto: '',
      codigo: '',
      tipo: '',
      familia: '',
      tabela: tabelaForro || 'Tabela 1',
      quantidade: 0,
      preco: 0,
      total: 0,
      custo: 0
    }]);
  };
  
  // Adicionar novo acessório (genérico)
  const adicionarAcessorio = () => {
    setAcessoriosEdicao(prev => [...prev, {
      id: gerarId(),
      forroId: forrosEdicao.length > 0 ? forrosEdicao[0].id : null,
      produto: '',
      codigo: '',
      tipo: '',
      familia: '',
      tabela: 'Tabela 1',
      quantidade: 0,
      preco: 0,
      total: 0,
      custo: 0
    }]);
  };
  
  // Remover acessório
  const removerAcessorio = (id) => {
    setAcessoriosEdicao(prev => prev.filter(a => a.id !== id));
  };
  
  // Atualizar acessório
  const atualizarAcessorio = (id, campo, valor) => {
    setAcessoriosEdicao(prev => prev.map(a => {
      if (a.id !== id) return a;
      
      const novoAcessorio = { ...a, [campo]: valor };
      
      // Se mudou o produto, atualizar dados relacionados
      if (campo === 'produto' && valor) {
        const produtoSelecionado = acessoriosLista.find(p => p.produto === valor);
        if (produtoSelecionado) {
          novoAcessorio.codigo = produtoSelecionado.codigo;
          novoAcessorio.tipo = produtoSelecionado.tipo;
          novoAcessorio.familia = produtoSelecionado.familia_produto;
          novoAcessorio.custo = getCusto(produtoSelecionado.familia_produto, dadosCompletos?.clientes?.estado, novoAcessorio.tabela);
          novoAcessorio.valorMinimo = getValorMinimo(produtoSelecionado.familia_produto, dadosCompletos?.clientes?.estado, novoAcessorio.tabela);
        }
      }
      
      // Se mudou tabela, atualizar custo e valor mínimo
      if (campo === 'tabela' && novoAcessorio.familia) {
        novoAcessorio.custo = getCusto(novoAcessorio.familia, dadosCompletos?.clientes?.estado, valor);
        novoAcessorio.valorMinimo = getValorMinimo(novoAcessorio.familia, dadosCompletos?.clientes?.estado, valor);
      }
      
      // Recalcular total
      const qtde = parseFloat(novoAcessorio.quantidade) || 0;
      const preco = parseFloat(novoAcessorio.preco) || 0;
      novoAcessorio.total = qtde * preco;
      
      // Sempre verificar se preço está abaixo do mínimo (ao final de qualquer alteração)
      const valorMinimo = novoAcessorio.valorMinimo || getValorMinimo(novoAcessorio.familia, dadosCompletos?.clientes?.estado, novoAcessorio.tabela);
      novoAcessorio.valorMinimo = valorMinimo;
      novoAcessorio.abaixoMinimo = preco < valorMinimo && valorMinimo > 0;
      
      return novoAcessorio;
    }));
  };
  
  // Buscar cliente para troca por CPF/CNPJ
  const buscarClienteTroca = async () => {
    if (!cnpjTroca || cnpjTroca.replace(/\D/g, '').length < 11) {
      showModal({
        type: 'warning',
        title: 'CPF/CNPJ Inválido',
        message: 'Digite um CPF (11 dígitos) ou CNPJ (14 dígitos) válido.',
        confirmText: 'OK',
        showCancel: false
      });
      return;
    }

    setBuscandoCliente(true);
    setClienteEncontrado(null);

    try {
      const cliente = await clienteAPI.buscarPorCNPJ(cnpjTroca);
      if (cliente) {
        setClienteEncontrado(cliente);
      } else {
        showModal({
          type: 'warning',
          title: 'Cliente não encontrado',
          message: 'Nenhum cliente encontrado com esse CPF/CNPJ. Verifique o número e tente novamente.',
          confirmText: 'OK',
          showCancel: false
        });
      }
    } catch (error) {
      showModal({
        type: 'error',
        title: 'Erro na busca',
        message: error.message || 'Não foi possível buscar o cliente. Tente novamente.',
        confirmText: 'OK',
        showCancel: false
      });
    } finally {
      setBuscandoCliente(false);
    }
  };

  // Confirmar troca de cliente - salva direto no banco
  const confirmarTrocaCliente = async () => {
    if (!clienteEncontrado || !dadosCompletos) return;

    // Verificar se não é o mesmo cliente
    if (clienteEncontrado.id === dadosCompletos?.clientes?.id) {
      showModal({
        type: 'warning',
        title: 'Mesmo Cliente',
        message: 'O cliente selecionado já é o cliente atual deste pedido.',
        confirmText: 'OK',
        showCancel: false
      });
      return;
    }

    try {
      // Nome do cliente original (antes da troca)
      const nomeOriginal = dadosCompletos?.cliente_original_nome || dadosCompletos?.clientes?.nome || 'Desconhecido';

      // Salvar troca direto no banco
      await pedidoAPI.atualizar(dadosCompletos.id, {
        cliente_id: clienteEncontrado.id,
        cliente_alterado: true,
        cliente_original_nome: nomeOriginal
      });

      // Atualizar dados locais
      setDadosCompletos(prev => ({
        ...prev,
        clientes: clienteEncontrado,
        cliente_id: clienteEncontrado.id,
        cliente_alterado: true,
        cliente_original_nome: nomeOriginal
      }));

      // Fechar painel e limpar
      setMostrarTrocaCliente(false);
      setCnpjTroca('');
      setClienteEncontrado(null);

      showModal({
        type: 'success',
        title: 'Cliente Alterado!',
        message: `Cliente do pedido trocado para: ${clienteEncontrado.nome}.`,
        confirmText: 'OK',
        showCancel: false
      });
    } catch (error) {
      showModal({
        type: 'error',
        title: 'Erro ao trocar cliente',
        message: error.message || 'Não foi possível salvar a troca de cliente. Tente novamente.',
        confirmText: 'OK',
        showCancel: false
      });
    }
  };

  // Cancelar troca de cliente
  const cancelarTrocaCliente = () => {
    setMostrarTrocaCliente(false);
    setCnpjTroca('');
    setClienteEncontrado(null);
  };

  // Salvar edição e reenviar para aprovação
  const salvarEdicao = async () => {
    if (!dadosCompletos || !usuarioLogado) return;
    
    // Validar se tem pelo menos uma forma de pagamento selecionada
    if (!dadosEdicao.forma_pagamento_1) {
      showModal({
        type: 'warning',
        title: 'Atenção',
        message: 'É obrigatório selecionar pelo menos uma forma de pagamento.',
        confirmText: 'OK',
        showCancel: false
      });
      return;
    }
    
    // Validar observação obrigatória para certas formas de pagamento (igual NovoPedido)
    const formasParaValidar = [
      { forma: dadosEdicao.forma_pagamento_1, observacao: dadosEdicao.observacao_pagamento_1 },
      { forma: dadosEdicao.forma_pagamento_2, observacao: dadosEdicao.observacao_pagamento_2 },
      { forma: dadosEdicao.forma_pagamento_3, observacao: dadosEdicao.observacao_pagamento_3 },
      { forma: dadosEdicao.forma_pagamento_4, observacao: dadosEdicao.observacao_pagamento_4 }
    ];
    
    const formasSemObservacao = formasParaValidar.filter(fp => {
      if (!fp.forma) return false;
      const formaConfig = FORMAS_PAGAMENTO.find(f => f.label === fp.forma);
      return formaConfig?.exigeObservacao && (!fp.observacao || fp.observacao.trim() === '');
    });
    
    if (formasSemObservacao.length > 0) {
      const nomes = formasSemObservacao.map(f => f.forma).join(', ');
      showModal({
        type: 'warning',
        title: 'Observação Obrigatória',
        message: `As seguintes formas de pagamento exigem observação: ${nomes}`,
        confirmText: 'OK',
        showCancel: false
      });
      return;
    }
    
    // Validar se tem pelo menos um item (forro ou acessório)
    if (forrosEdicao.length === 0 && acessoriosEdicao.length === 0) {
      showModal({
        type: 'warning',
        title: 'Atenção',
        message: 'É obrigatório ter pelo menos um item no pedido.',
        confirmText: 'OK',
        showCancel: false
      });
      return;
    }
    
    // Validar forros preenchidos
    const forrosInvalidos = forrosEdicao.filter(f => !f.produto || f.quantidade <= 0 || f.preco <= 0);
    if (forrosInvalidos.length > 0) {
      showModal({
        type: 'warning',
        title: 'Atenção',
        message: 'Preencha corretamente todos os campos dos forros (produto, quantidade e preço).',
        confirmText: 'OK',
        showCancel: false
      });
      return;
    }
    
    // Validar acessórios preenchidos
    const acessoriosInvalidos = acessoriosEdicao.filter(a => !a.produto || a.quantidade <= 0 || a.preco <= 0);
    if (acessoriosInvalidos.length > 0) {
      showModal({
        type: 'warning',
        title: 'Atenção',
        message: 'Preencha corretamente todos os campos dos acabamentos (produto, quantidade e preço).',
        confirmText: 'OK',
        showCancel: false
      });
      return;
    }
    
    // Validar preços mínimos (forros + acessórios)
    const forrosAbaixoMinimo = forrosEdicao.filter(f => f.abaixoMinimo);
    const acessoriosAbaixoMinimo = acessoriosEdicao.filter(a => a.abaixoMinimo);
    const totalAbaixoMinimo = forrosAbaixoMinimo.length + acessoriosAbaixoMinimo.length;
    
    if (totalAbaixoMinimo > 0) {
      const podeVenderAbaixoMinimo = usuarioLogado?.permissoes?.pode_vender_abaixo_minimo === true || usuarioLogado?.pode_vender_abaixo_minimo === true;
      
      if (!podeVenderAbaixoMinimo) {
        // Sem permissão: bloqueia salvamento
        const tipoMsg = forrosAbaixoMinimo.length > 0 && acessoriosAbaixoMinimo.length > 0
          ? `${forrosAbaixoMinimo.length} forro(s) e ${acessoriosAbaixoMinimo.length} acabamento(s)`
          : forrosAbaixoMinimo.length > 0
            ? `${forrosAbaixoMinimo.length} forro(s)`
            : `${acessoriosAbaixoMinimo.length} acabamento(s)`;
        showModal({
          type: 'error',
          title: 'Preço Abaixo do Mínimo',
          message: `Existem ${tipoMsg} com preço abaixo do valor mínimo permitido. Corrija os valores destacados em vermelho antes de salvar.`,
          confirmText: 'OK',
          showCancel: false
        });
        return;
      } else {
        // Com permissão: pede confirmação e continua salvamento
        const tipoMsg = forrosAbaixoMinimo.length > 0 && acessoriosAbaixoMinimo.length > 0
          ? `${forrosAbaixoMinimo.length} forro(s) e ${acessoriosAbaixoMinimo.length} acabamento(s)`
          : forrosAbaixoMinimo.length > 0
            ? `${forrosAbaixoMinimo.length} forro(s)`
            : `${acessoriosAbaixoMinimo.length} acabamento(s)`;
        showModal({
          type: 'warning',
          title: 'Preço Abaixo do Mínimo',
          message: `Você tem certeza que deseja salvar este orçamento? Este orçamento contém ${tipoMsg} com preço abaixo do valor mínimo.`,
          confirmText: 'Confirmar',
          cancelText: 'Cancelar',
          showCancel: true,
          onConfirm: () => setTimeout(() => executarSalvamentoEdicao(), 100)
        });
        return;
      }
    }
    
    executarSalvamentoEdicao();
  };

  const executarSalvamentoEdicao = async () => {
    setProcessandoEdicao(true);
    
    try {
      // Calcular totais por tabela
      const valorTabela1 = forrosEdicao.filter(f => f.tabela === 'Tabela 1').reduce((sum, f) => sum + (f.total || 0), 0) +
                          acessoriosEdicao.filter(a => a.tabela === 'Tabela 1').reduce((sum, a) => sum + (a.total || 0), 0);
      const valorTabela2 = forrosEdicao.filter(f => f.tabela === 'Tabela 2').reduce((sum, f) => sum + (f.total || 0), 0) +
                          acessoriosEdicao.filter(a => a.tabela === 'Tabela 2').reduce((sum, a) => sum + (a.total || 0), 0);
      
      // Calcular custos por tabela
      // CORREÇÃO: Para FORROS usar m2 * custo (custo é por m²)
      // Para ACESSÓRIOS usar quantidade * custo (custo é por unidade)
      const custoTabela1 = forrosEdicao.filter(f => f.tabela === 'Tabela 1').reduce((sum, f) => sum + ((f.m2 || 0) * (f.custo || 0)), 0) +
                          acessoriosEdicao.filter(a => a.tabela === 'Tabela 1').reduce((sum, a) => sum + ((a.quantidade || 0) * (a.custo || 0)), 0);
      const custoTabela2 = forrosEdicao.filter(f => f.tabela === 'Tabela 2').reduce((sum, f) => sum + ((f.m2 || 0) * (f.custo || 0)), 0) +
                          acessoriosEdicao.filter(a => a.tabela === 'Tabela 2').reduce((sum, a) => sum + ((a.quantidade || 0) * (a.custo || 0)), 0);
      
      // Calcular lucros por tabela (valor - custo)
      const lucroTabela1 = valorTabela1 - custoTabela1;
      const lucroTabela2 = valorTabela2 - custoTabela2;
      
      // IMPORTANTE: Calcular pontuações usando a mesma lógica do modo de edição
      const tab1Data = calcularResumoTabelaEdicao('Tabela 1');
      const tab2Data = calcularResumoTabelaEdicao('Tabela 2');
      const pontuacaoTabela1 = tab1Data.pontuacao || 0;
      const pontuacaoTabela2 = tab2Data.pontuacao || 0;
      
      // Calcular status baseado nas pontuações
      const statusTabela1 = tab1Data.temItens ? getStatusByScore(pontuacaoTabela1) : null;
      const statusTabela2 = tab2Data.temItens ? getStatusByScore(pontuacaoTabela2) : null;
      
      // Calcular pontuação geral e status geral (igual NovoPedido)
      const pontTab1Cap = tab1Data.temItens ? Math.min(pontuacaoTabela1, 50) : 0;
      const pontTab2Cap = tab2Data.temItens ? Math.min(pontuacaoTabela2, 50) : 0;
      const pontuacaoGeralCalc = pontTab1Cap + pontTab2Cap;
      const totalVendaGeralCalc = (tab1Data.valorGerado || 0) + (tab2Data.valorGerado || 0);
      const statusGeralCalc = totalVendaGeralCalc === 0 ? 'INVIÁVEL' : getStatusByScore(pontuacaoGeralCalc);
      
      // Preparar forros para envio
      const forrosParaSalvar = forrosEdicao.map(f => ({
        id: f.id,
        produto_nome: f.produto,
        produto_codigo: f.codigo,
        tipo: f.tipo,
        familia: f.familia,
        tipo_tabela: f.tabela,
        quantidade: f.quantidade,
        comprimento: f.comprimento,
        m2_total: f.m2,
        preco_unitario: f.preco,
        valor_total: f.total,
        custo_unitario: f.custo
      }));
      
      // Preparar acessórios para envio
      const acessoriosParaSalvar = acessoriosEdicao.map(a => ({
        produto_nome: a.produto,
        produto_codigo: a.codigo,
        tipo: a.tipo,
        familia: a.familia,
        tipo_tabela: a.tabela,
        quantidade: a.quantidade,
        preco_unitario: a.preco,
        valor_total: a.total,
        custo_unitario: a.custo,
        forro_id: a.forroId || a.forroIdOriginal || a.vinculoForroId || null
      }));
      
      const updateData = {
        // Dados de entrega
        mesmo_endereco_cliente: dadosEdicao.mesmo_endereco_cliente,
        endereco_entrega: dadosEdicao.mesmo_endereco_cliente ? null : dadosEdicao.endereco_entrega,
        bairro_entrega: dadosEdicao.mesmo_endereco_cliente ? null : dadosEdicao.bairro_entrega,
        cidade_entrega: dadosEdicao.mesmo_endereco_cliente ? null : dadosEdicao.cidade_entrega,
        estado_entrega: dadosEdicao.mesmo_endereco_cliente ? null : dadosEdicao.estado_entrega,
        tem_taxa_entrega: dadosEdicao.tem_taxa_entrega,
        valor_taxa_entrega: dadosEdicao.tem_taxa_entrega ? parseFloat(dadosEdicao.valor_taxa_entrega) || 0 : 0,
        
        // Formas de pagamento
        forma_pagamento_1: dadosEdicao.forma_pagamento_1,
        observacao_pagamento_1: dadosEdicao.observacao_pagamento_1,
        forma_pagamento_2: dadosEdicao.forma_pagamento_2,
        observacao_pagamento_2: dadosEdicao.observacao_pagamento_2,
        forma_pagamento_3: dadosEdicao.forma_pagamento_3,
        observacao_pagamento_3: dadosEdicao.observacao_pagamento_3,
        forma_pagamento_4: dadosEdicao.forma_pagamento_4,
        observacao_pagamento_4: dadosEdicao.observacao_pagamento_4,
        
        // Observação
        observacao_pedido: dadosEdicao.observacao_pedido,
        
        // Valores atualizados
        valor_tabela1: valorTabela1,
        valor_tabela2: valorTabela2,
        
        // Custos e lucros recalculados
        custo_tabela1: custoTabela1,
        custo_tabela2: custoTabela2,
        lucro_tabela1: lucroTabela1,
        lucro_tabela2: lucroTabela2,
        
        // PONTUAÇÕES E STATUS RECALCULADOS
        pontuacao_tabela1: pontuacaoTabela1,
        pontuacao_tabela2: pontuacaoTabela2,
        status_tabela1: statusTabela1,
        status_tabela2: statusTabela2,
        
        // PONTUAÇÃO GERAL E STATUS GERAL RECALCULADOS
        pontuacao_geral: pontuacaoGeralCalc,
        status_geral: statusGeralCalc,
        
        // Itens do pedido
        forros: forrosParaSalvar,
        acessorios: acessoriosParaSalvar,
        
        // Atualizar fase para aguardar nova etapa
        fase_atual: 'AGUARDANDO_PROXIMA_ETAPA',
        
        // Limpar dados de aprovação anterior para re-análise
        aprovacao_orcamento_por: null,
        aprovacao_orcamento_data: null,
        aprovacao_orcamento_observacao: null,

        // Troca de cliente (se houve)
        ...(clienteTrocado && novoClienteId ? {
          cliente_id: novoClienteId,
          cliente_alterado: true,
          cliente_original_nome: clienteOriginalNome || dadosCompletos?.cliente_original_nome || 'Desconhecido'
        } : {})
      };
      
      await pedidoAPI.atualizar(dadosCompletos.id, updateData);
      
      showModal({
        type: 'success',
        title: 'Alterações salvas!',
        message: `O orçamento ${dadosCompletos.numero_proposta} foi atualizado e reenviado para aprovação.`,
        confirmText: 'OK',
        showCancel: false
      });
      
      // Atualizar dados locais com estrutura correta para visualização
      const forrosParaVisualizacao = forrosEdicao.map(f => ({
        produto_nome: f.produto,
        produto_codigo: f.codigo,
        tipo: f.tipo,
        familia_produto: f.familia,
        tipo_tabela: f.tabela,
        quantidade: f.quantidade,
        comprimento: f.comprimento,
        m2: f.m2,
        preco_unitario: f.preco,
        total: f.total,
        custo_unitario: f.custo
      }));
      
      const acessoriosParaVisualizacao = acessoriosEdicao.map(a => ({
        produto_nome: a.produto,
        produto_codigo: a.codigo,
        tipo: a.tipo,
        familia_produto: a.familia,
        tipo_tabela: a.tabela,
        quantidade: a.quantidade,
        preco_unitario: a.preco,
        total: a.total,
        custo_unitario: a.custo,
        forro_id: a.forroId
      }));
      
      setDadosCompletos(prev => ({
        ...prev,
        ...updateData,
        forros: forrosParaVisualizacao,
        acessorios: acessoriosParaVisualizacao
      }));
      
      // Sair do modo edição
      setModoEdicao(false);
      setForrosEdicao([]);
      setAcessoriosEdicao([]);
      
      // Limpar estados de troca de cliente
      setClienteTrocado(false);
      setNovoClienteId(null);
      setClienteOriginalNome(null);
      setMostrarTrocaCliente(false);
      setCnpjTroca('');
      setClienteEncontrado(null);
      
      // Recarregar lista e detalhes do pedido do banco
      carregarPedidos();
      
      // Recarregar detalhes do pedido atual para ter dados atualizados do banco
      try {
        const response = await pedidoAPI.buscarPorId(dadosCompletos.id);
        if (response.success && response.pedido) {
          setDadosCompletos(response.pedido);
        }
      } catch (e) {
      }
      
    } catch (error) {
      console.error('Erro ao salvar edição:', error);
      showModal(montarModalErro(error, {
        titulo: 'Erro ao salvar',
        prefixo: 'Ocorreu um erro ao salvar as alterações: '
      }));
    }
    
    setProcessandoEdicao(false);
  };

  const getStatusConfigLocal = (status) => {
    try {
      const globalConfig = getStatusConfig(status);
      if (globalConfig) {
        return { 
          color: `bg-gradient-to-r ${globalConfig.bgGradient}`, 
          textColor: 'text-white', 
          icon: globalConfig.iconEmoji || '●', 
          label: globalConfig.label,
          IconComponent: globalConfig.icon 
        };
      }
    } catch (e) {}
    const configs = {
      'EXCELENTE': { color: 'bg-gradient-to-r from-emerald-600 to-emerald-700', textColor: 'text-white', icon: '🏆', label: 'EXCELENTE' },
      'BOA VENDA': { color: 'bg-gradient-to-r from-green-500 to-green-600', textColor: 'text-white', icon: '✓', label: 'BOA VENDA' },
      'VIÁVEL': { color: 'bg-gradient-to-r from-lime-400 to-lime-500', textColor: 'text-white', icon: '→', label: 'VIÁVEL' },
      'EM ANÁLISE': { color: 'bg-gradient-to-r from-amber-400 to-amber-500', textColor: 'text-white', icon: '⚠', label: 'EM ANÁLISE' },
      'INVIÁVEL': { color: 'bg-gradient-to-r from-red-400 to-red-500', textColor: 'text-white', icon: '✗', label: 'INVIÁVEL' },
      'RECUSADO': { color: 'bg-gradient-to-r from-red-600 to-red-700', textColor: 'text-white', icon: '✗', label: 'RECUSADO' },
      'APROVADO': { color: 'bg-gradient-to-r from-teal-500 to-teal-600', textColor: 'text-white', icon: '✓', label: 'APROVADO' },
      'REANÁLISE': { color: 'bg-gradient-to-r from-amber-500 to-orange-500', textColor: 'text-white', icon: '↻', label: 'REANÁLISE' }
    };
    return configs[status] || configs['EM ANÁLISE'];
  };

  // ========================================
  // CALCULAR RESUMO PARA MODO EDIÇÃO (igual NovoPedido)
  // Agrupa por FAMÍLIA do forro, não por forroId
  // ========================================
  const calcularResumoTabelaEdicao = (tabela) => {
    const estadoCliente = dadosCompletos?.clientes?.estado || 'Parana';
    const tipoTabela = tabela === 1 || tabela === 'Tabela 1' ? 'Tabela 1' : 'Tabela 2';
    
    const itensForro = forrosEdicao.filter(f => f.tabela === tipoTabela && f.produto);
    const itensAcessorios = acessoriosEdicao.filter(a => a.tabela === tipoTabela && a.produto);
    
    const temItens = itensForro.length > 0 || itensAcessorios.length > 0;
    
    if (!temItens) {
      return { 
        resumo: [], 
        valorGerado: 0, 
        valorForro: 0,
        valorAcabamento: 0,
        custoProducao: 0, 
        lucro: 0, 
        margem: 0,
        pontuacao: 0, 
        tabela,
        temItens: false
      };
    }
    
    // Famílias que têm pontuação dividida por 3
    const FAMILIAS_PONTUACAO_DIVIDIDA = ['Forro 8 mm', 'Forro Colorido'];
    
    // ========================================
    // NOVA LÓGICA: Agrupar por FAMÍLIA do forro (não por forroId)
    // Se tem 2 linhas de "Forro 6mm" com metragens diferentes, soma tudo
    // A pontuação é calculada sobre o TOTAL da família
    // ========================================
    
    // Primeiro, agrupar forros por FAMÍLIA
    const gruposPorFamilia = {};
    
    itensForro.forEach(forro => {
      const familia = forro.familia || forro.produto;
      if (!gruposPorFamilia[familia]) {
        gruposPorFamilia[familia] = {
          familia: familia,
          // Margem exigida vem da família
          margemExigida: getMargemExigida(familia, estadoCliente, tipoTabela),
          // Totais do grupo (todos os forros da mesma família + acabamentos)
          totalVenda: 0,
          custoTotal: 0,
          // Totais separados para exibição
          totalForro: 0,
          custoForro: 0,
          qtdeForro: 0, // Total de m² da família
          totalAcabamentos: 0,
          custoAcabamentos: 0,
          // Lista de forroIds desta família (para vincular acabamentos) - inclui id e idOriginal
          forroIds: [],
          forroIdsOriginais: [],
          // Lista de acabamentos da família
          acabamentos: []
        };
      }
      
      const qtdeForro = parseFloat(forro.m2) || 0;
      const totalForro = parseFloat(forro.total) || 0;
      const custoForro = qtdeForro * (parseFloat(forro.custo) || 0);
      
      gruposPorFamilia[familia].qtdeForro += qtdeForro;
      gruposPorFamilia[familia].totalForro += totalForro;
      gruposPorFamilia[familia].custoForro += custoForro;
      gruposPorFamilia[familia].totalVenda += totalForro;
      gruposPorFamilia[familia].custoTotal += custoForro;
      gruposPorFamilia[familia].forroIds.push(forro.id);
      // Guardar também o idOriginal para poder fazer match com acabamentos
      if (forro.idOriginal !== undefined && forro.idOriginal !== null) {
        gruposPorFamilia[familia].forroIdsOriginais.push(forro.idOriginal);
      }
    });
    
    // Depois, adicionar acabamentos às suas respectivas famílias (pelo forroId)
    let fallbackIndex = 0; // Round-robin para distribuir órfãos entre famílias
    itensAcessorios.forEach(acess => {
      const forroId = acess.forroId;
      const forroIdOriginal = acess.forroIdOriginal;
      const vinculoForroId = acess.vinculoForroId;
      
      // Encontrar a família do forro pai - tentar várias formas de match
      let forroPai = null;
      
      // 1. Tentar pelo vinculoForroId (fallback criado em entrarModoEdicao)
      if (!forroPai && vinculoForroId !== undefined && vinculoForroId !== null) {
        forroPai = itensForro.find(f => String(f.id) === String(vinculoForroId));
      }
      
      // 2. Tentar pelo forroId (comparação direta)
      if (!forroPai && forroId !== undefined && forroId !== null) {
        forroPai = itensForro.find(f => f.id === forroId);
      }
      
      // 3. Tentar pelo idOriginal
      if (!forroPai && forroIdOriginal !== undefined && forroIdOriginal !== null) {
        forroPai = itensForro.find(f => f.idOriginal === forroIdOriginal);
      }
      
      // 4. Tentar comparação de string/number (forroId vs id e idOriginal)
      if (!forroPai && forroId !== undefined && forroId !== null) {
        forroPai = itensForro.find(f => 
          String(f.id) === String(forroId) || 
          (f.idOriginal !== undefined && f.idOriginal !== null && String(f.idOriginal) === String(forroId))
        );
      }
      
      // 5. Tentar parseInt para lidar com tipos mistos
      if (!forroPai && forroId !== undefined && forroId !== null) {
        const forroIdInt = parseInt(forroId);
        if (!isNaN(forroIdInt)) {
          forroPai = itensForro.find(f => parseInt(f.id) === forroIdInt || parseInt(f.idOriginal) === forroIdInt);
        }
      }
      
      // FALLBACK: Distribuir round-robin entre as famílias (não apenas a primeira)
      if (!forroPai && itensForro.length > 0) {
        forroPai = itensForro[fallbackIndex % itensForro.length];
        fallbackIndex++;
      }
      
      // Familia de destino: a do forro pai ou, se a tabela nao tem nenhum forro,
      // a familia do proprio acabamento (para o valor nao se perder do total)
      const familia = forroPai
        ? (forroPai.familia || forroPai.produto)
        : (acess.familia || acess.produto);
      if (!familia) return;

      if (!gruposPorFamilia[familia]) {
        gruposPorFamilia[familia] = {
          familia: familia,
          margemExigida: getMargemExigida(familia, estadoCliente, tipoTabela),
          totalVenda: 0,
          custoTotal: 0,
          totalForro: 0,
          custoForro: 0,
          qtdeForro: 0,
          totalAcabamentos: 0,
          custoAcabamentos: 0,
          forroIds: [],
          forroIdsOriginais: [],
          acabamentos: []
        };
      }

      const qtdeAcess = parseInt(acess.quantidade) || 0;
      const totalAcess = parseFloat(acess.total) || 0;
      const custoAcess = qtdeAcess * (parseFloat(acess.custo) || 0);

      gruposPorFamilia[familia].acabamentos.push({
        produto: acess.produto,
        qtde: qtdeAcess,
        preco: parseFloat(acess.preco) || 0,
        custo: parseFloat(acess.custo) || 0,
        total: totalAcess,
        forroId: forroId
      });

      gruposPorFamilia[familia].totalAcabamentos += totalAcess;
      gruposPorFamilia[familia].custoAcabamentos += custoAcess;
      gruposPorFamilia[familia].totalVenda += totalAcess;
      gruposPorFamilia[familia].custoTotal += custoAcess;
    });
    
    // Calcular métricas para cada FAMÍLIA (forro agrupado + acabamentos)
    let somaPontuacaoPonderada = 0;
    const resumoLinhas = [];
    
    Object.values(gruposPorFamilia).forEach(grupo => {
      const totalGrupo = grupo.totalVenda || 0;
      const custoGrupo = grupo.custoTotal || 0;
      const lucroGrupo = totalGrupo - custoGrupo;
      
      // Margem atingida = (Lucro / Total Venda) * 100 - calculada no TOTAL da FAMÍLIA
      const margemAtingida = totalGrupo > 0 ? (lucroGrupo / totalGrupo) * 100 : 0;
      
      // Margem exigida vem da FAMÍLIA
      const margemExigida = grupo.margemExigida || 0;
      
      // Margem mínima exigida em R$ = Total Venda da FAMÍLIA * Margem Exigida %
      const margemExigidaReais = totalGrupo * (margemExigida / 100);
      
      // Diferença em R$ = Lucro da FAMÍLIA - Margem Mínima Exigida (R$)
      const diferencaReais = lucroGrupo - margemExigidaReais;
      
      // Pontuação da família = Margem Atingida % - Margem Exigida %
      let pontuacaoGrupo = margemAtingida - margemExigida;
      
      // Se a família for "Forro 8 mm" ou "Forro Colorido", dividir pontuação por 3
      if (FAMILIAS_PONTUACAO_DIVIDIDA.includes(grupo.familia)) {
        pontuacaoGrupo = pontuacaoGrupo / 3;
      }
      
      // Aplicar limitador de 50 pontos por família
      const pontuacaoGrupoLimitada = Math.min(pontuacaoGrupo, 50);
      
      // Guardar métricas no grupo
      grupo.lucro = lucroGrupo;
      grupo.margemAtingida = margemAtingida;
      grupo.margemExigidaReais = margemExigidaReais;
      grupo.diferencaReais = diferencaReais;
      grupo.pontuacao = pontuacaoGrupo; // Pontuação real (sem limite)
      grupo.pontuacaoLimitada = pontuacaoGrupoLimitada; // Pontuação com limite de 50
      
      // Somar as pontuações LIMITADAS de cada família
      somaPontuacaoPonderada += pontuacaoGrupoLimitada;
    });
    
    // Criar entradas para o resumo
    // Forros são agrupados por FAMÍLIA, acabamentos aparecem individualmente
    Object.values(gruposPorFamilia).forEach(grupo => {
      const forroTotal = grupo.totalForro;
      const forroCusto = grupo.custoForro;
      const forroLucro = forroTotal - forroCusto;
      const forroMargemAtingida = forroTotal > 0 ? (forroLucro / forroTotal) * 100 : 0;
      const forroMargemExigidaReais = forroTotal * (grupo.margemExigida / 100);
      const forroDiferencaReais = forroLucro - forroMargemExigidaReais;
      
      // Preço médio ponderado por m²
      const precoMedio = grupo.qtdeForro > 0 ? forroTotal / grupo.qtdeForro : 0;
      
      // Grupos criados so por acabamento orfao (tabela sem forro) nao geram linha de forro
      if (grupo.forroIds.length > 0) resumoLinhas.push({
        produto: grupo.familia,
        preco: precoMedio,
        qtdade: grupo.qtdeForro,
        total: forroTotal,
        custoUnit: grupo.qtdeForro > 0 ? forroCusto / grupo.qtdeForro : 0,
        custoTotal: forroCusto,
        margemExigida: grupo.margemExigida,
        isForro: true,
        isAcabamento: false,
        lucro: forroLucro,
        margemAtingida: forroMargemAtingida,
        margemExigidaReais: forroMargemExigidaReais,
        diferencaReais: forroDiferencaReais,
        pontuacao: grupo.pontuacaoLimitada // Pontuação da família
      });
      
      // Adicionar acabamentos AGRUPADOS por produto (ex: rodaforros iguais são somados)
      const acabamentosAgrupados = {};
      
      grupo.acabamentos.forEach(acess => {
        const produtoNome = acess.produto;
        
        if (!acabamentosAgrupados[produtoNome]) {
          acabamentosAgrupados[produtoNome] = {
            produto: produtoNome,
            preco: acess.preco,
            custo: acess.custo,
            qtde: 0,
            total: 0,
            custoTotal: 0,
            forroId: acess.forroId
          };
        }
        
        acabamentosAgrupados[produtoNome].qtde += acess.qtde || 0;
        acabamentosAgrupados[produtoNome].total += acess.total || 0;
        acabamentosAgrupados[produtoNome].custoTotal += (acess.qtde || 0) * (acess.custo || 0);
      });
      
      // Adicionar os acabamentos agrupados ao resumo
      Object.values(acabamentosAgrupados).forEach(acessAgrupado => {
        const acessTotal = acessAgrupado.total || 0;
        const acessCusto = acessAgrupado.custoTotal || 0;
        const acessLucro = acessTotal - acessCusto;
        const acessMargemAtingida = acessTotal > 0 ? (acessLucro / acessTotal) * 100 : 0;
        const acessMargemExigidaReais = acessTotal * (grupo.margemExigida / 100);
        const acessDiferencaReais = acessLucro - acessMargemExigidaReais;
        
        resumoLinhas.push({
          produto: acessAgrupado.produto,
          preco: acessAgrupado.preco,
          qtdade: acessAgrupado.qtde,
          total: acessTotal,
          custoUnit: acessAgrupado.custo,
          custoTotal: acessCusto,
          margemExigida: grupo.margemExigida, // Usa margem da família
          isForro: false,
          isAcabamento: true,
          forroId: acessAgrupado.forroId,
          lucro: acessLucro,
          margemAtingida: acessMargemAtingida,
          margemExigidaReais: acessMargemExigidaReais,
          diferencaReais: acessDiferencaReais,
          pontuacao: grupo.pontuacaoLimitada // Usa pontuação da família
        });
      });
    });
    
    const valorGerado = Object.values(gruposPorFamilia).reduce((s, g) => s + (g.totalVenda || 0), 0);
    const valorForro = Object.values(gruposPorFamilia).reduce((s, g) => s + (g.totalForro || 0), 0);
    const valorAcabamento = Object.values(gruposPorFamilia).reduce((s, g) => s + (g.totalAcabamentos || 0), 0);
    const custoProducao = Object.values(gruposPorFamilia).reduce((s, g) => s + (g.custoTotal || 0), 0);
    const lucro = valorGerado - custoProducao;
    const margem = valorGerado > 0 ? (lucro / valorGerado) * 100 : 0;
    const pontuacao = somaPontuacaoPonderada;
    
    return { 
      resumo: resumoLinhas, 
      valorGerado, 
      valorForro,
      valorAcabamento,
      custoProducao, 
      lucro, 
      margem,
      pontuacao, 
      tabela,
      temItens: true
    };
  };

  const calcularResumoTabela = (forros, acessorios) => {
    const estadoCliente = dadosCompletos?.clientes?.estado || 'Parana';
    // Determinar tipo_tabela a partir dos itens
    const tipoTabela = forros.length > 0 ? (forros[0].tipo_tabela || 'Tabela 1') : 
                       acessorios.length > 0 ? (acessorios[0].tipo_tabela || 'Tabela 1') : 'Tabela 1';
    
    // ========================================
    // NOVA LÓGICA: Agrupar por FAMÍLIA do forro (igual NovoPedido)
    // Se tem 2 linhas de "Forro 6mm" com metragens diferentes, soma tudo
    // Depois agrupa os acabamentos por produto dentro de cada família
    // ========================================
    
    const gruposPorFamilia = {};
    
    // Primeiro, agrupar forros por FAMÍLIA
    forros.forEach(item => {
      const familia = item.familia_produto || item.produto_nome;
      
      if (!gruposPorFamilia[familia]) {
        gruposPorFamilia[familia] = {
          familia: familia,
          margemExigida: getMargemExigida(familia, estadoCliente, tipoTabela),
          totalForro: 0,
          custoForro: 0,
          qtdeForro: 0,
          forroIds: [],
          acabamentos: []
        };
      }
      
      // Recalcular m2 e total se estiverem zerados
      let m2Item = parseFloat(item.m2) || 0;
      if (m2Item === 0 && item.quantidade && item.comprimento) {
        m2Item = (parseFloat(item.quantidade) || 0) * (parseFloat(item.comprimento) || 0) * 0.2;
      }
      
      let totalItem = parseFloat(item.total) || 0;
      if (totalItem === 0 && m2Item > 0 && item.preco_unitario) {
        totalItem = m2Item * (parseFloat(item.preco_unitario) || 0);
      }
      
      gruposPorFamilia[familia].qtdeForro += m2Item;
      gruposPorFamilia[familia].totalForro += totalItem;
      gruposPorFamilia[familia].custoForro += (m2Item * (parseFloat(item.custo_unitario) || 0));
      gruposPorFamilia[familia].forroIds.push(item.id);
    });
    
    // Depois, adicionar acabamentos às suas respectivas famílias
    // Melhoria: quando forro_id não bate, distribui por round-robin entre as famílias
    // (mesmo approach do modo edição com vinculoForroId)
    const familiasKeys = Object.keys(gruposPorFamilia);
    let unmatchedIdx = 0;
    
    acessorios.forEach(item => {
      const forroIdAcessorio = item.forro_id || item.forroId;
      
      // Encontrar a família do forro pai
      let familiaPai = null;
      
      // Tentativa 1: match direto por forro_id no array de IDs da família
      if (forroIdAcessorio !== null && forroIdAcessorio !== undefined) {
        for (const [familia, grupo] of Object.entries(gruposPorFamilia)) {
          if (grupo.forroIds.some(id => String(id) === String(forroIdAcessorio))) {
            familiaPai = familia;
            break;
          }
        }
      }
      
      // Tentativa 2: se forro_id é numérico, tentar match por parseInt
      if (!familiaPai && forroIdAcessorio !== null && forroIdAcessorio !== undefined) {
        const forroIdNum = parseInt(forroIdAcessorio);
        if (!isNaN(forroIdNum)) {
          for (const [familia, grupo] of Object.entries(gruposPorFamilia)) {
            if (grupo.forroIds.some(id => parseInt(id) === forroIdNum)) {
              familiaPai = familia;
              break;
            }
          }
        }
      }
      
      // FALLBACK: distribuir round-robin entre as famílias (melhor que tudo na primeira)
      if (!familiaPai && familiasKeys.length > 0) {
        familiaPai = familiasKeys[unmatchedIdx % familiasKeys.length];
        unmatchedIdx++;
      }

      // Tabela sem nenhum forro: cria o grupo pela familia do proprio acabamento,
      // senao o valor do acabamento sumiria do resumo e do total
      if (!familiaPai) {
        familiaPai = item.familia_produto || item.produto_nome;
        if (familiaPai && !gruposPorFamilia[familiaPai]) {
          gruposPorFamilia[familiaPai] = {
            familia: familiaPai,
            margemExigida: getMargemExigida(familiaPai, estadoCliente, tipoTabela),
            totalForro: 0,
            custoForro: 0,
            qtdeForro: 0,
            forroIds: [],
            acabamentos: []
          };
        }
      }

      if (familiaPai && gruposPorFamilia[familiaPai]) {
        const qtdeItem = parseFloat(item.quantidade) || 0;
        let totalItem = parseFloat(item.total) || 0;
        if (totalItem === 0 && qtdeItem > 0 && item.preco_unitario) {
          totalItem = qtdeItem * (parseFloat(item.preco_unitario) || 0);
        }
        
        gruposPorFamilia[familiaPai].acabamentos.push({
          produto: item.produto_nome,
          preco: parseFloat(item.preco_unitario) || 0,
          custo: parseFloat(item.custo_unitario) || 0,
          qtde: qtdeItem,
          total: totalItem,
          custoTotal: qtdeItem * (parseFloat(item.custo_unitario) || 0)
        });
      }
    });
    
    // Famílias que têm pontuação dividida por 3 (igual NovoPedido)
    const FAMILIAS_PONTUACAO_DIVIDIDA = ['Forro 8 mm', 'Forro Colorido'];
    
    // Calcular métricas por FAMÍLIA (igual NovoPedido)
    Object.values(gruposPorFamilia).forEach(grupo => {
      const totalGrupo = grupo.totalForro + grupo.acabamentos.reduce((s, a) => s + a.total, 0);
      const custoGrupo = grupo.custoForro + grupo.acabamentos.reduce((s, a) => s + a.custoTotal, 0);
      const lucroGrupo = totalGrupo - custoGrupo;
      const margemAtingida = totalGrupo > 0 ? (lucroGrupo / totalGrupo) * 100 : 0;
      
      // Pontuação da família = Margem Atingida % - Margem Exigida %
      let pontuacaoGrupo = margemAtingida - grupo.margemExigida;
      
      // Se a família for "Forro 8 mm" ou "Forro Colorido", dividir pontuação por 3
      if (FAMILIAS_PONTUACAO_DIVIDIDA.includes(grupo.familia)) {
        pontuacaoGrupo = pontuacaoGrupo / 3;
      }
      
      // Aplicar limitador de 50 pontos por família
      grupo.pontuacaoLimitada = Math.min(pontuacaoGrupo, 50);
    });
    
    // Criar entradas para o resumo: primeiro forros, depois acabamentos agrupados
    const resumoLinhas = [];
    
    Object.values(gruposPorFamilia).forEach(grupo => {
      const forroTotal = grupo.totalForro;
      const forroCusto = grupo.custoForro;
      const forroLucro = forroTotal - forroCusto;
      const forroMargemAtingida = forroTotal > 0 ? (forroLucro / forroTotal) * 100 : 0;
      const forroMargemExigidaReais = forroTotal * (grupo.margemExigida / 100);
      const forroDiferencaReais = forroLucro - forroMargemExigidaReais;
      
      // Preço médio ponderado por m²
      const precoMedio = grupo.qtdeForro > 0 ? forroTotal / grupo.qtdeForro : 0;
      
      // Adicionar linha do forro
      // Grupos criados so por acabamento orfao (tabela sem forro) nao geram linha de forro
      if (grupo.forroIds.length > 0) resumoLinhas.push({
        produto: grupo.familia,
        preco: precoMedio,
        qtdade: grupo.qtdeForro,
        total: forroTotal,
        custoUnit: grupo.qtdeForro > 0 ? forroCusto / grupo.qtdeForro : 0,
        custoTotal: forroCusto,
        margemExigida: grupo.margemExigida,
        isForro: true,
        isAcabamento: false,
        lucro: forroLucro,
        margemAtingida: forroMargemAtingida,
        margemExigidaReais: forroMargemExigidaReais,
        diferencaReais: forroDiferencaReais,
        pontuacao: grupo.pontuacaoLimitada // Pontuação da família
      });
      
      // Agrupar acabamentos por produto (somar quantidades e valores de produtos iguais)
      const acabamentosAgrupados = {};
      grupo.acabamentos.forEach(acess => {
        const produtoNome = acess.produto;
        if (!acabamentosAgrupados[produtoNome]) {
          acabamentosAgrupados[produtoNome] = {
            produto: produtoNome,
            preco: acess.preco,
            custo: acess.custo,
            qtde: 0,
            total: 0,
            custoTotal: 0
          };
        }
        acabamentosAgrupados[produtoNome].qtde += acess.qtde;
        acabamentosAgrupados[produtoNome].total += acess.total;
        acabamentosAgrupados[produtoNome].custoTotal += acess.custoTotal;
      });
      
      // Adicionar acabamentos agrupados ao resumo
      Object.values(acabamentosAgrupados).forEach(acessAgrupado => {
        const acessTotal = acessAgrupado.total || 0;
        const acessCusto = acessAgrupado.custoTotal || 0;
        const acessLucro = acessTotal - acessCusto;
        const acessMargemAtingida = acessTotal > 0 ? (acessLucro / acessTotal) * 100 : 0;
        const acessMargemExigidaReais = acessTotal * (grupo.margemExigida / 100);
        const acessDiferencaReais = acessLucro - acessMargemExigidaReais;
        
        resumoLinhas.push({
          produto: acessAgrupado.produto,
          preco: acessAgrupado.preco,
          qtdade: acessAgrupado.qtde,
          total: acessTotal,
          custoUnit: acessAgrupado.custo,
          custoTotal: acessCusto,
          margemExigida: grupo.margemExigida,
          isForro: false,
          isAcabamento: true,
          lucro: acessLucro,
          margemAtingida: acessMargemAtingida,
          margemExigidaReais: acessMargemExigidaReais,
          diferencaReais: acessDiferencaReais,
          pontuacao: grupo.pontuacaoLimitada // Usa pontuação da família
        });
      });
    });
    
    // Somar pontuação total de todas as famílias (igual NovoPedido)
    const pontuacaoTotal = Object.values(gruposPorFamilia).reduce((sum, grupo) => sum + grupo.pontuacaoLimitada, 0);
    
    return { resumo: resumoLinhas, pontuacao: pontuacaoTotal };
  };

  // Resumo simples para Resumo da Compra em modo edição (ordem Forro -> Acabamentos)
  // AGRUPADO POR FAMÍLIA igual NovoPedido
  const buildResumoCompraEdicao = (tabela) => {
    const forros = forrosEdicao.filter(f => f.tabela === tabela && f.produto);
    const acessorios = acessoriosEdicao.filter(a => a.tabela === tabela && a.produto);

    const pertenceAoForro = (acessorio, forro) => {
      const aForroId = acessorio.forroId !== undefined && acessorio.forroId !== null ? String(acessorio.forroId) : null;
      const aForroIdOriginal = acessorio.forroIdOriginal !== undefined && acessorio.forroIdOriginal !== null ? String(acessorio.forroIdOriginal) : null;
      const aVinculoForroId = acessorio.vinculoForroId !== undefined && acessorio.vinculoForroId !== null ? String(acessorio.vinculoForroId) : null;
      const forroId = String(forro.id);
      const forroIdOriginal = forro.idOriginal !== undefined && forro.idOriginal !== null ? String(forro.idOriginal) : null;

      return (
        (aVinculoForroId && aVinculoForroId === forroId) ||
        (aForroId && (aForroId === forroId || (forroIdOriginal && aForroId === forroIdOriginal))) ||
        (aForroIdOriginal && (aForroIdOriginal === forroId || (forroIdOriginal && aForroIdOriginal === forroIdOriginal)))
      );
    };

    // ========================================
    // AGRUPAR FORROS POR FAMÍLIA
    // ========================================
    const gruposFamilia = {};
    
    forros.forEach(forro => {
      const familia = forro.familia || forro.produto;
      
      if (!gruposFamilia[familia]) {
        gruposFamilia[familia] = {
          familia: familia,
          m2Total: 0,
          totalVenda: 0,
          forroIds: [],
          acabamentos: []
        };
      }
      
      gruposFamilia[familia].m2Total += parseFloat(forro.m2) || 0;
      gruposFamilia[familia].totalVenda += parseFloat(forro.total) || 0;
      gruposFamilia[familia].forroIds.push(forro.id);
      if (forro.idOriginal !== undefined && forro.idOriginal !== null) {
        gruposFamilia[familia].forroIds.push(forro.idOriginal);
      }
    });
    
    // Vincular acessórios aos grupos de família
    const usados = new Set();
    const familiasKeys = Object.keys(gruposFamilia);
    let fallbackIdx = 0;
    
    acessorios.forEach(acess => {
      // Encontrar a família do forro pai
      let familiaPai = null;
      for (const forro of forros) {
        if (pertenceAoForro(acess, forro)) {
          familiaPai = forro.familia || forro.produto;
          break;
        }
      }
      
      // Fallback: distribuir round-robin entre famílias
      if (!familiaPai && familiasKeys.length > 0) {
        familiaPai = familiasKeys[fallbackIdx % familiasKeys.length];
        fallbackIdx++;
      }
      
      if (familiaPai && gruposFamilia[familiaPai]) {
        usados.add(String(acess.id));
        gruposFamilia[familiaPai].acabamentos.push({
          produto: acess.produto,
          preco: parseFloat(acess.preco) || 0,
          qtde: parseFloat(acess.quantidade) || 0,
          total: parseFloat(acess.total) || 0
        });
      }
    });
    
    // Construir linhas de resumo
    const linhas = [];
    
    Object.values(gruposFamilia).forEach(grupo => {
      // Preço médio ponderado
      const precoMedio = grupo.m2Total > 0 ? grupo.totalVenda / grupo.m2Total : 0;
      
      // Adicionar linha do forro agrupado
      linhas.push({
        produto: grupo.familia,
        preco: precoMedio,
        qtdade: grupo.m2Total,
        total: grupo.totalVenda,
        isForro: true,
        isAcabamento: false
      });
      
      // Agrupar acabamentos por produto
      const acabamentosAgrupados = {};
      grupo.acabamentos.forEach(acess => {
        if (!acabamentosAgrupados[acess.produto]) {
          acabamentosAgrupados[acess.produto] = {
            produto: acess.produto,
            preco: acess.preco,
            qtdade: 0,
            total: 0,
            isForro: false,
            isAcabamento: true
          };
        }
        acabamentosAgrupados[acess.produto].qtdade += acess.qtde;
        acabamentosAgrupados[acess.produto].total += acess.total;
      });
      
      // Adicionar acabamentos agrupados
      Object.values(acabamentosAgrupados).forEach(acessAgrupado => {
        linhas.push(acessAgrupado);
      });
    });

    // Órfãos (sem vínculo) no final - também agrupados
    const orfaos = acessorios.filter(a => !usados.has(String(a.id)));
    const orfaosAgrupados = {};
    
    orfaos.forEach(acess => {
      const produtoNome = acess.produto;
      
      if (!orfaosAgrupados[produtoNome]) {
        orfaosAgrupados[produtoNome] = {
          produto: produtoNome,
          preco: parseFloat(acess.preco) || 0,
          qtdade: 0,
          total: 0,
          isForro: false,
          isAcabamento: true
        };
      }
      
      orfaosAgrupados[produtoNome].qtdade += parseFloat(acess.quantidade) || 0;
      orfaosAgrupados[produtoNome].total += parseFloat(acess.total) || 0;
    });
    
    Object.values(orfaosAgrupados).forEach(orfaoAgrupado => {
      linhas.push(orfaoAgrupado);
    });

    return linhas;
  };

  const getStatusAptidao = (status) => {
    if (['EXCELENTE', 'BOA VENDA', 'VIÁVEL'].includes(status)) return { texto: 'Aprovado Automaticamente', cor: 'text-green-600', bg: 'bg-green-50', icon: CheckCircle };
    if (status === 'APROVADO') return { texto: 'Aprovado pela Diretoria', cor: 'text-teal-600', bg: 'bg-teal-50', icon: ShieldCheck };
    if (status === 'EM ANÁLISE') return { texto: 'Aguardando Próxima Etapa', cor: 'text-amber-600', bg: 'bg-amber-50', icon: Clock };
    if (status === 'RECUSADO') return { texto: 'Recusado', cor: 'text-red-600', bg: 'bg-red-50', icon: XCircle };
    if (status === 'REANÁLISE') return { texto: 'Enviado para Reanálise', cor: 'text-amber-600', bg: 'bg-amber-50', icon: RotateCcw };
    return { texto: 'Não Aprovado', cor: 'text-red-600', bg: 'bg-red-50', icon: XCircle };
  };

  // ========================================
  // LÓGICA CORRIGIDA: Verificar se pode imprimir
  // SEMPRE permite imprimir orçamento, independente do status
  // ========================================
  const podeImprimirProposta = () => {
    if (!dadosCompletos) return false;
    
    // Sempre permitir impressão do orçamento/pedido
    return true;
  };

  // Função para chamar o serviço de impressão
  const handleImprimirProposta = (tabela) => {
    if (!dadosCompletos) return;
    
    const cliente = dadosCompletos.clientes || {
      nome: dadosCompletos.cliente_nome,
      cnpj: dadosCompletos.cnpj,
      endereco: dadosCompletos.endereco,
      bairro: dadosCompletos.bairro,
      cidade: dadosCompletos.cidade,
      estado: dadosCompletos.estado,
      telefone: dadosCompletos.telefone,
      email: dadosCompletos.email
    };

    // Pegar formas de pagamento dos campos individuais do pedido
    const formasPagamento = getFormasPagamento(dadosCompletos);

    // Mapear forros do formato do banco para o formato esperado pelo componente de impressão
    const forrosMapeados = (dadosCompletos.forros || []).map(f => ({
      ...f,
      produto: f.produto_nome || f.produto || '',
      codigo: f.produto_codigo || f.codigo || '',
      tip: f.tipo || f.tip || 'BCO',
      qtde: f.quantidade || f.qtde || 0,
      comp: f.comprimento || f.comp || 0,
      m2: f.m2 || 0,
      preco: f.preco_unitario || f.preco || 0,
      total: f.total || 0,
      ordem: f.tipo_tabela || f.ordem || 'Tabela 1',
      tabela: f.tipo_tabela || f.ordem || 'Tabela 1'
    }));

    // Mapear acessórios do formato do banco para o formato esperado pelo componente de impressão
    const acessoriosMapeados = (dadosCompletos.acessorios || []).map(a => ({
      ...a,
      produto: a.produto_nome || a.produto || '',
      cod: a.produto_codigo || a.cod || '',
      codigo: a.produto_codigo || a.codigo || '',
      tip: a.tipo || a.tip || 'BCO',
      qtde: a.quantidade || a.qtde || 0,
      preco: a.preco_unitario || a.preco || 0,
      total: a.total || 0,
      ordem: a.tipo_tabela || a.ordem || 'Tabela 1',
      tabela: a.tipo_tabela || a.ordem || 'Tabela 1'
    }));

    // Mapear produtos especiais (Demais Produtos)
    const produtosEspeciaisMapeados = (dadosCompletos.produtosEspeciais || []).map(p => ({
      ...p,
      produto_nome: p.produto_nome || '',
      produto_codigo: p.produto_codigo || '',
      familia_produto: p.familia_produto || 'Demais Produtos',
      quantidade: p.quantidade || 1,
      preco_unitario: p.preco_unitario || 0,
      total: p.total || 0
    }));

    imprimirPropostaService({
      pedido: dadosCompletos,
      cliente: cliente,
      forros: forrosMapeados,
      acessorios: acessoriosMapeados,
      produtosEspeciais: produtosEspeciaisMapeados,
      formaPagamento: formasPagamento,
      observacao: dadosCompletos.observacao_pedido || dadosCompletos.observacao || dadosCompletos.observacoes || '',
      tabela: tabela,
      valorFrete: dadosCompletos.tem_taxa_entrega ? (parseFloat(dadosCompletos.valor_taxa_entrega) || 0) : 0,
      cargoUsuario: usuarioLogado?.departamento || ''
    });
  };

  const formatarData = (data) => {
    if (!data) return '-';
    if (typeof data === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(data)) {
      const [ano, mes, dia] = data.split('-');
      return `${dia}/${mes}/${ano}`;
    }
    const dataObj = new Date(data);
    return dataObj.toLocaleDateString('pt-BR');
  };

  // Formata data e hora no formato brasileiro (horário local)
  const formatarDataHora = (data) => {
    if (!data) return '-';
    const dataObj = new Date(data);
    return dataObj.toLocaleDateString('pt-BR') + ', ' + dataObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatarHora = (hora) => hora || '-';

  const formatarMoeda = (valor) => {
    return (valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Função para limpar nome de produto removendo código no início (ex: "R Rodaforro..." vira "Rodaforro...")
  const limparNomeProduto = (nome) => {
    if (!nome) return nome;
    // Remove código de 1-3 caracteres seguido de espaço no início do nome
    return nome.replace(/^[A-Z0-9]{1,3}\s+/, '');
  };

  // ========================================
  // FUNÇÕES HELPER PARA CALCULAR M² E TOTAL
  // Recalcula valores se estiverem zerados no banco
  // ========================================
  const calcularM2Item = (item) => {
    // Primeiro tenta usar o m2 salvo
    let m2 = parseFloat(item.m2) || 0;
    // Se m2 está zerado mas temos quantidade e comprimento, recalcular
    // M² = quantidade × comprimento × 0.2 (largura padrão do forro de 200mm = 0.2m)
    if (m2 === 0 && item.quantidade && item.comprimento) {
      m2 = (parseFloat(item.quantidade) || 0) * (parseFloat(item.comprimento) || 0) * 0.2;
    }
    return m2;
  };

  const calcularTotalItem = (item, isForro = true) => {
    // Primeiro tenta usar o total salvo
    let total = parseFloat(item.total) || 0;
    // Se total está zerado mas temos os dados necessários, recalcular
    if (total === 0) {
      if (isForro) {
        // Para forro: total = m² × preço unitário
        const m2 = calcularM2Item(item);
        const preco = parseFloat(item.preco_unitario) || 0;
        if (m2 > 0 && preco > 0) {
          total = m2 * preco;
        }
      } else {
        // Para acessório: total = quantidade × preço unitário
        const qtde = parseFloat(item.quantidade) || 0;
        const preco = parseFloat(item.preco_unitario) || 0;
        if (qtde > 0 && preco > 0) {
          total = qtde * preco;
        }
      }
    }
    return total;
  };

  // Função para formatar pontuação (trava máx 50, sem "+" na frente)
  const formatarPontuacao = (valor) => {
    const pontuacao = valor || 0;
    // Trava máxima de 50 pontos para exibição
    const pontuacaoExibida = pontuacao > 50 ? 50 : pontuacao;
    return pontuacaoExibida.toFixed(2).replace('.', ',');
  };

  // Função para calcular tempo total do pedido
  const calcularTempoTotal = (horaInicio, horaFim) => {
    if (!horaInicio || !horaFim) return null;
    
    const [horaIni, minIni] = horaInicio.split(':').map(Number);
    const [horaF, minF] = horaFim.split(':').map(Number);
    
    const minutosInicio = horaIni * 60 + minIni;
    const minutosFim = horaF * 60 + minF;
    
    let diferencaMinutos = minutosFim - minutosInicio;
    if (diferencaMinutos < 0) diferencaMinutos += 24 * 60;
    
    const horas = Math.floor(diferencaMinutos / 60);
    const minutos = diferencaMinutos % 60;
    
    if (horas === 0) return `${minutos} minuto${minutos !== 1 ? 's' : ''}`;
    if (minutos === 0) return `${horas} hora${horas !== 1 ? 's' : ''}`;
    return `${horas} hora${horas !== 1 ? 's' : ''} e ${minutos} minuto${minutos !== 1 ? 's' : ''}`;
  };

  // Obter formas de pagamento do pedido
  const getFormasPagamento = (pedido) => {
    const formas = [];
    if (pedido.forma_pagamento_1) formas.push({ forma: pedido.forma_pagamento_1, observacao: pedido.observacao_pagamento_1 });
    if (pedido.forma_pagamento_2) formas.push({ forma: pedido.forma_pagamento_2, observacao: pedido.observacao_pagamento_2 });
    if (pedido.forma_pagamento_3) formas.push({ forma: pedido.forma_pagamento_3, observacao: pedido.observacao_pagamento_3 });
    if (pedido.forma_pagamento_4) formas.push({ forma: pedido.forma_pagamento_4, observacao: pedido.observacao_pagamento_4 });
    return formas;
  };

  // Funções auxiliares para formas de pagamento em modo edição (igual NovoPedido)
  const getFormasPagamentoEdicao = () => {
    return [
      { forma: dadosEdicao.forma_pagamento_1 || '', observacao: dadosEdicao.observacao_pagamento_1 || '' },
      { forma: dadosEdicao.forma_pagamento_2 || '', observacao: dadosEdicao.observacao_pagamento_2 || '' },
      { forma: dadosEdicao.forma_pagamento_3 || '', observacao: dadosEdicao.observacao_pagamento_3 || '' },
      { forma: dadosEdicao.forma_pagamento_4 || '', observacao: dadosEdicao.observacao_pagamento_4 || '' }
    ];
  };

  const atualizarFormaPagamentoEdicao = (index, campo, valor) => {
    const num = index + 1;
    if (campo === 'forma') {
      setDadosEdicao(prev => ({ ...prev, [`forma_pagamento_${num}`]: valor }));
    } else {
      setDadosEdicao(prev => ({ ...prev, [`observacao_pagamento_${num}`]: valor }));
    }
  };

  const limparFormaPagamentoEdicao = (index) => {
    const num = index + 1;
    setDadosEdicao(prev => ({ 
      ...prev, 
      [`forma_pagamento_${num}`]: '', 
      [`observacao_pagamento_${num}`]: '' 
    }));
  };

  const getFormasPagamentoSelecionadasEdicao = () => {
    let count = 0;
    if (dadosEdicao.forma_pagamento_1) count++;
    if (dadosEdicao.forma_pagamento_2) count++;
    if (dadosEdicao.forma_pagamento_3) count++;
    if (dadosEdicao.forma_pagamento_4) count++;
    return count;
  };

  useEffect(() => {
    setQuantidadeVisivel(10);
  }, [filtros]);

  // Só refiltra 250ms depois da última tecla. Sem isso cada caractere digitado
  // reprocessava a lista inteira de pedidos e a busca engasgava.
  useEffect(() => {
    const timer = setTimeout(() => setBuscaDebounced(filtros.busca), 250);
    return () => clearTimeout(timer);
  }, [filtros.busca]);

  // Função para calcular status geral do pedido baseado na pontuação
  const calcularStatusGeralPedido = useCallback((p) => {
    const valorTab1 = parseFloat(p.valor_tabela1) || 0;
    const valorTab2 = parseFloat(p.valor_tabela2) || 0;
    const valorProdEspeciais = parseFloat(p.valor_produtos_especiais) || 0;
    const temTab1 = valorTab1 > 0;
    const temTab2 = valorTab2 > 0;
    const temProdEspeciais = valorProdEspeciais > 0;

    // Se não tem itens nas tabelas, mas tem produtos especiais,
    // usar o status_geral/pontuacao_geral do banco (já calculados na criação)
    if (!temTab1 && !temTab2) {
      if (temProdEspeciais && p.status_geral) {
        return p.status_geral;
      }
      if (temProdEspeciais && p.pontuacao_geral != null) {
        return getStatusByScore(parseFloat(p.pontuacao_geral) || 0);
      }
      return 'INVIÁVEL';
    }

    // Sempre recalcular pela soma das pontuações individuais das tabelas,
    // pois pontuacao_geral e status_geral no banco podem estar desatualizados.
    const pontTab1 = temTab1 ? Math.min(parseFloat(p.pontuacao_tabela1) || 0, 50) : 0;
    const pontTab2 = temTab2 ? Math.min(parseFloat(p.pontuacao_tabela2) || 0, 50) : 0;
    const pontuacaoGeral = pontTab1 + pontTab2;

    return getStatusByScore(pontuacaoGeral);
  }, []);

  // Campos do filtro separados do objeto `filtros`: como o objeto é recriado a
  // cada tecla digitada, usá-lo direto na dependência anularia o debounce.
  const {
    tipo: filtroTipo,
    fase: filtroFase,
    scoreVenda: filtroScoreVenda,
    responsavel: filtroResponsavel,
    dataInicio: filtroDataInicio,
    dataFim: filtroDataFim,
    dataFaturamentoInicio: filtroFaturamentoInicio,
    dataFaturamentoFim: filtroFaturamentoFim,
    produtosEspeciais: filtroProdutosEspeciais
  } = filtros;

  // Memorizado: antes esta filtragem rodava a cada render (inclusive ao abrir
  // um modal ou digitar), percorrendo todos os pedidos várias vezes.
  const pedidosFiltrados = useMemo(() => {
    const buscaLower = buscaDebounced ? buscaDebounced.toLowerCase() : '';
    const buscaSemPontuacao = buscaLower.replace(/[.\-\/]/g, '');

    // Datas convertidas uma única vez, fora do laço
    const limiteInicio = filtroDataInicio ? new Date(filtroDataInicio) : null;
    const limiteFim = filtroDataFim ? new Date(filtroDataFim + 'T23:59:59') : null;
    const limiteFatInicio = filtroFaturamentoInicio ? new Date(filtroFaturamentoInicio) : null;
    const limiteFatFim = filtroFaturamentoFim ? new Date(filtroFaturamentoFim + 'T23:59:59') : null;

    return pedidos.filter(p => {
      const clienteNome = p.clientes?.nome || '';
      const clienteCpfCnpj = (p.clientes?.cnpj || '').replace(/[.\-\/]/g, '').toLowerCase();
      const buscaMatch = !buscaLower ||
        clienteNome.toLowerCase().includes(buscaLower) ||
        (p.numero_proposta && p.numero_proposta.toLowerCase().includes(buscaLower)) ||
        (p.vendedor && p.vendedor.toLowerCase().includes(buscaLower)) ||
        (p.bairro && p.bairro.toLowerCase().includes(buscaLower)) ||
        (clienteCpfCnpj && clienteCpfCnpj.includes(buscaSemPontuacao));

      if (!buscaMatch) return false;

      // Filtro por tipo de documento (Orçamento ou Pedido)
      const tipoDoc = (p.tipo_documento || 'Orcamento').toUpperCase();
      if (filtroTipo && tipoDoc !== filtroTipo.toUpperCase()) return false;

      // Filtro por fase atual - "Cancelado" agrupa todos os status de cancelamento
      if (filtroFase) {
        if (filtroFase === 'CANCELADO') {
          const statusCancelamento = ['CANCELADO', 'PEDIDO_CANCELADO', 'PEDIDO_CANCELADO_POS_FATURAMENTO', 'ORCAMENTO_CANCELADO'];
          if (!statusCancelamento.includes(p.fase_atual) && !p.cancelado_por) return false;
        } else if (p.fase_atual !== filtroFase) {
          return false;
        }
      }

      // Filtro por score de venda (status de pontuação)
      if (filtroScoreVenda && calcularStatusGeralPedido(p) !== filtroScoreVenda) return false;

      if (filtroResponsavel && p.vendedor !== filtroResponsavel) return false;

      // Filtro por produtos especiais
      const valorProdEspeciais = parseFloat(p.valor_produtos_especiais) || 0;
      if (filtroProdutosEspeciais === 'SIM' && !(valorProdEspeciais > 0)) return false;
      if (filtroProdutosEspeciais === 'NAO' && valorProdEspeciais !== 0) return false;
      if (filtroProdutosEspeciais === 'APENAS') {
        // Apenas produtos especiais (sem forros/acessórios)
        const valorTab1 = parseFloat(p.valor_tabela1) || 0;
        const valorTab2 = parseFloat(p.valor_tabela2) || 0;
        if (!(valorProdEspeciais > 0 && valorTab1 === 0 && valorTab2 === 0)) return false;
      }

      if (limiteInicio && p.created_at && new Date(p.created_at) < limiteInicio) return false;
      if (limiteFim && p.created_at && new Date(p.created_at) > limiteFim) return false;

      if (limiteFatInicio || limiteFatFim) {
        if (p.fase_atual !== 'PEDIDO_FATURADO') return false;
        const dataRefFaturamento = new Date(p.faturado_data || p.created_at);
        if (limiteFatInicio && dataRefFaturamento < limiteFatInicio) return false;
        if (limiteFatFim && dataRefFaturamento > limiteFatFim) return false;
      }

      return true;
    });
  }, [
    pedidos, buscaDebounced, filtroTipo, filtroFase, filtroScoreVenda,
    filtroResponsavel, filtroDataInicio, filtroDataFim,
    filtroFaturamentoInicio, filtroFaturamentoFim, filtroProdutosEspeciais,
    calcularStatusGeralPedido
  ]);

  // Estatísticas do Dashboard - baseadas na FASE ATUAL do fluxo.
  // Uma única passagem pela lista em vez das 6 varreduras separadas de antes.
  const { totalPedidos, totalGeral, faturados, confirmados, aguardando, cancelados, arquivados } = useMemo(() => {
    const FASES_AGUARDANDO = ['AGUARDANDO_PROXIMA_ETAPA', 'SOLICITAR_EDICAO'];
    const FASES_CANCELADAS = ['ORCAMENTO_CANCELADO', 'ORCAMENTO_RECUSADO', 'PEDIDO_CANCELADO', 'PEDIDO_CANCELADO_POS_FATURAMENTO', 'CANCELADO'];

    const acc = {
      totalPedidos: pedidosFiltrados.length,
      totalGeral: 0, faturados: 0, confirmados: 0,
      aguardando: 0, cancelados: 0, arquivados: 0
    };

    for (const p of pedidosFiltrados) {
      acc.totalGeral += (parseFloat(p.valor_tabela1) || 0)
        + (parseFloat(p.valor_tabela2) || 0)
        + (parseFloat(p.valor_produtos_especiais) || 0);

      const fase = p.fase_atual;
      if (fase === 'PEDIDO_FATURADO') acc.faturados++;
      else if (fase === 'PEDIDO_CONFIRMADO') acc.confirmados++;
      else if (fase === 'ARQUIVADO') acc.arquivados++;

      if (FASES_AGUARDANDO.includes(fase)) acc.aguardando++;
      if (FASES_CANCELADAS.includes(fase)) acc.cancelados++;
    }

    return acc;
  }, [pedidosFiltrados]);

  // ==================== TELA DE DETALHES - NOVO DESIGN ====================
  if (pedidoSelecionado && dadosCompletos) {
    const cliente = dadosCompletos.clientes || {};
    
    // ========================================
    // USAR CÁLCULO DINÂMICO QUANDO EM MODO EDIÇÃO
    // ========================================
    let resumoTab1, resumoTab2, pontuacaoTab1Calc, pontuacaoTab2Calc, statusTab1, statusTab2;
    let valorTotalTab1, valorTotalTab2, custoTotalTab1, custoTotalTab2, lucroTab1, lucroTab2;
    let temItensTab1, temItensTab2;
    
    if (modoEdicao) {
      // Calcular resumos dinamicamente igual NovoPedido
      const tab1Data = calcularResumoTabelaEdicao('Tabela 1');
      const tab2Data = calcularResumoTabelaEdicao('Tabela 2');
      
      resumoTab1 = tab1Data.resumo;
      resumoTab2 = tab2Data.resumo;
      pontuacaoTab1Calc = tab1Data.pontuacao;
      pontuacaoTab2Calc = tab2Data.pontuacao;
      temItensTab1 = tab1Data.temItens;
      temItensTab2 = tab2Data.temItens;
      valorTotalTab1 = tab1Data.valorGerado;
      valorTotalTab2 = tab2Data.valorGerado;
      custoTotalTab1 = tab1Data.custoProducao;
      custoTotalTab2 = tab2Data.custoProducao;
      lucroTab1 = tab1Data.lucro;
      lucroTab2 = tab2Data.lucro;
      
      // Calcular status baseado na pontuação calculada
      statusTab1 = temItensTab1 ? getStatusByScore(pontuacaoTab1Calc) : null;
      statusTab2 = temItensTab2 ? getStatusByScore(pontuacaoTab2Calc) : null;
    } else {
      // Usar dados do banco
      const forrosTab1 = (dadosCompletos.forros || []).filter(f => f.tipo_tabela === 'Tabela 1');
      const forrosTab2 = (dadosCompletos.forros || []).filter(f => f.tipo_tabela === 'Tabela 2');
      const acessoriosTab1 = (dadosCompletos.acessorios || []).filter(a => a.tipo_tabela === 'Tabela 1');
      const acessoriosTab2 = (dadosCompletos.acessorios || []).filter(a => a.tipo_tabela === 'Tabela 2');
      temItensTab1 = forrosTab1.length > 0 || acessoriosTab1.length > 0;
      temItensTab2 = forrosTab2.length > 0 || acessoriosTab2.length > 0;
      
      // pontuação e status serão recalculados após calcularResumoTabela
      
      // Primeiro tenta usar o valor do banco
      let valorTab1Banco = parseFloat(dadosCompletos.valor_tabela1) || 0;
      let valorTab2Banco = parseFloat(dadosCompletos.valor_tabela2) || 0;

      // O total exibido tem que ser SEMPRE a soma das linhas mostradas na tabela.
      // Pedidos gravados antes da correcao do calcResumo (NovoPedido) ficaram com
      // valor_tabela1/2 sem os acabamentos cujo vinculo com o forro pai se perdeu.
      // Por isso, quando a soma dos itens diverge do valor do banco, vale a soma dos itens.
      const somarItensTabela = (forros, acessorios) =>
        forros.reduce((sum, item) => sum + calcularTotalItem(item, true), 0) +
        acessorios.reduce((sum, item) => sum + calcularTotalItem(item, false), 0);

      const valorTab1Itens = somarItensTabela(forrosTab1, acessoriosTab1);
      const valorTab2Itens = somarItensTabela(forrosTab2, acessoriosTab2);

      // Tolerancia de 1 centavo para nao brigar com arredondamento
      if (valorTab1Itens > 0 && Math.abs(valorTab1Itens - valorTab1Banco) > 0.01) {
        valorTab1Banco = valorTab1Itens;
      }
      if (valorTab2Itens > 0 && Math.abs(valorTab2Itens - valorTab2Banco) > 0.01) {
        valorTab2Banco = valorTab2Itens;
      }

      valorTotalTab1 = valorTab1Banco;
      valorTotalTab2 = valorTab2Banco;
      
      // RECALCULAR custo total dos itens para garantir precisão
      // Para forros: custo = m² × custo unitário
      // Para acessórios: custo = quantidade × custo unitário
      const custoTotalTab1Calc = forrosTab1.reduce((sum, item) => {
        const m2 = calcularM2Item(item);
        const custoUnit = parseFloat(item.custo_unitario) || 0;
        return sum + (m2 * custoUnit);
      }, 0) + acessoriosTab1.reduce((sum, item) => {
        const qtd = parseFloat(item.quantidade) || 0;
        const custoUnit = parseFloat(item.custo_unitario) || 0;
        return sum + (qtd * custoUnit);
      }, 0);
      
      const custoTotalTab2Calc = forrosTab2.reduce((sum, item) => {
        const m2 = calcularM2Item(item);
        const custoUnit = parseFloat(item.custo_unitario) || 0;
        return sum + (m2 * custoUnit);
      }, 0) + acessoriosTab2.reduce((sum, item) => {
        const qtd = parseFloat(item.quantidade) || 0;
        const custoUnit = parseFloat(item.custo_unitario) || 0;
        return sum + (qtd * custoUnit);
      }, 0);
      
      custoTotalTab1 = custoTotalTab1Calc > 0 ? custoTotalTab1Calc : (parseFloat(dadosCompletos.custo_tabela1) || 0);
      custoTotalTab2 = custoTotalTab2Calc > 0 ? custoTotalTab2Calc : (parseFloat(dadosCompletos.custo_tabela2) || 0);
      lucroTab1 = valorTotalTab1 - custoTotalTab1;
      lucroTab2 = valorTotalTab2 - custoTotalTab2;
      
      resumoTab1 = calcularResumoTabela(forrosTab1, acessoriosTab1);
      resumoTab2 = calcularResumoTabela(forrosTab2, acessoriosTab2);
      
      // Se o usuário tem acesso a custo_unitario (admin/diretoria), recalcular pontuação
      // Se não tem (vendas), usar pontuação armazenada no banco (calculada corretamente na criação)
      if (dadosCompletos.podeVerCustos !== false) {
        // Admin/Diretoria: recalcular pela pontuação calculada dinamicamente
        pontuacaoTab1Calc = resumoTab1.pontuacao;
        pontuacaoTab2Calc = resumoTab2.pontuacao;
      } else {
        // Vendas: usar pontuação do banco (sem custo_unitario, recálculo seria incorreto)
        pontuacaoTab1Calc = parseFloat(dadosCompletos.pontuacao_tabela1) || 0;
        pontuacaoTab2Calc = parseFloat(dadosCompletos.pontuacao_tabela2) || 0;
      }
      resumoTab1 = resumoTab1.resumo;
      resumoTab2 = resumoTab2.resumo;
      
      // Calcular status com a pontuação
      statusTab1 = temItensTab1 ? getStatusByScore(pontuacaoTab1Calc) : null;
      statusTab2 = temItensTab2 ? getStatusByScore(pontuacaoTab2Calc) : null;
    }
    
    // Variáveis derivadas
    const pontuacaoTab1 = pontuacaoTab1Calc;
    const pontuacaoTab2 = pontuacaoTab2Calc;
    const margemTab1 = valorTotalTab1 > 0 ? (lucroTab1 / valorTotalTab1) * 100 : 0;
    const margemTab2 = valorTotalTab2 > 0 ? (lucroTab2 / valorTotalTab2) * 100 : 0;
    
    const configTab1 = statusTab1 ? getStatusConfigLocal(statusTab1) : null;
    const configTab2 = statusTab2 ? getStatusConfigLocal(statusTab2) : null;
    const aptidaoTab1 = statusTab1 ? getStatusAptidao(statusTab1) : null;
    const aptidaoTab2 = statusTab2 ? getStatusAptidao(statusTab2) : null;
    const formasPagamento = getFormasPagamento(dadosCompletos);

    // Calcular status geral do pedido para decidir se mostra seção de aprovação
    const totalVendaGeral = valorTotalTab1 + valorTotalTab2;
    const valorProdEspeciaisDetalhe = parseFloat(dadosCompletos.valor_produtos_especiais) || 0;
    const pontTab1Cap = valorTotalTab1 > 0 ? Math.min(pontuacaoTab1, 50) : 0;
    const pontTab2Cap = valorTotalTab2 > 0 ? Math.min(pontuacaoTab2, 50) : 0;
    const pontuacaoGeral = pontTab1Cap + pontTab2Cap;
    const statusGeralPedido = (() => {
      if (totalVendaGeral === 0 && valorProdEspeciaisDetalhe > 0) {
        // Pedido só com produtos especiais: usar status do banco
        if (dadosCompletos.status_geral) return dadosCompletos.status_geral;
        if (dadosCompletos.pontuacao_geral != null) return getStatusByScore(parseFloat(dadosCompletos.pontuacao_geral) || 0);
      }
      return totalVendaGeral === 0 ? 'INVIÁVEL' : getStatusByScore(pontuacaoGeral);
    })();
    
    // Só mostra seção de aprovação se status geral for EM ANÁLISE ou INVIÁVEL
    const mostrarSecaoAprovacao = ['EM ANÁLISE', 'INVIÁVEL'].includes(statusGeralPedido);

    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        {/* Modal de Confirmação de Edição */}
        {mostrarConfirmacaoEdicao && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden transform animate-slideIn">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Edit2 size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Confirmar Edição</h3>
                    <p className="text-sm text-white/80">Ação necessária</p>
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertTriangle size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium mb-2">
                      Você tem certeza que deseja editar este pedido?
                    </p>
                    <p className="text-sm text-gray-500">
                      Ao entrar no modo de edição, você poderá alterar os dados do documento. 
                      <span className="font-medium text-blue-600"> Lembre-se: somente alterações serão salvas.</span>
                    </p>
                  </div>
                </div>
                
                {/* Buttons */}
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setMostrarConfirmacaoEdicao(false)}
                    className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                  >
                    Não, Cancelar
                  </button>
                  <button
                    onClick={confirmarEIniciarEdicao}
                    className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-medium hover:from-blue-600 hover:to-cyan-600 transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
                  >
                    <Edit2 size={18} />
                    Sim, Editar Pedido
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="px-8 py-6 w-full">
          
          {/* Botão Voltar e Ações de Link */}
          <div className="max-w-6xl mx-auto mb-6 flex items-center justify-between">
            <button 
              onClick={() => { 
                setPedidoSelecionado(null); 
                setDadosCompletos(null); 
                setModoEdicao(false); 
                // Atualizar a URL para remover o número do pedido
                navigate('/consulta-pedidos', { replace: true });
              }} 
              className="bg-white text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-50 hover:scale-105 hover:shadow-lg flex items-center gap-2 font-bold shadow-md border border-gray-200 transition-all duration-300 group"
            >
              <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform duration-300" />
              Voltar para Lista
            </button>

            {/* Botões de Copiar e Compartilhar Link */}
            <div className="flex items-center gap-3">
              {/* Botão Copiar Link */}
              <button
                onClick={() => {
                  const url = `${window.location.origin}/consulta-pedidos/${dadosCompletos?.numero_proposta}`;
                  navigator.clipboard.writeText(url).then(() => {
                    setLinkCopiado(true);
                    setTimeout(() => setLinkCopiado(false), 2000);
                  });
                }}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl font-semibold shadow-md border transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                  linkCopiado 
                    ? 'bg-green-500 text-white border-green-500' 
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
                title="Copiar link do pedido"
              >
                {linkCopiado ? (
                  <>
                    <CheckCheck size={18} />
                    Copiado!
                  </>
                ) : (
                  <>
                    <Link size={18} />
                    Copiar Link
                  </>
                )}
              </button>

              {/* Botão Compartilhar */}
              <button
                onClick={() => {
                  const url = `${window.location.origin}/consulta-pedidos/${dadosCompletos?.numero_proposta}`;
                  const texto = `Proposta ${dadosCompletos?.numero_proposta} - ${dadosCompletos?.nome_cliente || 'Cliente'}`;

                  // Verificar se o navegador suporta Web Share API
                  if (navigator.share) {
                    navigator.share({
                      title: `Proposta ${dadosCompletos?.numero_proposta}`,
                      text: texto,
                      url: url
                    }).catch(console.error);
                  } else {
                    // Fallback: copiar link e mostrar mensagem
                    navigator.clipboard.writeText(url).then(() => {
                      showModal({
                        type: 'success',
                        title: 'Link copiado!',
                        message: 'O link foi copiado para a área de transferência. Cole onde desejar para compartilhar.',
                        confirmText: 'OK',
                        showCancel: false
                      });
                    });
                  }
                }}
                className="flex items-center gap-2 px-4 py-3 rounded-xl font-semibold shadow-md border border-blue-200 bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-600 hover:to-cyan-600 transition-all duration-300 hover:scale-105 hover:shadow-lg"
                title="Compartilhar pedido"
              >
                <Share2 size={18} />
                Compartilhar
              </button>

              {/* Botão Cancelar Pedido Faturado - apenas para Admin/Diretoria/Administrativo em PEDIDO_FATURADO */}
              {dadosCompletos?.fase_atual === 'PEDIDO_FATURADO' && podeCancelarFaturado() && (
                <button
                  onClick={handleCancelarPedidoFaturado}
                  disabled={executandoAcao}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl font-semibold shadow-md border border-red-200 bg-gradient-to-r from-red-600 to-rose-600 text-white hover:from-red-700 hover:to-rose-700 transition-all duration-300 hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Cancelar este pedido após o faturamento"
                >
                  <Ban size={18} />
                  Cancelar
                </button>
              )}
            </div>
          </div>

          {/* ========================================
              BANNER MODO DE EDIÇÃO ATIVO - Aparece no lugar das Ações Rápidas
          ======================================== */}
          {modoEdicao && (
            <div className="max-w-6xl mx-auto mb-6">
              <div className="bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 rounded-2xl p-6 shadow-xl border-2 border-orange-400">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center animate-pulse">
                      <Edit2 size={28} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">Modo de Edição Ativo</h3>
                      <p className="text-white/90 text-sm">Os campos destacados em laranja abaixo estão disponíveis para edição. Faça as alterações necessárias e clique em "Salvar Edição" no final da página.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={cancelarEdicao}
                      className="flex items-center gap-2 px-5 py-2.5 bg-white/20 text-white rounded-xl text-sm font-bold hover:bg-white/30 transition-all"
                    >
                      <X size={16} />
                      Cancelar Edição
                    </button>
                    <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-xl">
                      <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                      <span className="text-white font-bold text-sm">Editando...</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========================================
              AÇÕES RÁPIDAS - Estilo Card
              Oculta quando pedido está cancelado, finalizado, arquivado ou em modo de edição
          ======================================== */}
          {!modoEdicao && dadosCompletos.fase_atual !== 'PEDIDO_CANCELADO' && dadosCompletos.fase_atual !== 'PEDIDO_CANCELADO_POS_FATURAMENTO' && dadosCompletos.fase_atual !== 'PEDIDO_FATURADO' && dadosCompletos.fase_atual !== 'ARQUIVADO' && dadosCompletos.fase_atual !== 'CANCELADO' && (
          <div className="max-w-6xl mx-auto mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Settings size={20} className="text-blue-600" />
              Ações Rápidas
            </h2>
            
            {/* BOTÕES QUANDO É PEDIDO CONFIRMADO */}
            {dadosCompletos.tipo_documento === 'PEDIDO' && dadosCompletos.fase_atual === 'PEDIDO_CONFIRMADO' ? (
              <div className="space-y-4">
                {/* Botões de Ação - Visíveis para TODOS os usuários */}
                <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                  {/* Finalizar Pedido */}
                  <button
                    onClick={handleFinalizarPedido}
                    disabled={executandoAcao || carregandoPedidosCliente}
                    className="relative overflow-hidden rounded-2xl p-5 text-left w-full bg-gradient-to-br from-emerald-600 to-green-500 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 ease-out group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-xl bg-white/20">
                        {carregandoPedidosCliente ? (
                          <div className="w-7 h-7 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <PackageCheck size={28} className="text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base font-bold text-white">Finalizar Pedido</h3>
                        <p className="text-sm text-white/80">
                          {carregandoPedidosCliente ? 'Carregando pedidos do cliente...' : 'Marcar como faturado'}
                        </p>
                      </div>
                      <ArrowRight size={20} className="text-white/80 transform group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>

                  {/* Cancelar Pedido */}
                  <button
                    onClick={handleCancelarPedido}
                    disabled={executandoAcao}
                    className="relative overflow-hidden rounded-2xl p-5 text-left w-full bg-gradient-to-br from-red-600 to-rose-500 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 ease-out group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-xl bg-white/20">
                        <Ban size={28} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base font-bold text-white">Cancelar Pedido</h3>
                        <p className="text-sm text-white/80">Cancelar este pedido</p>
                      </div>
                      <ArrowRight size={20} className="text-white/80 transform group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                </div>

                {/* Seção Informativa - Visível para TODOS */}
                <div className="bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-2xl p-6 shadow-lg">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-500 rounded-xl flex items-center justify-center shadow-lg">
                      <PackageCheck size={24} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-emerald-700">Pedido Confirmado</h2>
                      <p className="text-sm text-emerald-500">Este pedido está aguardando processamento pela equipe administrativa</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-xl p-4 border border-emerald-100">
                      <div className="flex items-center gap-2 mb-2">
                        <FileCheck size={16} className="text-emerald-500" />
                        <span className="text-xs font-semibold text-gray-500 uppercase">Etapa Atual</span>
                      </div>
                      <p className="text-base font-bold text-gray-800">Pedido Confirmado</p>
                    </div>
                    
                    <div className="bg-white rounded-xl p-4 border border-emerald-100">
                      <div className="flex items-center gap-2 mb-2">
                        <User size={16} className="text-emerald-500" />
                        <span className="text-xs font-semibold text-gray-500 uppercase">Confirmado por</span>
                      </div>
                      <p className="text-base font-bold text-gray-800">{dadosCompletos.pedido_confirmado_por || 'Não informado'}</p>
                    </div>
                    
                    <div className="bg-white rounded-xl p-4 border border-emerald-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar size={16} className="text-emerald-500" />
                        <span className="text-xs font-semibold text-gray-500 uppercase">Data e Hora</span>
                      </div>
                      <p className="text-base font-bold text-gray-800">
                        {dadosCompletos.pedido_confirmado_data 
                          ? new Date(dadosCompletos.pedido_confirmado_data).toLocaleString('pt-BR', { 
                              day: '2-digit', 
                              month: '2-digit', 
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              timeZone: 'America/Sao_Paulo' 
                            })
                          : 'Não informado'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : dadosCompletos.fase_atual === 'PEDIDO_FATURADO' || dadosCompletos.fase_atual === 'PEDIDO_CANCELADO' || dadosCompletos.fase_atual === 'ARQUIVADO' || dadosCompletos.fase_atual === 'CANCELADO' ? (
              /* BOTÕES QUANDO ESTÁ FINALIZADO/CANCELADO/ARQUIVADO - Só editar e imprimir */
              <div className="space-y-4">
                <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                  {/* Editar Pedido - Oculto para vendas somente de Produtos Especiais */}
                  {!isVendaSomenteEspeciais() && (
                  <button
                    onClick={solicitarConfirmacaoEdicao}
                    disabled={executandoAcao}
                    className="relative overflow-hidden rounded-2xl p-5 text-left w-full bg-gradient-to-br from-blue-600 to-cyan-500 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 ease-out group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-xl bg-white/20">
                        <Edit2 size={28} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base font-bold text-white">Editar Pedido</h3>
                        <p className="text-sm text-white/80">Modificar dados do documento</p>
                      </div>
                      <ArrowRight size={20} className="text-white/80 transform group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                  )}

                  {/* Status do documento */}
                  <div className="rounded-2xl p-5 text-left w-full bg-gradient-to-br from-slate-100 to-slate-200 text-gray-800 shadow-lg border border-slate-300">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-xl bg-slate-300/50">
                        <FileCheck size={28} className="text-slate-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base font-bold text-gray-800">Documento Encerrado</h3>
                        <p className="text-sm text-gray-500">
                          {dadosCompletos.fase_atual === 'PEDIDO_FATURADO' && 'Pedido faturado com sucesso'}
                          {dadosCompletos.fase_atual === 'PEDIDO_CANCELADO' && 'Pedido foi cancelado'}
                          {dadosCompletos.fase_atual === 'ARQUIVADO' && 'Proposta arquivada'}
                          {dadosCompletos.fase_atual === 'CANCELADO' && 'Proposta cancelada'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Seção Informativa - Pedido Faturado */}
                {dadosCompletos.fase_atual === 'PEDIDO_FATURADO' && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-6 shadow-lg">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
                        <PackageCheck size={24} className="text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-green-700">Pedido Faturado</h2>
                        <p className="text-sm text-green-500">Este pedido foi faturado e encerrado com sucesso</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white rounded-xl p-4 border border-green-100">
                        <div className="flex items-center gap-2 mb-2">
                          <FileCheck size={16} className="text-green-500" />
                          <span className="text-xs font-semibold text-gray-500 uppercase">Etapa Atual</span>
                        </div>
                        <p className="text-base font-bold text-gray-800">Pedido Faturado</p>
                      </div>
                      
                      <div className="bg-white rounded-xl p-4 border border-green-100">
                        <div className="flex items-center gap-2 mb-2">
                          <User size={16} className="text-green-500" />
                          <span className="text-xs font-semibold text-gray-500 uppercase">Faturado por</span>
                        </div>
                        <p className="text-base font-bold text-gray-800">{dadosCompletos.faturado_por || 'Não informado'}</p>
                      </div>
                      
                      <div className="bg-white rounded-xl p-4 border border-green-100">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar size={16} className="text-green-500" />
                          <span className="text-xs font-semibold text-gray-500 uppercase">Data e Hora</span>
                        </div>
                        <p className="text-base font-bold text-gray-800">
                          {dadosCompletos.faturado_data
                            ? new Date(dadosCompletos.faturado_data).toLocaleString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                timeZone: 'America/Sao_Paulo'
                              })
                            : 'Não informado'}
                        </p>
                      </div>
                    </div>

                    {dadosCompletos.faturamento_observacao && (
                      <div className="mt-4 bg-white rounded-xl p-4 border border-green-100">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertCircle size={16} className="text-green-500" />
                          <span className="text-xs font-semibold text-gray-500 uppercase">Observação do Faturamento</span>
                        </div>
                        <p className="text-base text-gray-800 whitespace-pre-wrap">{dadosCompletos.faturamento_observacao}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Seção Informativa - Pedido Cancelado */}
                {dadosCompletos.fase_atual === 'PEDIDO_CANCELADO' && (
                  <div className="bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 rounded-2xl p-6 shadow-lg">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-rose-500 rounded-xl flex items-center justify-center shadow-lg">
                        <XCircle size={24} className="text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-red-700">Pedido Cancelado</h2>
                        <p className="text-sm text-red-500">Este pedido foi cancelado</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white rounded-xl p-4 border border-red-100">
                        <div className="flex items-center gap-2 mb-2">
                          <FileCheck size={16} className="text-red-500" />
                          <span className="text-xs font-semibold text-gray-500 uppercase">Etapa Atual</span>
                        </div>
                        <p className="text-base font-bold text-gray-800">Pedido Cancelado</p>
                      </div>
                      
                      <div className="bg-white rounded-xl p-4 border border-red-100">
                        <div className="flex items-center gap-2 mb-2">
                          <User size={16} className="text-red-500" />
                          <span className="text-xs font-semibold text-gray-500 uppercase">Cancelado por</span>
                        </div>
                        <p className="text-base font-bold text-gray-800">{dadosCompletos.cancelado_por || 'Não informado'}</p>
                      </div>
                      
                      <div className="bg-white rounded-xl p-4 border border-red-100">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar size={16} className="text-red-500" />
                          <span className="text-xs font-semibold text-gray-500 uppercase">Data e Hora</span>
                        </div>
                        <p className="text-base font-bold text-gray-800">
                          {dadosCompletos.data_cancelamento 
                            ? new Date(dadosCompletos.data_cancelamento).toLocaleString('pt-BR', { 
                                day: '2-digit', 
                                month: '2-digit', 
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                timeZone: 'America/Sao_Paulo' 
                              })
                            : 'Não informado'}
                        </p>
                      </div>
                    </div>
                    
                    {dadosCompletos.observacao_cancelamento && (
                      <div className="mt-4 bg-white rounded-xl p-4 border border-red-100">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertCircle size={16} className="text-red-500" />
                          <span className="text-xs font-semibold text-gray-500 uppercase">Motivo do Cancelamento</span>
                        </div>
                        <p className="text-base text-gray-800">{dadosCompletos.observacao_cancelamento}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              /* BOTÕES PARA ORÇAMENTOS (ainda não é pedido confirmado) */
              <div className={`grid gap-4 ${podeGerenciarOrcamento() ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 md:grid-cols-2'}`}>
                
                {/* Editar Pedido - Oculto para vendas somente de Produtos Especiais */}
                {!isVendaSomenteEspeciais() && (
                <button
                  onClick={solicitarConfirmacaoEdicao}
                  disabled={executandoAcao}
                  className="relative overflow-hidden rounded-2xl p-5 text-left w-full bg-gradient-to-br from-blue-600 to-cyan-500 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 ease-out group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-white/20">
                      <Edit2 size={28} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-bold text-white">Editar Pedido</h3>
                      <p className="text-sm text-white/80">Modificar dados do documento</p>
                    </div>
                    <ArrowRight size={20} className="text-white/80 transform group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
                )}

                {/* Botões para quem pode gerenciar orçamento (Admin/Diretoria/Administrativo OU fluxo automático + valor < 10k) */}
                {podeGerenciarOrcamento() && (
                  <>
                    {/* Confirmar Pedido */}
                    <button
                      onClick={handleConfirmarPedido}
                      disabled={executandoAcao}
                      className="relative overflow-hidden rounded-2xl p-5 text-left w-full bg-white/70 backdrop-blur-xl border border-white/20 text-gray-800 shadow-lg hover:shadow-xl transform hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 ease-out group disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-100/50 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl" style={{ backgroundColor: '#10b98115' }}>
                          <CheckCircle size={28} className="text-emerald-500" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-base font-bold text-gray-800">Confirmar Pedido</h3>
                          <p className="text-sm text-gray-500">Transformar em pedido oficial</p>
                        </div>
                        <ArrowRight size={20} className="text-gray-400 transform group-hover:translate-x-1 transition-transform" />
                      </div>
                    </button>

                    {/* Arquivar Proposta */}
                    <button
                      onClick={handleArquivarProposta}
                      disabled={executandoAcao}
                      className="relative overflow-hidden rounded-2xl p-5 text-left w-full bg-white/70 backdrop-blur-xl border border-white/20 text-gray-800 shadow-lg hover:shadow-xl transform hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 ease-out group disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-slate-100/50 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl" style={{ backgroundColor: '#64748b15' }}>
                          <Archive size={28} className="text-slate-500" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-base font-bold text-gray-800">Arquivar Proposta</h3>
                          <p className="text-sm text-gray-500">Guardar para consulta futura</p>
                        </div>
                        <ArrowRight size={20} className="text-gray-400 transform group-hover:translate-x-1 transition-transform" />
                      </div>
                    </button>

                    {/* Cancelar Proposta */}
                    <button
                      onClick={handleCancelarProposta}
                      disabled={executandoAcao}
                      className="relative overflow-hidden rounded-2xl p-5 text-left w-full bg-white/70 backdrop-blur-xl border border-white/20 text-gray-800 shadow-lg hover:shadow-xl transform hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 ease-out group disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-red-100/50 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl" style={{ backgroundColor: '#ef444415' }}>
                          <Ban size={28} className="text-red-500" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-base font-bold text-gray-800">Cancelar Proposta</h3>
                          <p className="text-sm text-gray-500">Encerrar este documento</p>
                        </div>
                        <ArrowRight size={20} className="text-gray-400 transform group-hover:translate-x-1 transition-transform" />
                      </div>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          )}

          {/* ========================================
              SEÇÃO INFORMATIVA - PEDIDO FATURADO
              Aparece apenas quando o pedido está faturado
              Mostra histórico: Faturado + Confirmado
          ======================================== */}
          {dadosCompletos.fase_atual === 'PEDIDO_FATURADO' && (
            <div className="max-w-6xl mx-auto mb-6 space-y-4">
              {/* Card Pedido Faturado - Azul claro */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                    <PackageCheck size={24} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-blue-700">Pedido Faturado</h2>
                    <p className="text-sm text-blue-500">Este pedido foi faturado e encerrado com sucesso</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl p-4 border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <FileCheck size={16} className="text-blue-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Etapa Atual</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">Pedido Faturado</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <User size={16} className="text-blue-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Faturado por</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">{dadosCompletos.faturado_por || 'Não informado'}</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar size={16} className="text-blue-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Data e Hora</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">
                      {dadosCompletos.faturado_data
                        ? new Date(dadosCompletos.faturado_data).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'America/Sao_Paulo'
                          })
                        : 'Não informado'}
                    </p>
                  </div>
                </div>

                {dadosCompletos.faturamento_observacao && (
                  <div className="mt-4 bg-white rounded-xl p-4 border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle size={16} className="text-blue-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Observação do Faturamento</span>
                    </div>
                    <p className="text-base text-gray-800 whitespace-pre-wrap">{dadosCompletos.faturamento_observacao}</p>
                  </div>
                )}
              </div>

              {/* Card Pedido Confirmado - Histórico (verde) */}
              <div className="bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-500 rounded-xl flex items-center justify-center shadow-lg">
                    <PackageCheck size={24} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-emerald-700">Pedido Confirmado</h2>
                    <p className="text-sm text-emerald-500">Este pedido foi confirmado antes do faturamento</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl p-4 border border-emerald-100">
                    <div className="flex items-center gap-2 mb-2">
                      <FileCheck size={16} className="text-emerald-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Etapa</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">Pedido Confirmado</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 border border-emerald-100">
                    <div className="flex items-center gap-2 mb-2">
                      <User size={16} className="text-emerald-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Confirmado por</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">{dadosCompletos.pedido_confirmado_por || 'Não informado'}</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 border border-emerald-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar size={16} className="text-emerald-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Data e Hora</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">
                      {dadosCompletos.pedido_confirmado_data 
                        ? new Date(dadosCompletos.pedido_confirmado_data).toLocaleString('pt-BR', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'America/Sao_Paulo' 
                          })
                        : 'Não informado'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========================================
              SEÇÃO INFORMATIVA - PEDIDO CANCELADO
              Aparece apenas quando o pedido está cancelado
              Mostra histórico: Cancelado + Confirmado
          ======================================== */}
          {dadosCompletos.fase_atual === 'PEDIDO_CANCELADO' && (
            <div className="max-w-6xl mx-auto mb-6 space-y-4">
              {/* Card Pedido Cancelado - Vermelho */}
              <div className="bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-rose-500 rounded-xl flex items-center justify-center shadow-lg">
                    <XCircle size={24} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-red-700">Pedido Cancelado</h2>
                    <p className="text-sm text-red-500">
                      {dadosCompletos.pedido_confirmado_por 
                        ? 'Este pedido foi cancelado na etapa de faturamento'
                        : 'Este pedido foi cancelado'}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl p-4 border border-red-100">
                    <div className="flex items-center gap-2 mb-2">
                      <FileCheck size={16} className="text-red-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Etapa Atual</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">Pedido Cancelado</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 border border-red-100">
                    <div className="flex items-center gap-2 mb-2">
                      <User size={16} className="text-red-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Cancelado por</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">{dadosCompletos.cancelado_por || 'Não informado'}</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 border border-red-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar size={16} className="text-red-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Data e Hora</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">
                      {dadosCompletos.data_cancelamento 
                        ? new Date(dadosCompletos.data_cancelamento).toLocaleString('pt-BR', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'America/Sao_Paulo' 
                          })
                        : 'Não informado'}
                    </p>
                  </div>
                </div>
                
                {dadosCompletos.observacao_cancelamento && (
                  <div className="mt-4 bg-white rounded-xl p-4 border border-red-100">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle size={16} className="text-red-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Motivo do Cancelamento</span>
                    </div>
                    <p className="text-base text-gray-800">{dadosCompletos.observacao_cancelamento}</p>
                  </div>
                )}
              </div>

              {/* Card Pedido Confirmado - Histórico (verde) - só mostra se tiver dados de confirmação */}
              {dadosCompletos.pedido_confirmado_por && (
                <div className="bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-2xl p-6 shadow-lg">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-500 rounded-xl flex items-center justify-center shadow-lg">
                      <PackageCheck size={24} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-emerald-700">Pedido Confirmado</h2>
                      <p className="text-sm text-emerald-500">Este pedido foi confirmado na 1ª etapa</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-xl p-4 border border-emerald-100">
                      <div className="flex items-center gap-2 mb-2">
                        <FileCheck size={16} className="text-emerald-500" />
                        <span className="text-xs font-semibold text-gray-500 uppercase">Etapa</span>
                      </div>
                      <p className="text-base font-bold text-gray-800">Pedido Confirmado</p>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-emerald-100">
                      <div className="flex items-center gap-2 mb-2">
                        <User size={16} className="text-emerald-500" />
                        <span className="text-xs font-semibold text-gray-500 uppercase">Confirmado por</span>
                      </div>
                      <p className="text-base font-bold text-gray-800">{dadosCompletos.pedido_confirmado_por}</p>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-emerald-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar size={16} className="text-emerald-500" />
                        <span className="text-xs font-semibold text-gray-500 uppercase">Data e Hora</span>
                      </div>
                      <p className="text-base font-bold text-gray-800">
                        {dadosCompletos.pedido_confirmado_data
                          ? new Date(dadosCompletos.pedido_confirmado_data).toLocaleString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              timeZone: 'America/Sao_Paulo'
                            })
                          : 'Não informado'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ========================================
              SEÇÃO INFORMATIVA - PEDIDO CANCELADO APÓS FATURAMENTO
              Mostra histórico COMPLETO: Cancelamento + Faturamento + Confirmação
          ======================================== */}
          {dadosCompletos.fase_atual === 'PEDIDO_CANCELADO_POS_FATURAMENTO' && (
            <div className="max-w-6xl mx-auto mb-6 space-y-4">
              {/* Banner topo - Pedido Cancelado Após Faturamento */}
              <div className="bg-gradient-to-r from-rose-600 to-red-700 border-2 border-rose-300 rounded-2xl p-6 shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center shadow-lg">
                    <Ban size={32} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-white tracking-wide">PEDIDO CANCELADO APÓS FATURAMENTO</h2>
                    <p className="text-sm text-white/90">
                      Este pedido foi faturado e posteriormente cancelado. Veja abaixo o histórico completo do fluxo.
                    </p>
                  </div>
                </div>
              </div>

              {/* Card 1: Cancelamento (mais recente, vermelho) */}
              <div className="bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-rose-500 rounded-xl flex items-center justify-center shadow-lg">
                    <XCircle size={24} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-red-700">Cancelamento</h2>
                    <p className="text-sm text-red-500">Cancelado após o faturamento</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl p-4 border border-red-100">
                    <div className="flex items-center gap-2 mb-2">
                      <FileCheck size={16} className="text-red-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Etapa Atual</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">Cancelado Após Faturamento</p>
                  </div>

                  <div className="bg-white rounded-xl p-4 border border-red-100">
                    <div className="flex items-center gap-2 mb-2">
                      <User size={16} className="text-red-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Cancelado por</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">{dadosCompletos.cancelado_por || 'Não informado'}</p>
                  </div>

                  <div className="bg-white rounded-xl p-4 border border-red-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar size={16} className="text-red-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Data e Hora</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">
                      {dadosCompletos.data_cancelamento
                        ? new Date(dadosCompletos.data_cancelamento).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'America/Sao_Paulo'
                          })
                        : 'Não informado'}
                    </p>
                  </div>
                </div>

                {dadosCompletos.observacao_cancelamento && (
                  <div className="mt-4 bg-white rounded-xl p-4 border border-red-100">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle size={16} className="text-red-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Motivo do Cancelamento</span>
                    </div>
                    <p className="text-base text-gray-800">{dadosCompletos.observacao_cancelamento}</p>
                  </div>
                )}
              </div>

              {/* Card 2: Faturamento (azul) - preservado */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                    <PackageCheck size={24} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-blue-700">Pedido Faturado</h2>
                    <p className="text-sm text-blue-500">Este pedido foi faturado antes do cancelamento</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl p-4 border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <FileCheck size={16} className="text-blue-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Etapa</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">Pedido Faturado</p>
                  </div>

                  <div className="bg-white rounded-xl p-4 border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <User size={16} className="text-blue-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Faturado por</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">{dadosCompletos.faturado_por || 'Não informado'}</p>
                  </div>

                  <div className="bg-white rounded-xl p-4 border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar size={16} className="text-blue-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Data e Hora</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">
                      {dadosCompletos.faturado_data
                        ? new Date(dadosCompletos.faturado_data).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'America/Sao_Paulo'
                          })
                        : 'Não informado'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Card 3: Confirmação (verde) - se houver */}
              {dadosCompletos.pedido_confirmado_por && (
                <div className="bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-2xl p-6 shadow-lg">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-500 rounded-xl flex items-center justify-center shadow-lg">
                      <CheckCircle size={24} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-emerald-700">Pedido Confirmado</h2>
                      <p className="text-sm text-emerald-500">Confirmação inicial antes do faturamento</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-xl p-4 border border-emerald-100">
                      <div className="flex items-center gap-2 mb-2">
                        <FileCheck size={16} className="text-emerald-500" />
                        <span className="text-xs font-semibold text-gray-500 uppercase">Etapa</span>
                      </div>
                      <p className="text-base font-bold text-gray-800">Pedido Confirmado</p>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-emerald-100">
                      <div className="flex items-center gap-2 mb-2">
                        <User size={16} className="text-emerald-500" />
                        <span className="text-xs font-semibold text-gray-500 uppercase">Confirmado por</span>
                      </div>
                      <p className="text-base font-bold text-gray-800">{dadosCompletos.pedido_confirmado_por}</p>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-emerald-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar size={16} className="text-emerald-500" />
                        <span className="text-xs font-semibold text-gray-500 uppercase">Data e Hora</span>
                      </div>
                      <p className="text-base font-bold text-gray-800">
                        {dadosCompletos.pedido_confirmado_data
                          ? new Date(dadosCompletos.pedido_confirmado_data).toLocaleString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              timeZone: 'America/Sao_Paulo'
                            })
                          : 'Não informado'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* MODAL SELECIONAR PEDIDOS PARA FATURAMENTO EM LOTE */}
          {modalSelecionarLote && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 animate-fadeIn max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between mb-4 flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl bg-emerald-100">
                      <PackageCheck size={28} className="text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">Selecionar Pedidos para Faturar</h3>
                      <p className="text-sm text-gray-500">
                        {dadosCompletos?.clientes?.nome || 'Cliente'} tem {pedidosConfirmadosCliente.length} pedido(s) confirmado(s)
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setModalSelecionarLote(false);
                      setPedidosLoteSelecionados([]);
                      setPedidosConfirmadosCliente([]);
                    }}
                    disabled={executandoAcao}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                    aria-label="Fechar"
                  >
                    <X size={18} className="text-gray-500" />
                  </button>
                </div>

                <div className="mb-3 p-3 rounded-xl bg-blue-50 border border-blue-200 flex items-start gap-2 flex-shrink-0">
                  <AlertCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-700">
                    Marque os pedidos confirmados deste cliente que deseja faturar em conjunto.
                    Todos os selecionados serão faturados com a <strong>mesma data, hora e observação</strong>.
                  </p>
                </div>

                <div className="flex-1 overflow-y-auto space-y-2 pr-1 -mr-1">
                  {pedidosConfirmadosCliente.map((p) => {
                    const isAtual = p.id === dadosCompletos?.id;
                    const selecionado = pedidosLoteSelecionados.includes(p.id);
                    const valorFmt = (parseFloat(p.valor_total) || 0).toLocaleString('pt-BR', {
                      style: 'currency', currency: 'BRL'
                    });
                    const dataFmt = p.created_at
                      ? new Date(p.created_at).toLocaleDateString('pt-BR', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          timeZone: 'America/Sao_Paulo'
                        })
                      : '—';
                    // Mesma lógica do card da lista — recalcula pelas pontuações individuais
                    const statusPontuacao = calcularStatusGeralPedido(p);
                    return (
                      <div
                        key={p.id}
                        className={`rounded-xl border-2 p-3 transition-all ${
                          selecionado
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        } ${isAtual ? 'cursor-default' : 'cursor-pointer'}`}
                        onClick={() => !isAtual && togglePedidoLote(p.id)}
                      >
                        <div className="flex items-start gap-3">
                          <div className="pt-1 flex-shrink-0">
                            <input
                              type="checkbox"
                              checked={selecionado}
                              disabled={isAtual}
                              onChange={() => togglePedidoLote(p.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-5 h-5 accent-emerald-600 cursor-pointer disabled:cursor-not-allowed"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-bold text-gray-800">
                                #{p.id}
                              </span>
                              {p.numero_proposta && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">
                                  {p.numero_proposta}
                                </span>
                              )}
                              {isAtual && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-600 text-white font-bold">
                                  PEDIDO ATUAL
                                </span>
                              )}
                            </div>
                            {p.vendedor && (
                              <div className="text-xs text-gray-600 mb-1 inline-flex items-center gap-1 truncate">
                                <User size={12} className="flex-shrink-0" />
                                <span className="truncate"><strong>Responsável:</strong> {p.vendedor}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap mb-2">
                              <span className="inline-flex items-center gap-1">
                                <Calendar size={12} />
                                {dataFmt}
                              </span>
                              <span className="inline-flex items-center gap-1 font-semibold text-gray-800">
                                <DollarSign size={12} />
                                {valorFmt}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              {statusPontuacao && (
                                <StatusBadge status={statusPontuacao} tamanho="xs" />
                              )}
                              <FaseBadge fase="PEDIDO_CONFIRMADO" tamanho="xs" fullLabel={true} />
                            </div>
                          </div>
                          {!isAtual && (
                            <a
                              href={`/consulta-pedidos/${p.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex-shrink-0 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              <Eye size={12} />
                              Ver mais
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex gap-3 mt-4 flex-shrink-0">
                  <button
                    onClick={() => {
                      setModalSelecionarLote(false);
                      setPedidosLoteSelecionados([]);
                      setPedidosConfirmadosCliente([]);
                    }}
                    disabled={executandoAcao}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-300 disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={avancarParaDataHora}
                    disabled={executandoAcao || pedidosLoteSelecionados.length === 0}
                    className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <ArrowRight size={18} />
                    Continuar ({pedidosLoteSelecionados.length})
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* MODAL FINALIZAR PEDIDO - data, hora e observação (se data ≠ hoje) */}
          {modalFinalizarPedido && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-fadeIn">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl bg-emerald-100">
                      <PackageCheck size={28} className="text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">
                        {pedidosLoteSelecionados.length > 1
                          ? `Finalizar ${pedidosLoteSelecionados.length} Pedidos`
                          : 'Finalizar Pedido'}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {pedidosLoteSelecionados.length > 1
                          ? 'A mesma data, hora e observação serão aplicadas a todos'
                          : 'Confirme os dados do faturamento'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setModalFinalizarPedido(false)}
                    disabled={executandoAcao}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                    aria-label="Fechar"
                  >
                    <X size={18} className="text-gray-500" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Data de Faturamento <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={dataFaturamento}
                      onChange={(e) => setDataFaturamento(e.target.value)}
                      max={obterDataHojeSP()}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Hora <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      value={horaFaturamento}
                      onChange={(e) => setHoraFaturamento(e.target.value)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                    />
                  </div>
                </div>

                {dataFaturamento && dataFaturamento !== obterDataHojeSP() && (
                  <div className="mb-4">
                    <div className="mb-2 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2">
                      <AlertCircle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-amber-700">
                        A data de faturamento é diferente de hoje. A data e hora reais deste registro
                        (<strong>{(() => { const [a,m,d] = obterDataHojeSP().split('-'); return `${d}/${m}/${a}`; })()} {obterHoraAgoraSP()}</strong>)
                        serão adicionadas automaticamente no início da observação e não podem ser alteradas.
                      </p>
                    </div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Motivo / Observação <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={observacaoFaturamento}
                      onChange={(e) => setObservacaoFaturamento(e.target.value)}
                      placeholder="Explique o motivo de o faturamento estar sendo registrado em uma data diferente de hoje..."
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300 resize-none"
                      rows={4}
                    />
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => setModalFinalizarPedido(false)}
                    disabled={executandoAcao}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-300 disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={confirmarFinalizarPedido}
                    disabled={
                      executandoAcao ||
                      !dataFaturamento ||
                      !horaFaturamento ||
                      (dataFaturamento !== obterDataHojeSP() && !observacaoFaturamento.trim())
                    }
                    className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {executandoAcao ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Faturando...
                      </>
                    ) : (
                      <>
                        <PackageCheck size={18} />
                        {pedidosLoteSelecionados.length > 1
                          ? `Sim, Finalizar ${pedidosLoteSelecionados.length}`
                          : 'Sim, Finalizar'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* MODAL CANCELAR PEDIDO/PROPOSTA COM OBSERVAÇÃO */}
          {modalCancelarPedido && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-fadeIn">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded-xl bg-red-100">
                    <Ban size={28} className="text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">
                      {tipoCancelamentoModal === 'pos_faturamento'
                        ? 'Cancelar Pedido Faturado'
                        : (dadosCompletos?.tipo_documento === 'PEDIDO' ? 'Cancelar Pedido' : 'Cancelar Proposta')}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {tipoCancelamentoModal === 'pos_faturamento'
                        ? 'Este pedido já foi faturado. Informe o motivo do cancelamento.'
                        : 'Informe o motivo do cancelamento'}
                    </p>
                  </div>
                </div>

                {tipoCancelamentoModal === 'pos_faturamento' && (
                  <div className="mb-4 bg-amber-50 border-2 border-amber-200 rounded-xl p-3 flex items-start gap-2">
                    <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-700">
                      <strong>Atenção:</strong> o pedido será marcado como <strong>"Cancelado Após Faturamento"</strong>. O histórico de faturamento será preservado para auditoria.
                    </p>
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Motivo do Cancelamento <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={observacaoCancelamento}
                    onChange={(e) => setObservacaoCancelamento(e.target.value)}
                    placeholder={
                      tipoCancelamentoModal === 'pos_faturamento'
                        ? 'Descreva o motivo do cancelamento após o faturamento (obrigatório)...'
                        : `Descreva o motivo pelo qual ${dadosCompletos?.tipo_documento === 'PEDIDO' ? 'este pedido' : 'esta proposta'} está sendo cancelado(a)...`
                    }
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-300 resize-none"
                    rows={4}
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setModalCancelarPedido(false)}
                    disabled={executandoAcao}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-300 disabled:opacity-50"
                  >
                    Voltar
                  </button>
                  <button
                    onClick={confirmarCancelamentoPedido}
                    disabled={executandoAcao || !observacaoCancelamento.trim()}
                    className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {executandoAcao ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Cancelando...
                      </>
                    ) : (
                      <>
                        <Ban size={18} />
                        Confirmar Cancelamento
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ========================================
              BOTÃO FAZER PEDIDO - Aparece quando orçamento aprovado
          ======================================== */}
          {(dadosCompletos.fase_atual === 'ORCAMENTO_APROVADO' || dadosCompletos.fase_atual === 'APROVADO_COM_AJUSTES') && (
            <div className="w-full max-w-6xl mx-auto mb-6">
              <div className={`rounded-2xl p-6 border-2 shadow-lg ${
                dadosCompletos.fase_atual === 'APROVADO_COM_AJUSTES'
                  ? 'bg-gradient-to-br from-blue-50 via-sky-50 to-white border-blue-200'
                  : 'bg-gradient-to-br from-green-50 via-emerald-50 to-white border-green-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-lg ${
                      dadosCompletos.fase_atual === 'APROVADO_COM_AJUSTES'
                        ? 'bg-gradient-to-br from-blue-500 to-sky-500'
                        : 'bg-gradient-to-br from-green-500 to-emerald-500'
                    }`}>
                      <Package size={28} className="text-white" />
                    </div>
                    <div>
                      <h3 className={`text-xl font-bold ${
                        dadosCompletos.fase_atual === 'APROVADO_COM_AJUSTES' ? 'text-blue-700' : 'text-green-700'
                      }`}>
                        {dadosCompletos.fase_atual === 'APROVADO_COM_AJUSTES' 
                          ? 'Orçamento Aprovado com Ajustes!' 
                          : 'Orçamento Aprovado!'}
                      </h3>
                      <p className={`text-sm ${
                        dadosCompletos.fase_atual === 'APROVADO_COM_AJUSTES' ? 'text-blue-600' : 'text-green-600'
                      }`}>
                        {dadosCompletos.fase_atual === 'APROVADO_COM_AJUSTES' 
                          ? 'Aprovado com ajustes pelo administrativo. Clique para transformar em pedido.'
                          : 'Clique no botão para transformar este orçamento em pedido.'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => document.getElementById('secao-fazer-pedido')?.scrollIntoView({ behavior: 'smooth' })}
                    className={`text-white px-8 py-4 rounded-xl font-bold flex items-center gap-3 hover:scale-105 hover:shadow-xl transition-all duration-300 group ${
                      dadosCompletos.fase_atual === 'APROVADO_COM_AJUSTES'
                        ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-600 hover:to-sky-600'
                        : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'
                    }`}
                  >
                    <ShoppingCart size={24} className="group-hover:rotate-12 transition-transform duration-300" />
                    <span className="text-lg">FAZER PEDIDO</span>
                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ========================================
              BOTÃO EDITAR PEDIDO - Aparece quando solicitada edição
          ======================================== */}
          {dadosCompletos.fase_atual === 'SOLICITAR_EDICAO' && (
            <div className="w-full max-w-6xl mx-auto mb-6">
              <div className={`rounded-2xl p-6 border-2 shadow-lg transition-all duration-300 ${
                modoEdicao 
                  ? 'bg-gradient-to-br from-orange-100 via-amber-100 to-orange-50 border-orange-400 ring-2 ring-orange-300' 
                  : 'bg-gradient-to-br from-orange-50 via-amber-50 to-white border-orange-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg ${modoEdicao ? 'animate-pulse' : ''}`}>
                      <Edit2 size={28} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-orange-700">
                        {modoEdicao ? 'Modo de Edição Ativo' : 'Edição Solicitada!'}
                      </h3>
                      <p className="text-sm text-orange-600">
                        {modoEdicao 
                          ? 'Edite os campos destacados em laranja abaixo e depois salve as alterações.'
                          : 'O administrativo solicitou alterações neste orçamento. Clique para editar os dados.'
                        }
                      </p>
                      {!modoEdicao && dadosCompletos.aprovacao_orcamento_observacao && (
                        <p className="text-xs text-orange-500 mt-1 italic">
                          Motivo: {dadosCompletos.aprovacao_orcamento_observacao}
                        </p>
                      )}
                    </div>
                  </div>
                  {!modoEdicao ? (
                    <button
                      onClick={iniciarEdicaoReal}
                      className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-4 rounded-xl font-bold flex items-center gap-3 hover:from-orange-600 hover:to-amber-600 hover:scale-105 hover:shadow-xl transition-all duration-300 group"
                    >
                      <Edit2 size={24} className="group-hover:rotate-12 transition-transform duration-300" />
                      <span className="text-lg">EDITAR PEDIDO</span>
                      <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
                    </button>
                  ) : (
                    <div className="flex items-center gap-3">
                      <button
                        onClick={cancelarEdicao}
                        className="bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-bold flex items-center gap-2 hover:bg-gray-300 hover:scale-105 transition-all duration-300"
                      >
                        <X size={20} />
                        <span>Cancelar</span>
                      </button>
                      <button
                        onClick={salvarEdicao}
                        disabled={processandoEdicao}
                        className={`bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-3 rounded-xl font-bold flex items-center gap-3 hover:from-orange-600 hover:to-amber-600 hover:scale-105 hover:shadow-xl transition-all duration-300 ${processandoEdicao ? 'opacity-70 cursor-not-allowed' : ''}`}
                      >
                        {processandoEdicao ? (
                          <>
                            <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            <span>Salvando...</span>
                          </>
                        ) : (
                          <>
                            <Check size={20} />
                            <span>Salvar e Reenviar</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ========================================
              SEÇÃO PROPOSTA CANCELADA - Mostra detalhes do cancelamento
          ======================================== */}
          {dadosCompletos.fase_atual === 'CANCELADO' && (
            <div className="w-full max-w-6xl mx-auto mb-6">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-rose-500 rounded-xl flex items-center justify-center shadow-lg">
                    <Ban size={24} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-red-700">Proposta Cancelada</h2>
                    <p className="text-sm text-red-500">Esta proposta foi cancelada e não pode mais ser processada</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-white rounded-xl p-4 border border-red-100">
                    <div className="flex items-center gap-2 mb-2">
                      <User size={16} className="text-red-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Cancelado por</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">{dadosCompletos.cancelado_por || 'Não informado'}</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 border border-red-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar size={16} className="text-red-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Data do Cancelamento</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">
                      {dadosCompletos.data_cancelamento 
                        ? new Date(dadosCompletos.data_cancelamento).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })
                        : 'Não informado'}
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 border border-red-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock size={16} className="text-red-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Hora do Cancelamento</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">
                      {dadosCompletos.data_cancelamento 
                        ? new Date(dadosCompletos.data_cancelamento).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' })
                        : 'Não informado'}
                    </p>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-4 border border-red-100">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText size={16} className="text-red-500" />
                    <span className="text-xs font-semibold text-gray-500 uppercase">Motivo do Cancelamento</span>
                  </div>
                  <p className="text-base text-gray-700 whitespace-pre-wrap">
                    {dadosCompletos.observacao_cancelamento || 'Nenhuma observação registrada'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ========================================
              SEÇÃO PROPOSTA ARQUIVADA - Mostra detalhes do arquivamento
          ======================================== */}
          {dadosCompletos.fase_atual === 'ARQUIVADO' && (
            <div className="w-full max-w-6xl mx-auto mb-6">
              <div className="bg-gradient-to-r from-slate-50 to-gray-100 border-2 border-slate-300 rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-slate-500 to-gray-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Archive size={24} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-700">Proposta Arquivada</h2>
                    <p className="text-sm text-slate-500">Esta proposta foi arquivada para consulta futura</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl p-4 border border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <User size={16} className="text-slate-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Arquivado por</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">{dadosCompletos.arquivado_por || 'Não informado'}</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 border border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar size={16} className="text-slate-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Data do Arquivamento</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">
                      {dadosCompletos.data_arquivamento 
                        ? new Date(dadosCompletos.data_arquivamento).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })
                        : 'Não informado'}
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 border border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock size={16} className="text-slate-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Hora do Arquivamento</span>
                    </div>
                    <p className="text-base font-bold text-gray-800">
                      {dadosCompletos.data_arquivamento 
                        ? new Date(dadosCompletos.data_arquivamento).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' })
                        : 'Não informado'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========================================
              1. DADOS DA PROPOSTA
          ======================================== */}
          <div className="w-full max-w-6xl mx-auto mb-6">
            <div className="w-full h-px bg-gray-200 mb-6"></div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 group cursor-default">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                  <FileText size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Dados da Proposta</h2>
                  <p className="text-xs text-gray-400">Informações da proposta atual</p>
                </div>
              </div>
              <button 
                onClick={() => toggleExpandir('proposta')} 
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-300"
              >
                <ChevronDown size={20} className={`transition-transform duration-300 ${expandido.proposta ? 'rotate-180' : ''}`} />
                {expandido.proposta ? 'Recolher' : 'Expandir'}
              </button>
            </div>
          </div>

          {expandido.proposta && (
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-gray-100 w-full max-w-6xl mx-auto hover:shadow-xl hover:scale-[1.01] hover:-translate-y-1 transition-all duration-300">
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-300">
                  <label className="block text-xs font-semibold mb-1.5 text-gray-500 uppercase tracking-wide flex items-center gap-1"><Calendar size={12} />Data</label>
                  <p className="text-sm font-bold text-gray-700">{formatarData(dadosCompletos.data || dadosCompletos.created_at)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-300">
                  <label className="block text-xs font-semibold mb-1.5 text-gray-500 uppercase tracking-wide flex items-center gap-1"><Clock size={12} />Hora</label>
                  <p className="text-sm font-bold text-gray-700">{formatarHora(dadosCompletos.hora)}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl p-4 shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300">
                  <label className="block text-xs font-semibold mb-1.5 text-white/90 uppercase tracking-wide">Proposta Nº</label>
                  <p className="text-sm font-bold text-white">{dadosCompletos.numero_proposta}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-300">
                  <label className="block text-xs font-semibold mb-1.5 text-gray-500 uppercase tracking-wide flex items-center gap-1"><User size={12} />Responsável</label>
                  {podeAlterarResponsavel() ? (
                    <select
                      value={dadosCompletos.vendedor || ''}
                      onChange={(e) => alterarResponsavel(e.target.value)}
                      disabled={alterandoResponsavel}
                      className="w-full text-sm font-bold text-gray-700 bg-transparent border-none focus:outline-none cursor-pointer disabled:opacity-50 disabled:cursor-wait"
                    >
                      <option value={dadosCompletos.vendedor}>{dadosCompletos.vendedor}</option>
                      {listaUsuariosAtivos
                        .filter(u => u.nome_completo !== dadosCompletos.vendedor && u.nome !== dadosCompletos.vendedor)
                        .map(u => (
                          <option key={u.id} value={u.nome_completo || u.nome}>
                            {u.nome_completo || u.nome} ({u.departamento})
                          </option>
                        ))
                      }
                    </select>
                  ) : (
                    <p className="text-sm font-bold text-gray-700">{dadosCompletos.vendedor}</p>
                  )}
                </div>
              </div>
              
              {/* Informações de Tempo do Pedido */}
              {dadosCompletos.hora && dadosCompletos.hora_fim && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200">
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-300">
                    <label className="block text-xs font-semibold mb-1.5 text-gray-500 uppercase tracking-wide flex items-center gap-1"><Clock size={12} />Início Pedido</label>
                    <p className="text-sm font-bold text-gray-700">{dadosCompletos.hora?.substring(0, 5) || '-'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-300">
                    <label className="block text-xs font-semibold mb-1.5 text-gray-500 uppercase tracking-wide flex items-center gap-1"><Clock size={12} />Fim Pedido</label>
                    <p className="text-sm font-bold text-gray-700">{dadosCompletos.hora_fim?.substring(0, 5) || '-'}</p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-300">
                    <label className="block text-xs font-semibold mb-1.5 text-emerald-600 uppercase tracking-wide flex items-center gap-1"><Clock size={12} />Tempo Total</label>
                    <p className="text-sm font-bold text-emerald-700">{calcularTempoTotal(dadosCompletos.hora, dadosCompletos.hora_fim) || '-'}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ========================================
              2. DADOS DO CLIENTE
          ======================================== */}
          <div className="w-full max-w-6xl mx-auto mb-6">
            <div className="w-full h-px bg-gray-200 mb-6"></div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 group cursor-default">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                  <User size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Dados do Cliente</h2>
                  <p className="text-xs text-gray-400">Informações do cliente</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => toggleExpandir('cliente')} 
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-300"
                >
                  <ChevronDown size={20} className={`transition-transform duration-300 ${expandido.cliente ? 'rotate-180' : ''}`} />
                  {expandido.cliente ? 'Recolher' : 'Expandir'}
                </button>
              </div>
            </div>
          </div>

          {expandido.cliente && (
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-6 w-full max-w-6xl mx-auto border border-gray-100 hover:shadow-2xl hover:scale-[1.01] hover:-translate-y-1 transition-all duration-300">
              <div className="bg-gradient-to-br from-blue-50 via-cyan-50 to-white rounded-2xl p-6 border-2 border-blue-200 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="grid grid-cols-3 gap-x-8 gap-y-4">
                  <div className="group"><p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-1 flex items-center gap-1"><User size={12} />Cliente</p><p className="font-bold text-gray-800 text-lg group-hover:text-blue-600 transition-colors duration-300">{cliente.nome || 'Não informado'}</p></div>
                  <div className="group"><p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-1 flex items-center gap-1"><Building2 size={12} />CNPJ</p><p className="font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">{cliente.cnpj || 'Não informado'}</p></div>
                  <div className="group"><p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-1 flex items-center gap-1"><MapPin size={12} />Endereço</p><p className="font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">{cliente.endereco || '-'}</p></div>
                  <div className="group"><p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-1">Bairro</p><p className="font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">{cliente.bairro || dadosCompletos.bairro || '-'}</p></div>
                  <div className="group"><p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-1">Cidade</p><p className="font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">{cliente.cidade || '-'}</p></div>
                  <div className="group"><p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-1">Estado</p><p className="font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">{cliente.estado || '-'}</p></div>
                  <div className="group"><p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-1 flex items-center gap-1"><Phone size={12} />Telefone</p><p className="font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">{cliente.telefone || '-'}</p></div>
                  <div className="group"><p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-1 flex items-center gap-1"><Mail size={12} />Email</p><p className="font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">{cliente.email || '-'}</p></div>
                  <div className="group"><p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-1 flex items-center gap-1"><Building2 size={12} />Rede</p><p className="font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">{cliente.rede || 'Não informado'}</p></div>
                  <div className="group"><p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-1 flex items-center gap-1"><Calendar size={12} />Constituição / Nasc.</p><p className="font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">{cliente.data_constituicao_nascimento ? new Date(cliente.data_constituicao_nascimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : 'Não informado'}</p></div>
                  <div className="group"><p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-1 flex items-center gap-1"><DollarSign size={12} />Limite de Crédito</p><p className="font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">R$ {formatarMoeda(cliente.limite_credito || 0)}</p></div>
                </div>
              </div>
              {/* Botão Trocar Cliente - dentro do card, abaixo dos dados */}
              {(modoEdicao || dadosCompletos?.fase_atual === 'PEDIDO_CONFIRMADO') && usuarioLogado?.permissoes?.pode_trocar_cliente_pedido && (
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => {
                      if (mostrarTrocaCliente) {
                        cancelarTrocaCliente();
                      } else {
                        setMostrarTrocaCliente(true);
                      }
                    }}
                    className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 ${
                      mostrarTrocaCliente 
                        ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700' 
                        : 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600'
                    }`}
                    title={mostrarTrocaCliente ? 'Cancelar troca de cliente' : 'Trocar o cliente deste pedido'}
                  >
                    {mostrarTrocaCliente ? <X size={16} /> : <Repeat size={16} />}
                    {mostrarTrocaCliente ? 'Cancelar Troca' : 'Trocar Cliente'}
                  </button>
                </div>
              )}

              {/* Painel de Troca de Cliente - CPF/CNPJ */}
              {(modoEdicao || dadosCompletos?.fase_atual === 'PEDIDO_CONFIRMADO') && mostrarTrocaCliente && (
                <div className="mt-4 bg-gradient-to-br from-orange-50 via-amber-50 to-white rounded-2xl p-6 border-2 border-orange-300 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
                      <Search size={16} className="text-white" />
                    </div>
                    <h3 className="font-bold text-orange-700 text-lg">Buscar Novo Cliente por CPF/CNPJ</h3>
                  </div>

                  <div className="flex items-center gap-3 mb-4">
                    <input
                      type="text"
                      value={cnpjTroca}
                      onChange={(e) => setCnpjTroca(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') buscarClienteTroca(); }}
                      placeholder="Digite o CPF ou CNPJ do novo cliente..."
                      className="flex-1 px-4 py-3 border-2 border-orange-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 text-gray-700 font-medium placeholder-gray-400 text-lg"
                      autoFocus
                    />
                    <button
                      onClick={buscarClienteTroca}
                      disabled={buscandoCliente}
                      className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-wait flex items-center gap-2"
                    >
                      {buscandoCliente ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Buscando...
                        </>
                      ) : (
                        <>
                          <Search size={18} />
                          Buscar
                        </>
                      )}
                    </button>
                  </div>

                  {/* Resultado da busca - Cliente encontrado */}
                  {clienteEncontrado && (
                    <div className="bg-white rounded-xl border-2 border-green-300 p-5 shadow-md">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <CheckCircle size={20} className="text-green-500" />
                          <span className="font-bold text-green-700 text-base">Cliente Encontrado</span>
                        </div>
                        {clienteEncontrado.id === dadosCompletos?.clientes?.id && (
                          <span className="text-xs font-semibold text-amber-600 bg-amber-100 px-3 py-1 rounded-full">
                            Cliente atual
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-gray-500 font-semibold uppercase">Nome</p>
                          <p className="font-bold text-gray-800">{clienteEncontrado.nome}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-semibold uppercase">CPF/CNPJ</p>
                          <p className="font-bold text-gray-800">{clienteEncontrado.cnpj}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-semibold uppercase">Cidade</p>
                          <p className="font-bold text-gray-800">{clienteEncontrado.cidade || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-semibold uppercase">Endereço</p>
                          <p className="font-bold text-gray-800">{clienteEncontrado.endereco || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-semibold uppercase">Telefone</p>
                          <p className="font-bold text-gray-800">{clienteEncontrado.telefone || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-semibold uppercase">Limite de Crédito</p>
                          <p className="font-bold text-gray-800">R$ {formatarMoeda(clienteEncontrado.limite_credito || 0)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 justify-end">
                        <button
                          onClick={cancelarTrocaCliente}
                          className="px-5 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-300"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={confirmarTrocaCliente}
                          className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
                        >
                          <Check size={16} />
                          Confirmar Troca
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Indicador permanente: cliente já foi alterado (salvo no banco) */}
              {dadosCompletos?.cliente_alterado && (
                <div className="mt-4 flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-300 rounded-xl">
                  <AlertTriangle size={16} className="text-amber-600 flex-shrink-0" />
                  <span className="text-sm font-semibold text-amber-700">
                    Atenção: esse pedido já teve alteração no cliente.{dadosCompletos.cliente_original_nome ? ` Cliente original: ${dadosCompletos.cliente_original_nome}.` : ''}
                  </span>
                </div>
              )}

              {/* Observação do pedido — fora do card azul, igual ao NovoPedido */}
              {dadosCompletos.observacao_cliente && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm" style={{ color: '#1F3D7A' }}>
                    <strong>Observação:</strong> {dadosCompletos.observacao_cliente}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ========================================
              2.5. DADOS DA ENTREGA
          ======================================== */}
          <div className="w-full max-w-6xl mx-auto mb-6">
            <div className="w-full h-px bg-gray-200 mb-6"></div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 group cursor-default">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                  <MapPin size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Dados da Entrega</h2>
                  <p className="text-xs text-gray-400">Endereço de entrega do pedido</p>
                </div>
              </div>
              <button 
                onClick={() => toggleExpandir('entrega')} 
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-300"
              >
                <ChevronDown size={20} className={`transition-transform duration-300 ${expandido.entrega ? 'rotate-180' : ''}`} />
                {expandido.entrega ? 'Recolher' : 'Expandir'}
              </button>
            </div>
          </div>

          {expandido.entrega && (
            <div className={`bg-white rounded-2xl shadow-lg p-8 mb-6 w-full max-w-6xl mx-auto border ${modoEdicao ? 'border-orange-300 ring-2 ring-orange-200' : 'border-gray-100'} hover:shadow-2xl hover:scale-[1.01] hover:-translate-y-1 transition-all duration-300`}>
              {/* Modo Edição: Campos editáveis */}
              {modoEdicao ? (
                <>
                  {/* Pergunta sobre endereço - EDITÁVEL */}
                  <div className="mb-6">
                    <label className="block text-sm font-bold text-orange-600 uppercase tracking-wide mb-3">
                      O endereço de entrega é o mesmo que o endereço do cliente? *
                    </label>
                    <div className="flex gap-4">
                      <button
                        onClick={() => setDadosEdicao(prev => ({ ...prev, mesmo_endereco_cliente: true }))}
                        className={`flex-1 py-3 rounded-xl font-bold transition-all ${dadosEdicao.mesmo_endereco_cliente ? 'bg-blue-500 text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                      >
                        SIM
                      </button>
                      <button
                        onClick={() => setDadosEdicao(prev => ({ ...prev, mesmo_endereco_cliente: false }))}
                        className={`flex-1 py-3 rounded-xl font-bold transition-all ${!dadosEdicao.mesmo_endereco_cliente ? 'bg-blue-500 text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                      >
                        NÃO
                      </button>
                    </div>
                  </div>

                  {/* Campos de endereço customizado (quando NÃO) */}
                  {!dadosEdicao.mesmo_endereco_cliente && (
                    <div className="bg-gradient-to-br from-orange-50 via-amber-50 to-white rounded-2xl p-6 border-2 border-orange-200 shadow-lg mb-6">
                      <div className="flex items-center gap-3 mb-4">
                        <MapPin size={20} className="text-orange-600" />
                        <span className="font-bold text-orange-700">Endereço de Entrega Personalizado</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Endereço</label>
                          <input
                            type="text"
                            value={dadosEdicao.endereco_entrega}
                            onChange={(e) => setDadosEdicao(prev => ({ ...prev, endereco_entrega: e.target.value }))}
                            className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-orange-400 focus:outline-none"
                            placeholder="Endereço de entrega"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Bairro</label>
                          <input
                            type="text"
                            value={dadosEdicao.bairro_entrega}
                            onChange={(e) => setDadosEdicao(prev => ({ ...prev, bairro_entrega: e.target.value }))}
                            className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-orange-400 focus:outline-none"
                            placeholder="Bairro"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Cidade</label>
                          <input
                            type="text"
                            value={dadosEdicao.cidade_entrega}
                            onChange={(e) => setDadosEdicao(prev => ({ ...prev, cidade_entrega: e.target.value }))}
                            className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-orange-400 focus:outline-none"
                            placeholder="Cidade"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Estado</label>
                          <select
                            value={dadosEdicao.estado_entrega}
                            onChange={(e) => setDadosEdicao(prev => ({ ...prev, estado_entrega: e.target.value }))}
                            className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-orange-400 focus:outline-none"
                          >
                            <option value="Parana">Paraná</option>
                            <option value="Santa Catarina">Santa Catarina</option>
                            <option value="Rio Grande do Sul">Rio Grande do Sul</option>
                            <option value="Sao Paulo">São Paulo</option>
                            <option value="Mato Grosso do Sul">Mato Grosso do Sul</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Card com dados do cliente (quando SIM) */}
                  {dadosEdicao.mesmo_endereco_cliente && (
                    <div className="bg-gradient-to-br from-blue-50 via-cyan-50 to-white rounded-2xl p-6 border-2 border-blue-200 shadow-lg mb-6">
                      <div className="flex items-center gap-3 mb-4">
                        <CheckCircle size={20} className="text-blue-600" />
                        <span className="font-bold text-blue-700">Dados do Cliente (Somente Leitura)</span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                        <div className="group">
                          <p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-1 flex items-center gap-1"><MapPin size={12} />Endereço</p>
                          <p className="font-bold text-gray-800">{cliente.endereco || '-'}</p>
                        </div>
                        <div className="group">
                          <p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-1">Bairro</p>
                          <p className="font-bold text-gray-800">{cliente.bairro || '-'}</p>
                        </div>
                        <div className="group">
                          <p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-1">Cidade</p>
                          <p className="font-bold text-gray-800">{cliente.cidade || '-'}</p>
                        </div>
                        <div className="group">
                          <p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-1">Estado</p>
                          <p className="font-bold text-gray-800">{cliente.estado || '-'}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* TAXA DE ENTREGA - EDITÁVEL */}
                  <div className="pt-6 border-t-2 border-orange-100">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-bold mb-3 text-orange-600 uppercase tracking-wide">
                          Taxa de Entrega? *
                        </label>
                        <div className="flex gap-4">
                          <button
                            onClick={() => setDadosEdicao(prev => ({ ...prev, tem_taxa_entrega: true }))}
                            className={`flex-1 py-3 rounded-xl font-bold transition-all ${dadosEdicao.tem_taxa_entrega ? 'bg-blue-500 text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          >
                            SIM
                          </button>
                          <button
                            onClick={() => setDadosEdicao(prev => ({ ...prev, tem_taxa_entrega: false, valor_taxa_entrega: '' }))}
                            className={`flex-1 py-3 rounded-xl font-bold transition-all ${!dadosEdicao.tem_taxa_entrega ? 'bg-blue-500 text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          >
                            NÃO
                          </button>
                        </div>
                      </div>
                      
                      {dadosEdicao.tem_taxa_entrega && (
                        <div>
                          <label className="block text-sm font-bold mb-3 text-orange-600 uppercase tracking-wide">
                            Valor da Taxa de Entrega (R$) *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={dadosEdicao.valor_taxa_entrega}
                            onChange={(e) => setDadosEdicao(prev => ({ ...prev, valor_taxa_entrega: e.target.value }))}
                            className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-orange-400 focus:outline-none"
                            placeholder="0,00"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                /* Modo Visualização: Campos somente leitura */
                <>
                  {/* Pergunta sobre endereço */}
                  <div className="mb-6">
                    <label className="block text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">
                      O endereço de entrega é o mesmo que o endereço do cliente? *
                    </label>
                    <div className="w-full px-4 py-3 bg-gray-100 border-2 border-gray-300 rounded-xl text-gray-700 font-bold">
                      {dadosCompletos.mesmo_endereco_cliente ? 'SIM' : 'NÃO'}
                    </div>
                  </div>

                  {/* Card com dados */}
                  <div className="bg-gradient-to-br from-blue-50 via-cyan-50 to-white rounded-2xl p-6 border-2 border-blue-200 shadow-lg">
                    <div className="flex items-center gap-3 mb-4">
                      <CheckCircle size={20} className="text-blue-600" />
                      <span className="font-bold text-blue-700">
                        {dadosCompletos.mesmo_endereco_cliente ? 'Dados do Cliente' : 'Endereço de Entrega'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                      <div className="group">
                        <p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-1 flex items-center gap-1"><MapPin size={12} />Endereço</p>
                        <p className="font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">
                          {dadosCompletos.mesmo_endereco_cliente ? cliente.endereco : (dadosCompletos.endereco_entrega || cliente.endereco || '-')}
                        </p>
                      </div>
                      <div className="group">
                        <p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-1">Bairro</p>
                        <p className="font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">
                          {dadosCompletos.mesmo_endereco_cliente ? cliente.bairro : (dadosCompletos.bairro_entrega || cliente.bairro || '-')}
                        </p>
                      </div>
                      <div className="group">
                        <p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-1">Cidade</p>
                        <p className="font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">
                          {dadosCompletos.mesmo_endereco_cliente ? cliente.cidade : (dadosCompletos.cidade_entrega || cliente.cidade || '-')}
                        </p>
                      </div>
                      <div className="group">
                        <p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-1">Estado</p>
                        <p className="font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">
                          {dadosCompletos.mesmo_endereco_cliente ? cliente.estado : (dadosCompletos.estado_entrega || cliente.estado || '-')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* TAXA DE ENTREGA - Aparece quando há dados definidos */}
                  {dadosCompletos.tem_taxa_entrega !== null && dadosCompletos.tem_taxa_entrega !== undefined && (
                    <div className="mt-6 pt-6 border-t-2 border-blue-100">
                      <div className="grid grid-cols-2 gap-6">
                        {/* Pergunta: Tem taxa de entrega? */}
                        <div>
                          <label className="block text-sm font-bold mb-3 text-blue-600 uppercase tracking-wide">
                            Taxa de Entrega? *
                          </label>
                          <div className="w-full p-4 text-base font-semibold border-2 rounded-xl bg-gray-100 border-gray-200 text-gray-700">
                            {dadosCompletos.tem_taxa_entrega ? 'SIM' : 'NÃO'}
                          </div>
                        </div>
                        
                        {/* Valor da Taxa de Entrega - Aparece apenas se SIM */}
                        {dadosCompletos.tem_taxa_entrega && (
                          <div>
                            <label className="block text-sm font-bold mb-3 text-blue-600 uppercase tracking-wide">
                              Valor da Taxa de Entrega (R$) *
                            </label>
                            <div className="w-full p-4 text-base font-semibold border-2 rounded-xl bg-gray-100 border-gray-200 text-gray-700">
                              R$ {formatarMoeda(dadosCompletos.valor_taxa_entrega || 0)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ========================================
              3. PEDIDO (FORRO E ACABAMENTO)
          ======================================== */}
          <div className="w-full max-w-6xl mx-auto mb-6">
            <div className="w-full h-px bg-gray-200 mb-6"></div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 group cursor-default">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                  <ShoppingCart size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Pedido</h2>
                  <p className="text-xs text-gray-400">Itens do pedido</p>
                </div>
              </div>
              <button 
                onClick={() => toggleExpandir('pedido')} 
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-300"
              >
                <ChevronDown size={20} className={`transition-transform duration-300 ${expandido.pedido ? 'rotate-180' : ''}`} />
                {expandido.pedido ? 'Recolher' : 'Expandir'}
              </button>
            </div>
          </div>

          {expandido.pedido && (
            <>
              {/* TABELA UNIFICADA FORRO + ACABAMENTOS (estilo NovoPedido) */}
              <div className={`bg-white rounded-xl shadow-md p-6 mb-6 w-full max-w-6xl mx-auto hover:shadow-lg transition-all duration-300 ${modoEdicao ? 'border-2 border-orange-300 ring-2 ring-orange-200' : ''}`}>
                <div className="flex justify-between items-center mb-4">
                  <div></div>
                  {modoEdicao ? (
                    <button 
                      onClick={adicionarForro} 
                      className="text-white px-4 py-2 rounded-lg hover:opacity-90 hover:scale-105 hover:shadow-md flex items-center gap-2 transition-all duration-300 group"
                      style={{background: 'linear-gradient(to right, #F97316, #F59E0B)'}}
                    >
                      <Plus size={18} className="group-hover:rotate-90 transition-transform duration-300" />
                      Adicionar
                    </button>
                  ) : (
                    <span className="bg-gray-100 text-gray-500 px-4 py-2 rounded-lg flex items-center gap-2 font-semibold text-sm">
                      <CheckCircle size={16} />Bloqueado
                    </span>
                  )}
                </div>
                
                <table className="w-full text-xs table-fixed">
                  <thead>
                    <tr className="text-white" style={{background: modoEdicao ? 'linear-gradient(to right, #F97316, #F59E0B)' : 'linear-gradient(to right, #1F3D7A, #2E5090)'}}>
                      <th className="p-2 text-center rounded-l-lg w-[100px]">ORDEM</th>
                      <th className="p-2 text-center w-[70px]">CÓD</th>
                      <th className="p-2 text-center">PRODUTO</th>
                      <th className="p-2 text-center w-[50px]">TIP</th>
                      <th className="p-2 text-center w-[70px]">QTDE</th>
                      <th className="p-2 text-center w-[85px]">COMP</th>
                      <th className="p-2 text-center w-[80px]">M²</th>
                      <th className="p-2 text-center w-[90px]">R$ UN</th>
                      <th className="p-2 text-center w-[110px]">R$ TOT</th>
                      <th className="p-2 rounded-r-lg w-[200px]">AÇÕES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modoEdicao ? (
                      /* MODO EDIÇÃO - Organizar por tabela: Forro + seus acabamentos */
                      (() => {
                        // Separar forros por tabela mantendo a ordem original
                        const forrosTab1 = forrosEdicao.filter(f => f.tabela === 'Tabela 1');
                        const forrosTab2 = forrosEdicao.filter(f => f.tabela === 'Tabela 2');
                        
                        // Calcular pontuação e status de cada TABELA (não por família)
                        const tab1Data = calcularResumoTabelaEdicao('Tabela 1');
                        const tab2Data = calcularResumoTabelaEdicao('Tabela 2');
                        
                        const pontuacaoTab1Calc = tab1Data.pontuacao || 0;
                        const pontuacaoTab2Calc = tab2Data.pontuacao || 0;
                        
                        const statusTab1Calc = tab1Data.temItens ? getStatusByScore(pontuacaoTab1Calc) : null;
                        const statusTab2Calc = tab2Data.temItens ? getStatusByScore(pontuacaoTab2Calc) : null;
                        
                        const configTab1 = statusTab1Calc ? getStatusConfig(statusTab1Calc) : null;
                        const configTab2 = statusTab2Calc ? getStatusConfig(statusTab2Calc) : null;
                        
                        // Função auxiliar para verificar se um acessório pertence a um forro específico
                        // Simplificada para usar comparação direta de valores
                        const acessorioPertenceAoForro = (acessorio, forro) => {
                          // Comparar forroId do acessório com id do forro
                          if (acessorio.forroId === forro.id) return true;
                          
                          // Comparar como strings para garantir compatibilidade de tipos
                          const aForroIdStr = acessorio.forroId != null ? String(acessorio.forroId) : null;
                          const aForroIdOriginalStr = acessorio.forroIdOriginal != null ? String(acessorio.forroIdOriginal) : null;
                          const aVinculoForroIdStr = acessorio.vinculoForroId != null ? String(acessorio.vinculoForroId) : null;
                          const forroIdStr = String(forro.id);
                          const forroIdOriginalStr = forro.idOriginal != null ? String(forro.idOriginal) : null;
                          
                          // Verificar se algum ID do acessório bate com algum ID do forro
                          const idsDoForro = [forroIdStr, forroIdOriginalStr].filter(Boolean);
                          const idsDoAcessorio = [aForroIdStr, aForroIdOriginalStr, aVinculoForroIdStr].filter(Boolean);
                          
                          return idsDoAcessorio.some(aId => idsDoForro.includes(aId));
                        };
                        
                        // Montar mapa de acabamentos por forro
                        // IMPORTANTE: Buscar em TODOS os forros, não só os da tabela atual
                        const buildAcabamentosPorForro = (forros, tabela) => {
                          const mapa = new Map();
                          forros.forEach(f => mapa.set(String(f.id), []));
                          
                          // Todos os forros de edição (de todas as tabelas) para buscar vínculos
                          const todosForros = forrosEdicao;

                          // Primeiro, vincular pelo forroId/forroIdOriginal/vinculoForroId
                          acessoriosEdicao.forEach(a => {
                            // Procurar o forro vinculado em TODOS os forros
                            const forroVinculado = todosForros.find(f => acessorioPertenceAoForro(a, f));
                            
                            // Se encontrou e o forro está na lista de forros desta tabela, adicionar
                            if (forroVinculado && forros.some(f => String(f.id) === String(forroVinculado.id))) {
                              mapa.get(String(forroVinculado.id)).push(a);
                            }
                          });

                          // Depois, distribuir órfãos (acessórios da mesma tabela sem vínculo válido)
                          const orfaos = acessoriosEdicao.filter(a => {
                            if (a.tabela !== tabela) return false;
                            // Verificar se não tem vínculo com NENHUM forro
                            return !todosForros.some(f => acessorioPertenceAoForro(a, f));
                          });

                          if (forros.length > 0 && orfaos.length > 0) {
                            let idx = 0;
                            orfaos.forEach(a => {
                              const forroAlvo = forros[idx % forros.length];
                              mapa.get(String(forroAlvo.id)).push(a);
                              idx += 1;
                            });
                          }

                          return mapa;
                        };

                        const getAcessorioKey = (a) => `acess-${a.id}-${a.forroId || a.forroIdOriginal || 'orphan'}-${a.tabela || 'sem-tabela'}`;
                        const acessoriosRenderizados = new Set();

                        const renderAcabamentoRow = (a, statusAcab, configAcab, tabelaOverride = null) => {
                          const acessorioKey = getAcessorioKey(a);
                          acessoriosRenderizados.add(acessorioKey);
                          const tabelaExibida = tabelaOverride || a.tabela || 'Tabela 1';
                          return (
                            <tr key={acessorioKey} className="border-b hover:bg-gray-100 transition-colors duration-200" style={{backgroundColor: '#f5f5f5'}}>
                              <td className="p-1 text-center">
                                <input type="text" value={tabelaExibida} className="w-full p-1 bg-gray-100 text-center font-bold text-xs" disabled title="Segue a tabela do forro" />
                              </td>
                              <td className="p-1 text-center">
                                <input type="text" value={a.codigo || ''} className="w-full p-1 text-center font-bold bg-transparent" disabled />
                              </td>
                              <td className="p-1">
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-400 text-[10px] font-bold">↳</span>
                                  <select 
                                    value={a.produto || ''} 
                                    onChange={(e) => atualizarAcessorio(a.id, 'produto', e.target.value)}
                                    className="w-full p-1 border rounded text-xs border-cyan-200"
                                  >
                                    <option value="">Selecione...</option>
                                    {acessoriosLista.map((x, idx) => <option key={idx} value={x.produto}>{x.produto}</option>)}
                                  </select>
                                </div>
                              </td>
                              <td className="p-1 text-center font-bold text-gray-500">{a.tipo}</td>
                              <td className="p-1 text-center">
                                <input 
                                  type="number" 
                                  min="0" 
                                  value={a.quantidade || ''} 
                                  onChange={(e) => atualizarAcessorio(a.id, 'quantidade', parseInt(e.target.value) || 0)}
                                  className="w-full p-1 border rounded text-center border-cyan-200"
                                />
                              </td>
                              <td className="p-1 text-center text-gray-400 text-[10px]" colSpan="2">-</td>
                              <td className="p-1 text-center">
                                <div className="relative group">
                                  <input 
                                    type="number" 
                                    step="0.01" 
                                    value={a.preco || ''} 
                                    onChange={(e) => atualizarAcessorio(a.id, 'preco', parseFloat(e.target.value) || 0)}
                                    className={`w-full p-1 border rounded text-center border-cyan-200 ${a.abaixoMinimo ? 'border-red-500 bg-red-50 text-red-600 border-2' : ''}`}
                                  />
                                  {a.abaixoMinimo && a.valorMinimo > 0 && (
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-red-600 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50">
                                      Mínimo: R$ {a.valorMinimo?.toFixed(2)}
                                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-red-600"></div>
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="p-1 text-center">
                                <input type="text" value={`R$ ${formatarMoeda(a.total || 0)}`} className="w-full p-1 bg-green-50 font-bold text-center" disabled />
                              </td>
                              <td className="p-1">
                                <div className="flex items-center justify-between gap-1">
                                  <button 
                                    onClick={() => removerAcessorio(a.id)} 
                                    className="bg-red-500 text-white p-1 rounded hover:bg-red-600 transition-colors" 
                                    title="Remover acabamento"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                  {statusAcab && configAcab && (
                                    <span className={`px-2 py-1 rounded text-[10px] font-bold whitespace-nowrap ${configAcab.bgSolid} ${configAcab.textColor}`}>
                                      {statusAcab === 'EM ANÁLISE' ? 'ANÁLISE' : statusAcab}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        };

                        // Função para renderizar um forro com seus acabamentos
                        const renderForroComAcabamentos = (forro, acabamentosDoForro, statusTabela, configTabela) => {
                          const todosAcabamentos = acabamentosDoForro;
                          
                          const podeAdicionarAcabamento = (parseFloat(forro.total) || 0) > 0;
                          const statusForro = statusTabela;
                          const configForro = configTabela;
                          const estadoCliente = dadosCompletos?.clientes?.estado || 'Parana';
                          
                          return (
                            <React.Fragment key={forro.id}>
                              {/* Linha do Forro */}
                              <tr className="border-b hover:bg-gray-200 transition-colors duration-200" style={{backgroundColor: '#e8e8e8'}}>
                                <td className="p-1 text-center">
                                  <select 
                                    value={forro.tabela || 'Tabela 1'} 
                                    onChange={(e) => atualizarForro(forro.id, 'tabela', e.target.value)}
                                    className="w-full p-1 border rounded font-bold text-center bg-yellow-50"
                                  >
                                    <option value="Tabela 1">Tabela 1</option>
                                    <option value="Tabela 2">Tabela 2</option>
                                  </select>
                                </td>
                                <td className="p-1 text-center">
                                  <input type="text" value={forro.codigo || ''} className="w-full p-1 bg-transparent text-center font-bold" disabled />
                                </td>
                                <td className="p-1">
                                  <select 
                                    value={forro.produto || ''} 
                                    onChange={(e) => atualizarForro(forro.id, 'produto', e.target.value)}
                                    className="w-full p-1 border rounded"
                                  >
                                    <option value="">Selecione...</option>
                                    {produtosForro.map((p, idx) => <option key={idx} value={p.produto}>{p.produto}</option>)}
                                  </select>
                                </td>
                                <td className="p-1 text-center font-bold">{forro.tipo}</td>
                                <td className="p-1 text-center">
                                  <input 
                                    type="number" 
                                    min="0" 
                                    value={forro.quantidade || ''} 
                                    onChange={(e) => atualizarForro(forro.id, 'quantidade', parseInt(e.target.value) || 0)}
                                    className="w-full p-1 border rounded text-center"
                                  />
                                </td>
                                <td className="p-1 text-center">
                                  <div className="flex items-center gap-1 justify-center">
                                    <input 
                                      type="text" 
                                      value={forro.comprimento || 6} 
                                      onChange={(e) => {
                                        // Converter vírgula para ponto
                                        let valor = e.target.value.replace(',', '.');
                                        atualizarForro(forro.id, 'comprimento', valor);
                                      }}
                                      onBlur={(e) => {
                                        // Validar e arredondar para 0.1 (10cm) ao sair do campo
                                        let valor = parseFloat(e.target.value.replace(',', '.')) || 3;
                                        // Limitar entre 3 e 12
                                        if (valor < 3) valor = 3;
                                        if (valor > 12) valor = 12;
                                        // Arredondar para 1 casa decimal (10 em 10 cm)
                                        valor = Math.round(valor * 10) / 10;
                                        atualizarForro(forro.id, 'comprimento', valor);
                                      }}
                                      className="w-14 p-1 border rounded text-center"
                                      placeholder="6"
                                    />
                                    <span className="text-xs text-gray-500">m</span>
                                    <div className="flex flex-col">
                                      <button 
                                        type="button"
                                        onClick={() => {
                                          let valor = parseFloat(forro.comprimento) || 3;
                                          valor = Math.round((valor + 0.1) * 10) / 10;
                                          if (valor > 12) valor = 12;
                                          atualizarForro(forro.id, 'comprimento', valor);
                                        }}
                                        disabled={parseFloat(forro.comprimento) >= 12}
                                        className="text-gray-500 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                        title="Aumentar 10cm"
                                      >
                                        <ChevronUp size={14} />
                                      </button>
                                      <button 
                                        type="button"
                                        onClick={() => {
                                          let valor = parseFloat(forro.comprimento) || 3;
                                          valor = Math.round((valor - 0.1) * 10) / 10;
                                          if (valor < 3) valor = 3;
                                          atualizarForro(forro.id, 'comprimento', valor);
                                        }}
                                        disabled={parseFloat(forro.comprimento) <= 3}
                                        className="text-gray-500 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                        title="Diminuir 10cm"
                                      >
                                        <ChevronDown size={14} />
                                      </button>
                                    </div>
                                  </div>
                                </td>
                                <td className="p-1 text-center">
                                  <input type="text" value={formatarMoeda(forro.m2 || 0)} className="w-full p-1 bg-blue-50 font-bold text-center" disabled />
                                </td>
                                <td className="p-1 text-center">
                                  <div className="relative group">
                                    <input 
                                      type="number" 
                                      step="0.01" 
                                      value={forro.preco || ''} 
                                      onChange={(e) => atualizarForro(forro.id, 'preco', parseFloat(e.target.value) || 0)}
                                      className={`w-full p-1 border rounded text-center ${forro.abaixoMinimo ? 'border-red-500 bg-red-50 text-red-600 border-2' : ''}`}
                                    />
                                    {forro.abaixoMinimo && forro.valorMinimo > 0 && (
                                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-red-600 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50">
                                        Mínimo: R$ {forro.valorMinimo?.toFixed(2)}
                                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-red-600"></div>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="p-1 text-center">
                                  <input type="text" value={`R$ ${formatarMoeda(forro.total || 0)}`} className="w-full p-1 bg-green-50 font-bold text-center" disabled />
                                </td>
                                <td className="p-1">
                                  <div className="flex items-center justify-between gap-1">
                                    <div className="flex items-center gap-1">
                                      {podeAdicionarAcabamento ? (
                                        <button 
                                          onClick={() => adicionarAcessorioParaForro(forro.id, forro.tabela)}
                                          className="text-white hover:opacity-90 px-2 py-1 rounded text-[10px] font-semibold transition-all flex items-center gap-1 whitespace-nowrap shadow-sm hover:shadow-md"
                                          style={{backgroundColor: '#00BEFF'}}
                                          title="Adicionar acabamento para este forro"
                                        >
                                          <Plus size={12} />
                                          Acabamento
                                        </button>
                                      ) : (
                                        <span className="text-gray-400 px-2 py-1 rounded text-[10px] font-semibold flex items-center gap-1 whitespace-nowrap cursor-not-allowed" title="Preencha o forro com valor > 0">
                                          <Plus size={12} />
                                          Acabamento
                                        </span>
                                      )}
                                      <button 
                                        onClick={() => removerForro(forro.id)} 
                                        className="bg-red-500 text-white p-1 rounded hover:bg-red-600 transition-colors" 
                                        title="Remover forro e seus acabamentos"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                    {statusForro && configForro && (
                                      <span className={`px-2 py-1 rounded text-[10px] font-bold whitespace-nowrap ${configForro.bgSolid} ${configForro.textColor}`}>
                                        {statusForro === 'EM ANÁLISE' ? 'ANÁLISE' : statusForro}
                                      </span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              
                              {/* Linhas de Acabamento do forro (exibidas logo abaixo do forro) */}
                              {todosAcabamentos.map((a) => renderAcabamentoRow(a, statusForro, configForro, forro.tabela))}
                            </React.Fragment>
                          );
                        };
                        
                        // Montar mapa de acabamentos por forro em cada tabela
                        const acabamentosPorForroTab1 = buildAcabamentosPorForro(forrosTab1, 'Tabela 1');
                        const acabamentosPorForroTab2 = buildAcabamentosPorForro(forrosTab2, 'Tabela 2');
                        
                        // Renderizar na mesma ordem do modo visualização:
                        // 1. Todos os forros da Tabela 1 (cada um com seus acabamentos)
                        // 2. Todos os forros da Tabela 2 (cada um com seus acabamentos)
                        return (
                          <>
                            {/* Renderiza forros da Tabela 1 com seus acabamentos */}
                            {forrosTab1.map((forro, i) => {
                              const acabamentosDoForro = acabamentosPorForroTab1.get(String(forro.id)) || [];
                              return renderForroComAcabamentos(
                                forro,
                                acabamentosDoForro,
                                statusTab1Calc,
                                configTab1
                              );
                            })}

                            {/* Renderiza acabamentos órfãos da Tabela 1 (se existirem) */}
                            {acessoriosEdicao
                              .filter(a => a.tabela === 'Tabela 1')
                              .filter(a => !acessoriosRenderizados.has(getAcessorioKey(a)))
                              .map(a => renderAcabamentoRow(a, statusTab1Calc, configTab1))}
                            
                            {/* Renderiza forros da Tabela 2 com seus acabamentos */}
                            {forrosTab2.map((forro, i) => {
                              const acabamentosDoForro = acabamentosPorForroTab2.get(String(forro.id)) || [];
                              return renderForroComAcabamentos(
                                forro,
                                acabamentosDoForro,
                                statusTab2Calc,
                                configTab2
                              );
                            })}

                            {/* Renderiza acabamentos órfãos da Tabela 2 (se existirem) */}
                            {acessoriosEdicao
                              .filter(a => a.tabela === 'Tabela 2')
                              .filter(a => !acessoriosRenderizados.has(getAcessorioKey(a)))
                              .map(a => renderAcabamentoRow(a, statusTab2Calc, configTab2))}
                          </>
                        );
                      })()
                    ) : (
                      /* MODO VISUALIZAÇÃO - dadosCompletos.forros e dadosCompletos.acessorios */
                      /* CORREÇÃO: Mostrar cada forro seguido de seus acabamentos na ordem de confecção */
                      (() => {
                        // Separa forros e acessórios por tabela
                        const forrosTab1 = (dadosCompletos.forros || []).filter(f => f.tipo_tabela === 'Tabela 1');
                        const forrosTab2 = (dadosCompletos.forros || []).filter(f => f.tipo_tabela === 'Tabela 2');
                        const acessoriosTab1 = (dadosCompletos.acessorios || []).filter(a => a.tipo_tabela === 'Tabela 1');
                        const acessoriosTab2 = (dadosCompletos.acessorios || []).filter(a => a.tipo_tabela === 'Tabela 2');
                        
                        // Função para vincular acessórios aos seus forros pelo forro_id
                        const buildAcessoriosPorForro = (forros, acessorios) => {
                          const mapa = new Map();
                          forros.forEach(f => mapa.set(String(f.id), []));
                          
                          // Vincular acessórios pelos IDs
                          acessorios.forEach(a => {
                            const forroIdAcess = String(a.forro_id || a.forroId || '');
                            if (mapa.has(forroIdAcess)) {
                              mapa.get(forroIdAcess).push(a);
                            }
                          });
                          
                          // Pegar acessórios órfãos (sem vínculo válido)
                          const orfaos = acessorios.filter(a => {
                            const forroIdAcess = String(a.forro_id || a.forroId || '');
                            return !mapa.has(forroIdAcess);
                          });
                          
                          // Distribuir órfãos proporcionalmente entre os forros
                          if (forros.length > 0 && orfaos.length > 0) {
                            let idx = 0;
                            orfaos.forEach(a => {
                              const forroAlvo = forros[idx % forros.length];
                              mapa.get(String(forroAlvo.id)).push(a);
                              idx++;
                            });
                          }
                          
                          return { mapa, orfaos: forros.length === 0 ? orfaos : [] };
                        };
                        
                        const { mapa: acessoriosPorForroTab1, orfaos: orfaosTab1 } = buildAcessoriosPorForro(forrosTab1, acessoriosTab1);
                        const { mapa: acessoriosPorForroTab2, orfaos: orfaosTab2 } = buildAcessoriosPorForro(forrosTab2, acessoriosTab2);
                        
                        // Função para renderizar um forro com seus acessórios vinculados
                        const renderForroComAcessorios = (item, i, acessoriosDoForro) => {
                          const m2Calculado = calcularM2Item(item);
                          const totalCalculado = calcularTotalItem(item, true);
                          
                          return (
                            <React.Fragment key={`forro-${item.id || i}`}>
                              {/* Linha do Forro */}
                              <tr className="border-b hover:bg-gray-200 transition-colors duration-200" style={{backgroundColor: '#e8e8e8'}}>
                                <td className="p-2 text-center">
                                  <span className={`px-2 py-1 rounded text-xs font-bold ${item.tipo_tabela === 'Tabela 1' ? 'bg-blue-100 text-blue-700' : 'bg-cyan-100 text-cyan-700'}`}>
                                    {item.tipo_tabela}
                                  </span>
                                </td>
                                <td className="p-2 text-center font-bold">{item.produto_codigo}</td>
                                <td className="p-2">{item.produto_nome}</td>
                                <td className="p-2 text-center font-bold">{item.tipo}</td>
                                <td className="p-2 text-center">{item.quantidade}</td>
                                <td className="p-2 text-center">{item.comprimento}m</td>
                                <td className="p-2 text-center font-bold bg-blue-50">{formatarMoeda(m2Calculado)}</td>
                                <td className="p-2 text-center">R$ {formatarMoeda(item.preco_unitario || 0)}</td>
                                <td className="p-2 text-center font-bold text-green-600 bg-green-50">R$ {formatarMoeda(totalCalculado)}</td>
                                <td className="p-2 text-center text-gray-400 text-xs">-</td>
                              </tr>
                              
                              {/* Linhas de Acabamento (acessórios) vinculados a este forro */}
                              {acessoriosDoForro.map((a, idx) => {
                                const totalAcessorio = calcularTotalItem(a, false);
                                return (
                                  <tr key={`acess-${a.id || idx}`} className="border-b hover:bg-gray-100 transition-colors duration-200" style={{backgroundColor: '#f5f5f5'}}>
                                    <td className="p-2 text-center">
                                      <span className={`px-2 py-1 rounded text-xs font-bold ${a.tipo_tabela === 'Tabela 1' ? 'bg-blue-100 text-blue-700' : 'bg-cyan-100 text-cyan-700'}`}>
                                        {a.tipo_tabela}
                                      </span>
                                    </td>
                                    <td className="p-2 text-center font-bold">{a.produto_codigo}</td>
                                    <td className="p-2">
                                      <div className="flex items-center gap-1">
                                        <span className="text-gray-400 text-sm font-bold">↳</span>
                                        <span>{a.produto_nome}</span>
                                      </div>
                                    </td>
                                    <td className="p-2 text-center font-bold text-gray-500">{a.tipo}</td>
                                    <td className="p-2 text-center">{a.quantidade}</td>
                                    <td className="p-2 text-center text-gray-400" colSpan="2">-</td>
                                    <td className="p-2 text-center">R$ {formatarMoeda(a.preco_unitario || 0)}</td>
                                    <td className="p-2 text-center font-bold text-green-600 bg-green-50">R$ {formatarMoeda(totalAcessorio)}</td>
                                    <td className="p-2 text-center text-gray-400 text-xs">-</td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          );
                        };
                        
                        // Função para renderizar acessórios órfãos
                        const renderAcessorioOrfao = (a, idx, tabela) => {
                          const totalAcessorio = calcularTotalItem(a, false);
                          return (
                            <tr key={`acess-orphan-${tabela}-${a.id || idx}`} className="border-b hover:bg-gray-100 transition-colors duration-200" style={{backgroundColor: '#f5f5f5'}}>
                              <td className="p-2 text-center">
                                <span className={`px-2 py-1 rounded text-xs font-bold ${tabela === 1 ? 'bg-blue-100 text-blue-700' : 'bg-cyan-100 text-cyan-700'}`}>
                                  {a.tipo_tabela}
                                </span>
                              </td>
                              <td className="p-2 text-center font-bold">{a.produto_codigo}</td>
                              <td className="p-2">
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-400 text-sm font-bold">↳</span>
                                  <span>{a.produto_nome}</span>
                                </div>
                              </td>
                              <td className="p-2 text-center font-bold text-gray-500">{a.tipo}</td>
                              <td className="p-2 text-center">{a.quantidade}</td>
                              <td className="p-2 text-center text-gray-400" colSpan="2">-</td>
                              <td className="p-2 text-center">R$ {formatarMoeda(a.preco_unitario || 0)}</td>
                              <td className="p-2 text-center font-bold text-green-600 bg-green-50">R$ {formatarMoeda(totalAcessorio)}</td>
                              <td className="p-2 text-center text-gray-400 text-xs">-</td>
                            </tr>
                          );
                        };
                        
                        return (
                          <>
                            {/* Renderiza forros da Tabela 1, cada um seguido de seus acessórios */}
                            {forrosTab1.map((item, i) => {
                              const acessoriosDoForro = acessoriosPorForroTab1.get(String(item.id)) || [];
                              return renderForroComAcessorios(item, i, acessoriosDoForro);
                            })}
                            
                            {/* Se não tem forros mas tem acessórios na Tabela 1, mostrar só os acessórios */}
                            {orfaosTab1.map((a, idx) => renderAcessorioOrfao(a, idx, 1))}
                            
                            {/* Renderiza forros da Tabela 2, cada um seguido de seus acessórios */}
                            {forrosTab2.map((item, i) => {
                              const acessoriosDoForro = acessoriosPorForroTab2.get(String(item.id)) || [];
                              return renderForroComAcessorios(item, i, acessoriosDoForro);
                            })}
                            
                            {/* Se não tem forros mas tem acessórios na Tabela 2, mostrar só os acessórios */}
                            {orfaosTab2.map((a, idx) => renderAcessorioOrfao(a, idx, 2))}
                          </>
                        );
                      })()
                    )}
                  </tbody>
                  {/* Linha de Totais - similar ao NovoPedido */}
                  {(temItensTab1 || temItensTab2) && (
                    <tfoot>
                      <tr className="bg-gray-100 border-t-2 border-gray-300">
                        <td colSpan={5} className="p-2">
                          <div className="flex items-center justify-end gap-4 flex-wrap w-full">
                            {podeVerMetricas() && (() => {
                              const totalForro = resumoTab1.filter(r => r.isForro).reduce((sum, r) => sum + r.total, 0) +
                                                resumoTab2.filter(r => r.isForro).reduce((sum, r) => sum + r.total, 0);
                              const totalAcabamento = resumoTab1.filter(r => !r.isForro).reduce((sum, r) => sum + r.total, 0) +
                                                     resumoTab2.filter(r => !r.isForro).reduce((sum, r) => sum + r.total, 0);
                              const percentualAcabamento = totalForro > 0 ? (totalAcabamento / totalForro) * 100 : 0;
                              return (
                                <span className="text-blue-600 font-bold text-xs whitespace-nowrap">
                                  % Acabamento: {percentualAcabamento.toFixed(1)}%
                                </span>
                              );
                            })()}
                            <span className="text-gray-600 font-semibold text-xs whitespace-nowrap">
                              Qtde. Acabamentos: {(modoEdicao ? acessoriosEdicao : (dadosCompletos.acessorios || [])).reduce((acc, a) => acc + (parseInt(a.quantidade) || 0), 0)}
                            </span>
                          </div>
                        </td>
                        <td className="p-2 text-right">
                          <span className="text-gray-600 font-bold text-xs whitespace-nowrap">TOTAIS:</span>
                        </td>
                        <td className="p-2 text-center">
                          <span className="text-gray-700 font-bold text-sm whitespace-nowrap">{formatarMoeda(
                            (modoEdicao ? forrosEdicao : (dadosCompletos.forros || [])).reduce((acc, f) => acc + (parseFloat(f.m2) || 0), 0)
                          )} m²</span>
                        </td>
                        <td className="p-2"></td>
                        <td className="p-2 text-center">
                          <span className="text-gray-700 font-bold text-sm whitespace-nowrap">R$ {formatarMoeda(valorTotalTab1 + valorTotalTab2)}</span>
                        </td>
                        <td className="p-2">
                          {/* Status Geral do Pedido */}
                          {(() => {
                            const totalVendaGeral = valorTotalTab1 + valorTotalTab2;
                            const pontTab1 = valorTotalTab1 > 0 ? Math.min(pontuacaoTab1, 50) : 0;
                            const pontTab2 = valorTotalTab2 > 0 ? Math.min(pontuacaoTab2, 50) : 0;
                            const pontuacaoGeralCalc = Math.min(pontTab1 + pontTab2, 50);
                            const _valProdEsp = parseFloat(dadosCompletos.valor_produtos_especiais) || 0;
                            const statusGeralCalc = (() => {
                              if (totalVendaGeral === 0 && _valProdEsp > 0) {
                                if (dadosCompletos.status_geral) return dadosCompletos.status_geral;
                                if (dadosCompletos.pontuacao_geral != null) return getStatusByScore(parseFloat(dadosCompletos.pontuacao_geral) || 0);
                              }
                              return totalVendaGeral === 0 ? 'INVIÁVEL' : getStatusByScore(pontuacaoGeralCalc);
                            })();
                            const configGeralCalc = getStatusConfig(statusGeralCalc);
                            const IconeGeral = configGeralCalc?.icon;
                            
                            return totalVendaGeral > 0 ? (
                              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold ${configGeralCalc?.bgSolid} ${configGeralCalc?.textColor}`}>
                                {IconeGeral && <IconeGeral size={16} />}
                                <span className="whitespace-nowrap">{statusGeralCalc}</span>
                              </span>
                            ) : null;
                          })()}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
                
                {/* Mensagem quando não há itens */}
                {((modoEdicao && forrosEdicao.length === 0) || (!modoEdicao && (!dadosCompletos.forros || dadosCompletos.forros.length === 0) && (!dadosCompletos.acessorios || dadosCompletos.acessorios.length === 0))) && (
                  <div className="text-center py-8 text-gray-400">
                    <ShoppingCart size={40} className="mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Nenhum item adicionado ainda</p>
                    {modoEdicao && <p className="text-xs">Clique em "Adicionar" para incluir um forro</p>}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ========================================
              4. RESUMO DA COMPRA
          ======================================== */}
          <div className="w-full max-w-6xl mx-auto mb-6">
            <div className="w-full h-px bg-gray-200 mb-6"></div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 group cursor-default">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                  <ShoppingCart size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Resumo da Compra</h2>
                  <p className="text-xs text-gray-400">Visão geral dos itens e valores</p>
                </div>
              </div>
              <button 
                onClick={() => toggleExpandir('resumo')} 
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-300"
              >
                <ChevronDown size={20} className={`transition-transform duration-300 ${expandido.resumo ? 'rotate-180' : ''}`} />
                {expandido.resumo ? 'Recolher' : 'Expandir'}
              </button>
            </div>
          </div>

          {expandido.resumo && (
            <div className="w-full max-w-6xl mx-auto bg-white rounded-2xl shadow-xl p-8 border border-gray-100 mb-6 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
              {(() => {
                const resumoTab1View = modoEdicao ? buildResumoCompraEdicao('Tabela 1') : resumoTab1;
                const resumoTab2View = modoEdicao ? buildResumoCompraEdicao('Tabela 2') : resumoTab2;
                return (
              <div className="grid grid-cols-2 gap-6">
                {/* TABELA 1 */}
                <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl hover:scale-[1.02] transition-all duration-300 border border-gray-100">
                  <div style={{backgroundColor: '#0d5dfc'}} className="p-4 text-white font-bold text-lg flex items-center justify-between min-h-[60px]">
                    <span className="text-xl">TABELA 1</span>
                    {temItensTab1 && (
                      <div className="bg-white/20 px-4 py-2 rounded-xl">
                        <span className="text-white/80 text-xs mr-2">Pontuação:</span>
                        <span className="font-black text-lg">{formatarPontuacao(pontuacaoTab1)}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    {temItensTab1 && resumoTab1View.length > 0 ? (
                      <>
                        <table className="w-full text-sm">
                          <thead><tr className="border-b-2"><th className="pb-3 text-left">Produto</th><th className="pb-3 text-right">Preço</th><th className="pb-3 text-right">Qtd/m²</th><th className="pb-3 text-right">Total</th></tr></thead>
                          <tbody>
                            {resumoTab1View.map((r, i) => (
                              <tr key={i} className={`border-b hover:bg-slate-50 transition-colors duration-200 ${r.isAcabamento ? 'bg-slate-50' : ''}`}>
                                <td className="py-2">
                                  <div className="flex items-center gap-1">
                                    {r.isAcabamento && <span className="text-gray-400 text-sm font-bold">↳</span>}
                                    <span className={r.isAcabamento ? 'text-slate-600' : ''}>{limparNomeProduto(r.produto)}</span>
                                  </div>
                                </td>
                                <td className="py-2 text-right">R$ {formatarMoeda(r.preco)}</td>
                                <td className="py-2 text-right">{r.isForro ? (parseFloat(r.qtdade) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : (r.qtdade || 0)}</td>
                                <td className="py-2 text-right font-bold">R$ {formatarMoeda(r.total)}</td>
                              </tr>
                            ))}
                            <tr className="bg-slate-100 font-bold border-t-2"><td colSpan="3" className="py-3">TOTAL</td><td className="py-3 text-right text-lg">R$ {formatarMoeda(valorTotalTab1)}</td></tr>
                          </tbody>
                        </table>
                        {statusTab1 && (
                          <div className="mt-4">
                            <StatusBadge status={statusTab1} tamanho="md" fullWidth />
                          </div>
                        )}
                      </>
                    ) : <p className="text-center py-8 text-gray-400">Nenhum item</p>}
                  </div>
                </div>

                {/* TABELA 2 */}
                <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl hover:scale-[1.02] transition-all duration-300 border border-gray-100">
                  <div style={{backgroundColor: '#00BEFF'}} className="p-4 text-white font-bold text-lg flex items-center justify-between min-h-[60px]">
                    <span className="text-xl">TABELA 2</span>
                    {temItensTab2 && (
                      <div className="bg-white/20 px-4 py-2 rounded-xl">
                        <span className="text-white/80 text-xs mr-2">Pontuação:</span>
                        <span className="font-black text-lg">{formatarPontuacao(pontuacaoTab2)}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    {temItensTab2 && resumoTab2View.length > 0 ? (
                      <>
                        <table className="w-full text-sm">
                          <thead><tr className="border-b-2"><th className="pb-3 text-left">Produto</th><th className="pb-3 text-right">Preço</th><th className="pb-3 text-right">Qtd/m²</th><th className="pb-3 text-right">Total</th></tr></thead>
                          <tbody>
                            {resumoTab2View.map((r, i) => (
                              <tr key={i} className={`border-b hover:bg-slate-50 transition-colors duration-200 ${r.isAcabamento ? 'bg-slate-50' : ''}`}>
                                <td className="py-2">
                                  <div className="flex items-center gap-1">
                                    {r.isAcabamento && <span className="text-gray-400 text-sm font-bold">↳</span>}
                                    <span className={r.isAcabamento ? 'text-slate-600' : ''}>{limparNomeProduto(r.produto)}</span>
                                  </div>
                                </td>
                                <td className="py-2 text-right">R$ {formatarMoeda(r.preco)}</td>
                                <td className="py-2 text-right">{r.isForro ? (parseFloat(r.qtdade) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : (r.qtdade || 0)}</td>
                                <td className="py-2 text-right font-bold">R$ {formatarMoeda(r.total)}</td>
                              </tr>
                            ))}
                            <tr className="bg-slate-100 font-bold border-t-2"><td colSpan="3" className="py-3">TOTAL</td><td className="py-3 text-right text-lg">R$ {formatarMoeda(valorTotalTab2)}</td></tr>
                          </tbody>
                        </table>
                        {statusTab2 && (
                          <div className="mt-4">
                            <StatusBadge status={statusTab2} tamanho="md" fullWidth />
                          </div>
                        )}
                      </>
                    ) : <p className="text-center py-8 text-gray-400">Nenhum item</p>}
                  </div>
                </div>
              </div>
                );
              })()}

              {/* SEÇÃO DE PRODUTOS ESPECIAIS (só aparece se houver) */}
              {dadosCompletos?.produtosEspeciais && dadosCompletos.produtosEspeciais.length > 0 && (
                <div className="mt-6">
                  <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl hover:scale-[1.01] transition-all duration-300 border border-gray-200">
                    <div className="bg-gradient-to-r from-slate-500 to-slate-600 p-4 text-white font-bold text-lg flex items-center justify-between min-h-[60px]">
                      <div className="flex items-center gap-3">
                        <Package size={24} />
                        <span className="text-xl">PRODUTOS ESPECIAIS</span>
                      </div>
                      <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                        Status: VIÁVEL
                      </span>
                    </div>
                    <div className="p-6">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b-2">
                            <th className="pb-3 text-left">Produto</th>
                            <th className="pb-3 text-right">Qtde</th>
                            <th className="pb-3 text-right">Preço</th>
                            <th className="pb-3 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dadosCompletos.produtosEspeciais.map((item, idx) => (
                            <tr key={idx} className="border-b hover:bg-slate-50 transition-colors duration-200">
                              <td className="py-2">{item.produto_nome || item.produto}</td>
                              <td className="py-2 text-right">{item.quantidade || item.qtde || 1}</td>
                              <td className="py-2 text-right">R$ {formatarMoeda(item.preco_unitario || item.preco)}</td>
                              <td className="py-2 text-right font-bold">R$ {formatarMoeda(item.total)}</td>
                            </tr>
                          ))}
                          <tr className="bg-slate-100 font-bold border-t-2">
                            <td colSpan="3" className="py-3">TOTAL</td>
                            <td className="py-3 text-right text-lg text-slate-700">
                              R$ {formatarMoeda(dadosCompletos.produtosEspeciais.reduce((acc, item) => acc + (parseFloat(item.total) || 0), 0))}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <div className="mt-4">
                        <div className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white text-center py-2 rounded-lg font-bold text-sm">
                          → VIÁVEL
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* SEÇÃO DE MÉTRICAS - Reorganizado igual NovoPedido */}
              {(temItensTab1 || temItensTab2) && (
                <div className="mt-8 pt-6 border-t-2 border-gray-100">
                  <div className={`grid gap-4 ${podeVerMetricas() ? 'grid-cols-3' : 'grid-cols-2'}`}>
                    {/* % de Acabamento - aparece para Admin, Diretoria, Vendas e Administrativo */}
                    {podeVerMetricas() && (
                    <div className="bg-white rounded-xl p-5 border-2 border-blue-100 shadow-md hover:shadow-lg hover:scale-[1.02] hover:border-blue-300 transition-all duration-300">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                          <BarChart3 size={16} className="text-white" />
                        </div>
                        <h4 className="text-sm font-bold text-blue-600 uppercase tracking-wide">% Acabamento</h4>
                      </div>
                      {(() => {
                        // Calcular valores de forro e acabamento
                        const totalForro = resumoTab1.filter(r => r.isForro).reduce((sum, r) => sum + r.total, 0) +
                                          resumoTab2.filter(r => r.isForro).reduce((sum, r) => sum + r.total, 0);
                        const totalAcabamento = resumoTab1.filter(r => !r.isForro).reduce((sum, r) => sum + r.total, 0) +
                                               resumoTab2.filter(r => !r.isForro).reduce((sum, r) => sum + r.total, 0);
                        const percentualAcabamento = totalForro > 0 ? (totalAcabamento / totalForro) * 100 : 0;
                        const metaAcabamento = 30;
                        const atingiuMeta = percentualAcabamento >= metaAcabamento;
                        
                        return (
                          <div className="space-y-3">
                            <div className="flex items-end justify-between">
                              <span className={`text-3xl font-black ${atingiuMeta ? 'text-emerald-600' : 'text-blue-600'}`}>
                                {percentualAcabamento.toFixed(1)}%
                              </span>
                              <span className="text-xs text-gray-400 font-medium">
                                meta <span className="font-bold text-gray-600">{metaAcabamento}%</span>
                              </span>
                            </div>
                            {/* Barra de progresso */}
                            <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                              <div 
                                className={`h-full rounded-full transition-all duration-500 ${atingiuMeta ? 'bg-gradient-to-r from-emerald-500 to-green-400' : 'bg-gradient-to-r from-blue-500 to-cyan-400'}`}
                                style={{ width: `${Math.min((percentualAcabamento / metaAcabamento) * 100, 100)}%` }}
                              ></div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div className="text-left">
                                <div className="font-bold text-gray-700">R$ {formatarMoeda(totalAcabamento)}</div>
                                <div className="text-gray-400 text-[10px] uppercase">Acabamento</div>
                              </div>
                              <div className="text-right">
                                <div className="font-bold text-gray-700">R$ {formatarMoeda(totalForro)}</div>
                                <div className="text-gray-400 text-[10px] uppercase">Forro</div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    )}

                    {/* Card PONTUAÇÃO + STATUS (juntos, centralizado verticalmente) */}
                    <div className="bg-white rounded-xl p-4 border-2 border-blue-100 shadow-md hover:shadow-lg hover:scale-[1.02] hover:border-blue-300 transition-all duration-300 flex items-center">
                      {(() => {
                        const totalVendaGeral = valorTotalTab1 + valorTotalTab2;
                        const _valProdEsp2 = parseFloat(dadosCompletos.valor_produtos_especiais) || 0;
                        
                        // Pontuação de cada tabela com cap de 50
                        const pontTab1 = temItensTab1 ? Math.min(pontuacaoTab1, 50) : 0;
                        const pontTab2 = temItensTab2 ? Math.min(pontuacaoTab2, 50) : 0;
                        
                        // Pontuação geral = SOMA das pontuações, também limitada a 50 no total
                        const pontuacaoGeral = Math.min(pontTab1 + pontTab2, 50);
                        
                        const statusGeral = (() => {
                          if (totalVendaGeral === 0 && _valProdEsp2 > 0) {
                            if (dadosCompletos.status_geral) return dadosCompletos.status_geral;
                            if (dadosCompletos.pontuacao_geral != null) return getStatusByScore(parseFloat(dadosCompletos.pontuacao_geral) || 0);
                          }
                          return totalVendaGeral === 0 ? 'INVIÁVEL' : getStatusByScore(pontuacaoGeral);
                        })();
                        const configGeral = getStatusConfig(statusGeral);
                        const IconeGeral = configGeral?.icon || AlertTriangle;
                        
                        return (
                          <div className="flex flex-col w-full justify-center">
                            {/* Pontuação Geral */}
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                                <BarChart3 size={14} className="text-white" />
                              </div>
                              <h4 className="text-sm font-bold text-blue-600 uppercase tracking-wide">Pontuação Geral</h4>
                              <span className={`text-2xl font-black ml-auto ${pontuacaoGeral >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                {pontuacaoGeral.toFixed(2).replace('.', ',')}
                              </span>
                            </div>
                            {/* Status */}
                            <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                              <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                                <IconeGeral size={14} className="text-white" />
                              </div>
                              <h4 className="text-sm font-bold text-blue-600 uppercase tracking-wide">Status</h4>
                              <div className="ml-auto">
                                <StatusBadge status={statusGeral} tamanho="md" />
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Card VALOR TOTAL DO PEDIDO */}
                    <div className="bg-white rounded-xl p-4 border-2 border-blue-100 shadow-md hover:shadow-lg hover:scale-[1.02] hover:border-blue-300 transition-all duration-300">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                          <DollarSign size={14} className="text-white" />
                        </div>
                        <h4 className="text-sm font-bold text-blue-600 uppercase tracking-wide">Valor Total</h4>
                      </div>
                      {(() => {
                        const totalProdutos = valorTotalTab1 + valorTotalTab2;
                        // Em modo edição, usar dadosEdicao; senão, usar dadosCompletos
                        const temTaxaEntregaAtual = modoEdicao ? dadosEdicao.tem_taxa_entrega : dadosCompletos.tem_taxa_entrega;
                        const valorTaxaEntregaAtual = modoEdicao ? dadosEdicao.valor_taxa_entrega : dadosCompletos.valor_taxa_entrega;
                        const taxaEntrega = temTaxaEntregaAtual ? (parseFloat(valorTaxaEntregaAtual) || 0) : 0;
                        const totalGeral = totalProdutos + taxaEntrega;
                        
                        return (
                          <div className="space-y-1">
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-500">Total Produtos</span>
                              <span className="text-lg font-bold text-gray-700">R$ {formatarMoeda(totalProdutos)}</span>
                            </div>
                            {taxaEntrega > 0 && (
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-gray-500">Taxa Entrega</span>
                                <span className="text-sm font-medium text-gray-600">R$ {formatarMoeda(taxaEntrega)}</span>
                              </div>
                            )}
                            {taxaEntrega > 0 ? (
                              <div className="flex justify-between items-center pt-1 border-t border-blue-200">
                                <span className="text-xs font-bold text-blue-600">TOTAL GERAL</span>
                                <span className="text-xl font-black text-blue-600">R$ {formatarMoeda(totalGeral)}</span>
                              </div>
                            ) : (
                              <div className="text-center pt-1">
                                <span className="text-2xl font-black text-blue-600">R$ {formatarMoeda(totalProdutos)}</span>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ========================================
              5. FORMAS DE PAGAMENTO
          ======================================== */}
          <div className="w-full max-w-6xl mx-auto mb-6">
            <div className="w-full h-px bg-gray-200 mb-6"></div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 group cursor-default">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                  <CreditCard size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Formas de Pagamento</h2>
                  <p className="text-xs text-gray-400">{modoEdicao ? 'Selecione até 4 formas de pagamento' : 'Condições de pagamento do pedido'}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-slate-100 to-gray-100 rounded-xl border border-slate-200">
                  <span className="text-sm text-slate-700 font-medium">Selecionadas:</span>
                  <span className="text-lg font-bold text-slate-600">{modoEdicao ? getFormasPagamentoSelecionadasEdicao() : formasPagamento.length}/4</span>
                </div>
                <button 
                  onClick={() => toggleExpandir('pagamento')} 
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-300"
                >
                  <ChevronDown size={20} className={`transition-transform duration-300 ${expandido.pagamento ? 'rotate-180' : ''}`} />
                  {expandido.pagamento ? 'Recolher' : 'Expandir'}
                </button>
              </div>
            </div>
          </div>

          {expandido.pagamento && (
            <div className={`w-full max-w-6xl mx-auto bg-white rounded-2xl shadow-xl p-8 border ${modoEdicao ? 'border-orange-300 ring-2 ring-orange-200' : 'border-gray-100'} mb-6 hover:shadow-2xl transition-all duration-300`}>
              {/* Modo Edição: Layout igual NovoPedido com cards e validação de observação */}
              {modoEdicao ? (
                <div className="space-y-4">
                  {getFormasPagamentoEdicao().map((fp, index) => {
                    const formaConfig = FORMAS_PAGAMENTO.find(f => f.label === fp.forma);
                    const obrigatorio = formaConfig?.exigeObservacao || false;
                    const faltaObservacao = obrigatorio && (!fp.observacao || fp.observacao.trim() === '');
                    
                    return (
                      <div 
                        key={index} 
                        className={`rounded-xl border-2 transition-all duration-300 ${
                          fp.forma 
                            ? 'border-orange-300 bg-gradient-to-r from-orange-50 to-amber-50 shadow-md' 
                            : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                        }`}
                      >
                        <div className="p-4">
                          <div className="flex items-center gap-4">
                            {/* Número da opção */}
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg shadow-sm ${
                              fp.forma 
                                ? 'bg-gradient-to-br from-orange-500 to-amber-500 text-white' 
                                : 'bg-gray-200 text-gray-500'
                            }`}>
                              {index + 1}
                            </div>

                            {/* Select da forma de pagamento */}
                            <div className="flex-1">
                              <select
                                value={fp.forma}
                                onChange={(e) => atualizarFormaPagamentoEdicao(index, 'forma', e.target.value)}
                                className={`w-full p-3 border-2 rounded-xl font-medium transition-all duration-300 ${
                                  fp.forma 
                                    ? 'border-orange-300 bg-white text-gray-800 focus:border-orange-500 focus:ring-2 focus:ring-orange-100' 
                                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-100'
                                }`}
                              >
                                <option value="">Selecione uma forma de pagamento...</option>
                                {FORMAS_PAGAMENTO.map((forma) => (
                                  <option key={forma.id} value={forma.label}>
                                    {forma.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* Campo de observação */}
                            <div className="flex-1">
                              <input
                                type="text"
                                value={fp.observacao}
                                onChange={(e) => atualizarFormaPagamentoEdicao(index, 'observacao', e.target.value)}
                                placeholder={obrigatorio ? "Observação obrigatória *" : "Observações (opcional)..."}
                                disabled={!fp.forma}
                                className={`w-full p-3 border-2 rounded-xl transition-all duration-300 ${
                                  !fp.forma
                                    ? 'bg-gray-100 cursor-not-allowed text-gray-400 border-gray-200' 
                                    : faltaObservacao
                                      ? 'border-red-300 bg-red-50 text-gray-700 hover:border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-100'
                                      : obrigatorio && fp.observacao
                                        ? 'border-green-300 bg-green-50 text-gray-700 hover:border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-100'
                                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-100'
                                }`}
                              />
                            </div>

                            {/* Botão limpar */}
                            {fp.forma && (
                              <button
                                onClick={() => limparFormaPagamentoEdicao(index)}
                                className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-300"
                                title="Limpar"
                              >
                                <X size={20} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Resumo das formas selecionadas em edição */}
                  {getFormasPagamentoSelecionadasEdicao() > 0 && (
                    <div className="mt-6 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200">
                      <h4 className="font-bold text-orange-700 mb-3 flex items-center gap-2">
                        <CheckCircle size={18} />
                        Formas de Pagamento Selecionadas
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {getFormasPagamentoEdicao().filter(f => f.forma).map((f, i) => (
                          <div key={i} className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-orange-200 shadow-sm">
                            <Wallet size={16} className="text-orange-600" />
                            <span className="font-medium text-gray-700">{f.forma}</span>
                            {f.observacao && (
                              <span className="text-xs text-gray-500 italic">({f.observacao})</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Aviso se nenhuma forma foi selecionada */}
                  {getFormasPagamentoSelecionadasEdicao() === 0 && (
                    <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                      <div className="flex items-center gap-3">
                        <AlertTriangle size={20} className="text-amber-600" />
                        <span className="text-amber-700 font-medium">Selecione pelo menos uma forma de pagamento para salvar o pedido.</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Modo Visualização: Somente leitura */
                formasPagamento.length > 0 ? (
                  <>
                    <div className="space-y-4">
                      {formasPagamento.map((fp, index) => (
                        <div 
                          key={index} 
                          className="rounded-xl border-2 border-blue-300 bg-gradient-to-r from-blue-50 to-cyan-50 shadow-md"
                        >
                          <div className="p-4">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg shadow-sm bg-gradient-to-br from-blue-500 to-cyan-500 text-white">
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <p className="font-bold text-gray-700">{fp.forma}</p>
                              </div>
                              {fp.observacao && (
                                <div className="flex-1">
                                  <p className="text-gray-500 italic text-sm">{fp.observacao}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Resumo das formas selecionadas */}
                    <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200">
                      <h4 className="font-bold text-blue-700 mb-3 flex items-center gap-2">
                        <CheckCircle size={18} />
                        Formas de Pagamento Selecionadas
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {formasPagamento.map((f, i) => (
                          <div key={i} className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-blue-200 shadow-sm">
                            <Wallet size={16} className="text-blue-600" />
                            <span className="font-medium text-gray-700">{f.forma}</span>
                            {f.observacao && (
                              <span className="text-xs text-gray-500 italic">({f.observacao})</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <CreditCard size={48} className="mx-auto mb-3 opacity-50" />
                    <p>Nenhuma forma de pagamento registrada</p>
                  </div>
                )
              )}
            </div>
          )}

          {/* ========================================
              5.1 ANÁLISE DE CRÉDITO (para formas parceladas)
          ======================================== */}
          {(() => {
            // Lista de formas parceladas (Cartão de Crédito NÃO é parcelado para a empresa, recebe à vista)
            const FORMAS_PARCELADAS = [
              'Boleto à Prazo', 'Boleto 7 dias', 'Boleto 14 dias', 'Boleto 21 dias', 'Boleto 28 dias', 
              'Boleto 30 dias', 'Boleto 30/60 dias', 'Boleto 30/60/90 dias', 'Boleto 30/60/90/120 dias',
              'Cheque à Prazo', 'Cheque 30 dias', 'Entrada + Parcelas'
            ];
            const temFormaPagamentoParcelada = formasPagamento.some(fp => FORMAS_PARCELADAS.includes(fp.forma));
            
            // Só mostrar se tiver forma de pagamento parcelada
            if (!temFormaPagamentoParcelada) return null;
            
            const percentualLimite = parseFloat(dadosCompletos.percentual_limite_credito) || 0;
            const limiteCredito = parseFloat(cliente.limite_credito) || 0;
            const extrapolouLimite = dadosCompletos.extrapolou_limite_credito;
            const valorTotalPedido = (parseFloat(dadosCompletos.valor_tabela1) || 0) + (parseFloat(dadosCompletos.valor_tabela2) || 0);
            
            return (
              <>
                <div className="w-full max-w-6xl mx-auto mb-6">
                  <div className="w-full h-px bg-gray-200 mb-6"></div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 group cursor-default">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                        <BarChart3 size={20} className="text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Análise de Crédito</h2>
                        <p className="text-xs text-gray-400">Avaliação do limite de crédito do cliente</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className={`flex items-center gap-2 px-4 py-2 rounded-xl border ${
                        extrapolouLimite 
                          ? 'bg-gradient-to-r from-red-100 to-orange-100 border-red-200' 
                          : limiteCredito === 0 
                            ? 'bg-gradient-to-r from-amber-100 to-yellow-100 border-amber-200'
                            : 'bg-gradient-to-r from-green-100 to-emerald-100 border-green-200'
                      }`}>
                        {extrapolouLimite ? (
                          <><AlertTriangle size={16} className="text-red-600" /><span className="text-sm text-red-700 font-medium">Limite Excedido</span></>
                        ) : limiteCredito === 0 ? (
                          <><AlertTriangle size={16} className="text-amber-600" /><span className="text-sm text-amber-700 font-medium">Sem Limite</span></>
                        ) : (
                          <><CheckCircle size={16} className="text-green-600" /><span className="text-sm text-green-700 font-medium">OK</span></>
                        )}
                      </div>
                      <button 
                        onClick={() => toggleExpandir('analiseCredito')} 
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-300"
                      >
                        <ChevronDown size={20} className={`transition-transform duration-300 ${expandido.analiseCredito ? 'rotate-180' : ''}`} />
                        {expandido.analiseCredito ? 'Recolher' : 'Expandir'}
                      </button>
                    </div>
                  </div>
                </div>

                {expandido.analiseCredito && (
                  <div className="w-full max-w-6xl mx-auto bg-white rounded-2xl shadow-xl p-8 border border-gray-100 mb-6 hover:shadow-2xl transition-all duration-300">
                    <div className="space-y-6">
                      {/* Cards de Informações */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {/* Porte do Cliente */}
                        <div className="rounded-xl border-2 border-gray-200 bg-gray-50 p-4 hover:shadow-md hover:scale-105 transition-all duration-300">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gray-200 flex items-center justify-center">
                              <Building2 size={18} className="text-gray-500" />
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 uppercase font-medium">Porte do Cliente</div>
                              <div className="text-lg font-bold text-gray-800">{cliente.porte || 'Não definido'}</div>
                            </div>
                          </div>
                        </div>

                        {/* Limite de Crédito */}
                        <div className={`rounded-xl border-2 p-4 hover:shadow-md hover:scale-105 transition-all duration-300 ${
                          limiteCredito > 0 
                            ? 'border-green-300 bg-gradient-to-r from-green-50 to-emerald-50' 
                            : 'border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50'
                        }`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                              limiteCredito > 0 
                                ? 'bg-gradient-to-br from-green-500 to-emerald-500' 
                                : 'bg-gradient-to-br from-amber-500 to-yellow-500'
                            }`}>
                              <Wallet size={18} className="text-white" />
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 uppercase font-medium">Limite de Crédito</div>
                              <div className={`text-lg font-bold ${limiteCredito > 0 ? 'text-green-600' : 'text-amber-600'}`}>
                                R$ {formatarMoeda(limiteCredito)}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Valor do Pedido */}
                        <div className="rounded-xl border-2 border-blue-300 bg-gradient-to-r from-blue-50 to-cyan-50 p-4 hover:shadow-md hover:scale-105 transition-all duration-300">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                              <DollarSign size={18} className="text-white" />
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 uppercase font-medium">Valor do Pedido</div>
                              <div className="text-lg font-bold text-blue-600">R$ {formatarMoeda(valorTotalPedido)}</div>
                            </div>
                          </div>
                        </div>

                        {/* % do Limite */}
                        <div className={`rounded-xl border-2 p-4 hover:shadow-md hover:scale-105 transition-all duration-300 ${
                          percentualLimite > 150 
                            ? 'border-red-300 bg-gradient-to-r from-red-50 to-orange-50' 
                            : percentualLimite > 100
                              ? 'border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50'
                              : 'border-blue-300 bg-gradient-to-r from-blue-50 to-cyan-50'
                        }`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                              percentualLimite > 150 
                                ? 'bg-gradient-to-br from-red-500 to-orange-500' 
                                : percentualLimite > 100
                                  ? 'bg-gradient-to-br from-amber-500 to-yellow-500'
                                  : 'bg-gradient-to-br from-blue-500 to-cyan-500'
                            }`}>
                              <TrendingUp size={18} className="text-white" />
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 uppercase font-medium">% do Limite</div>
                              <div className={`text-lg font-bold ${
                                percentualLimite > 150 
                                  ? 'text-red-600' 
                                  : percentualLimite > 100
                                    ? 'text-amber-600'
                                    : 'text-blue-600'
                              }`}>
                                {percentualLimite.toFixed(1)}%
                              </div>
                              <div className="text-xs text-gray-400">
                                {percentualLimite > 150 ? 'Limite excedido!' : percentualLimite > 100 ? 'Acima do limite' : 'Dentro do limite'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Alerta de Status */}
                      {extrapolouLimite && (
                        <div className="p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border border-red-200 hover:shadow-md transition-all duration-300">
                          <div className="flex items-center gap-3">
                            <AlertTriangle size={20} className="text-red-600" />
                            <div>
                              <span className="text-red-700 font-medium">Atenção: Ultrapassou 150% do Limite!</span>
                              <span className="text-red-600 ml-2">
                                A média está em <strong>{percentualLimite.toFixed(1)}%</strong> do limite de crédito
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {!extrapolouLimite && limiteCredito > 0 && percentualLimite > 100 && (
                        <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200 hover:shadow-md transition-all duration-300">
                          <div className="flex items-center gap-3">
                            <AlertTriangle size={20} className="text-amber-600" />
                            <span className="text-amber-700 font-medium">Acima do Limite - A média está em {percentualLimite.toFixed(1)}% do limite (tolerância até 150%).</span>
                          </div>
                        </div>
                      )}

                      {!extrapolouLimite && limiteCredito > 0 && percentualLimite <= 100 && (
                        <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 hover:shadow-md transition-all duration-300">
                          <div className="flex items-center gap-3">
                            <CheckCircle size={20} className="text-green-600" />
                            <span className="text-green-700 font-medium">Dentro do Limite de Crédito - A média está em {percentualLimite.toFixed(1)}% do limite aprovado.</span>
                          </div>
                        </div>
                      )}

                      {limiteCredito === 0 && (
                        <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200 hover:shadow-md transition-all duration-300">
                          <div className="flex items-center gap-3">
                            <AlertTriangle size={20} className="text-amber-600" />
                            <span className="text-amber-700 font-medium">Limite de Crédito Não Definido - Considere definir um limite antes de aprovar vendas parceladas.</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          {/* ========================================
              6. VISÃO ADMINISTRATIVA (com regra de cargo)
          ======================================== */}
          {podeVerVisaoAdmin() && (
            <>
              <div className="w-full max-w-6xl mx-auto mb-6">
                <div className="w-full h-px bg-gray-200 mb-6"></div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 group cursor-default">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                      <BarChart3 size={20} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Visão Administrativa</h2>
                      <p className="text-xs text-gray-400">Análise de custos e rentabilidade</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => toggleExpandir('admin')} 
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-300"
                  >
                    <ChevronDown size={20} className={`transition-transform duration-300 ${expandido.admin ? 'rotate-180' : ''}`} />
                    {expandido.admin ? 'Recolher' : 'Expandir'}
                  </button>
                </div>
              </div>

              {expandido.admin && (
                <div className="w-full max-w-6xl mx-auto bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl shadow-xl p-8 border border-slate-200 mb-6 hover:shadow-2xl hover:scale-[1.005] hover:-translate-y-1 transition-all duration-300">
                  <div className="space-y-8">
                    {(() => {
                      const estadoCliente = dadosCompletos?.clientes?.estado || 'Parana';

                      const acessorioPertenceAoForroAdmin = (acessorio, forro) => {
                        const aForroId = acessorio.forroId !== undefined && acessorio.forroId !== null ? String(acessorio.forroId) : null;
                        const aForroIdOriginal = acessorio.forroIdOriginal !== undefined && acessorio.forroIdOriginal !== null ? String(acessorio.forroIdOriginal) : null;
                        const aVinculoForroId = acessorio.vinculoForroId !== undefined && acessorio.vinculoForroId !== null ? String(acessorio.vinculoForroId) : null;
                        const forroId = String(forro.id);
                        const forroIdOriginal = forro.idOriginal !== undefined && forro.idOriginal !== null ? String(forro.idOriginal) : null;

                        return (
                          (aVinculoForroId && aVinculoForroId === forroId) ||
                          (aForroId && (aForroId === forroId || (forroIdOriginal && aForroId === forroIdOriginal))) ||
                          (aForroIdOriginal && (aForroIdOriginal === forroId || (forroIdOriginal && aForroIdOriginal === forroIdOriginal)))
                        );
                      };

                      const buildAdminResumoEdicao = (tabela) => {
                        if (!modoEdicao) return tabela === 'Tabela 1' ? resumoTab1 : resumoTab2;
                        const forros = forrosEdicao.filter(f => f.tabela === tabela && f.produto);
                        const acessorios = acessoriosEdicao.filter(a => a.tabela === tabela && a.produto);

                        // ========================================
                        // AGRUPAR FORROS POR FAMÍLIA
                        // ========================================
                        const gruposFamilia = {};
                        
                        forros.forEach(forro => {
                          const familia = forro.familia || forro.produto;
                          
                          if (!gruposFamilia[familia]) {
                            gruposFamilia[familia] = {
                              familia: familia,
                              m2Total: 0,
                              totalVenda: 0,
                              custoTotal: 0,
                              margemExigida: getMargemExigida(familia, estadoCliente, tabela) || 0,
                              forroIds: [],
                              forroIdsOriginais: [],
                              acabamentos: []
                            };
                          }
                          
                          const m2 = parseFloat(forro.m2) || 0;
                          const total = parseFloat(forro.total) || 0;
                          const custoUnit = parseFloat(forro.custo) || 0;
                          
                          gruposFamilia[familia].m2Total += m2;
                          gruposFamilia[familia].totalVenda += total;
                          gruposFamilia[familia].custoTotal += m2 * custoUnit;
                          gruposFamilia[familia].forroIds.push(forro.id);
                          if (forro.idOriginal !== undefined && forro.idOriginal !== null) {
                            gruposFamilia[familia].forroIdsOriginais.push(forro.idOriginal);
                          }
                        });
                        
                        // Vincular acessórios aos grupos de família
                        acessorios.forEach(acess => {
                          // Encontrar a família do forro pai
                          let familiaPai = null;
                          for (const forro of forros) {
                            if (acessorioPertenceAoForroAdmin(acess, forro)) {
                              familiaPai = forro.familia || forro.produto;
                              break;
                            }
                          }
                          
                          // Fallback: usar primeira família
                          if (!familiaPai && Object.keys(gruposFamilia).length > 0) {
                            familiaPai = Object.keys(gruposFamilia)[0];
                          }
                          
                          if (familiaPai && gruposFamilia[familiaPai]) {
                            gruposFamilia[familiaPai].acabamentos.push({
                              produto: acess.produto,
                              preco: parseFloat(acess.preco) || 0,
                              qtde: parseFloat(acess.quantidade) || 0,
                              total: parseFloat(acess.total) || 0,
                              custo: parseFloat(acess.custo) || 0
                            });
                          }
                        });
                        
                        // Famílias que têm pontuação dividida por 3 (igual NovoPedido)
                        const FAMILIAS_PONTUACAO_DIVIDIDA = ['Forro 8 mm', 'Forro Colorido'];
                        
                        // Calcular pontuação por família (incluindo acabamentos no total da família)
                        Object.values(gruposFamilia).forEach(grupo => {
                          const totalAcab = grupo.acabamentos.reduce((s, a) => s + (a.total || 0), 0);
                          const custoAcab = grupo.acabamentos.reduce((s, a) => s + ((a.qtde || 0) * (a.custo || 0)), 0);
                          const totalGrupo = grupo.totalVenda + totalAcab;
                          const custoGrupo = grupo.custoTotal + custoAcab;
                          const lucroGrupo = totalGrupo - custoGrupo;
                          const margemAtingidaGrupo = totalGrupo > 0 ? (lucroGrupo / totalGrupo) * 100 : 0;
                          let pontuacaoGrupo = margemAtingidaGrupo - (grupo.margemExigida || 0);
                          if (FAMILIAS_PONTUACAO_DIVIDIDA.includes(grupo.familia)) {
                            pontuacaoGrupo = pontuacaoGrupo / 3;
                          }
                          grupo.pontuacaoLimitada = Math.min(pontuacaoGrupo, 50);
                        });
                        
                        // Construir linhas de resumo
                        const linhas = [];
                        
                        Object.values(gruposFamilia).forEach(grupo => {
                          const lucro = grupo.totalVenda - grupo.custoTotal;
                          const margemAtingida = grupo.totalVenda > 0 ? (lucro / grupo.totalVenda) * 100 : 0;
                          const margemExigidaReais = grupo.totalVenda * (grupo.margemExigida / 100);
                          const diferencaReais = lucro - margemExigidaReais;
                          const precoMedio = grupo.m2Total > 0 ? grupo.totalVenda / grupo.m2Total : 0;
                          const custoMedio = grupo.m2Total > 0 ? grupo.custoTotal / grupo.m2Total : 0;
                          
                          // Adicionar linha do forro agrupado
                          linhas.push({
                            produto: grupo.familia,
                            preco: precoMedio,
                            qtdade: grupo.m2Total,
                            total: grupo.totalVenda,
                            custoUnit: custoMedio,
                            custoTotal: grupo.custoTotal,
                            margemExigida: grupo.margemExigida,
                            isForro: true,
                            isAcabamento: false,
                            lucro,
                            margemAtingida,
                            margemExigidaReais,
                            diferencaReais,
                            pontuacao: grupo.pontuacaoLimitada
                          });
                          
                          // Agrupar acabamentos por produto
                          const acabamentosAgrupados = {};
                          grupo.acabamentos.forEach(acess => {
                            if (!acabamentosAgrupados[acess.produto]) {
                              acabamentosAgrupados[acess.produto] = {
                                produto: acess.produto,
                                preco: acess.preco,
                                custo: acess.custo,
                                qtde: 0,
                                total: 0,
                                custoTotal: 0
                              };
                            }
                            acabamentosAgrupados[acess.produto].qtde += acess.qtde;
                            acabamentosAgrupados[acess.produto].total += acess.total;
                            acabamentosAgrupados[acess.produto].custoTotal += acess.qtde * acess.custo;
                          });
                          
                          // Adicionar acabamentos agrupados
                          Object.values(acabamentosAgrupados).forEach(acessAgrup => {
                            const lucroA = acessAgrup.total - acessAgrup.custoTotal;
                            const margemAtingidaA = acessAgrup.total > 0 ? (lucroA / acessAgrup.total) * 100 : 0;
                            const margemExigidaReaisA = acessAgrup.total * (grupo.margemExigida / 100);
                            const diferencaReaisA = lucroA - margemExigidaReaisA;
                            
                            linhas.push({
                              produto: acessAgrup.produto,
                              preco: acessAgrup.preco,
                              qtdade: acessAgrup.qtde,
                              total: acessAgrup.total,
                              custoUnit: acessAgrup.custo,
                              custoTotal: acessAgrup.custoTotal,
                              margemExigida: grupo.margemExigida,
                              isForro: false,
                              isAcabamento: true,
                              lucro: lucroA,
                              margemAtingida: margemAtingidaA,
                              margemExigidaReais: margemExigidaReaisA,
                              diferencaReais: diferencaReaisA,
                              pontuacao: grupo.pontuacaoLimitada
                            });
                          });
                        });

                        return linhas;
                      };

                      const adminResumoTab1 = buildAdminResumoEdicao('Tabela 1');
                      const adminResumoTab2 = buildAdminResumoEdicao('Tabela 2');

                      return (
                        <>
                          {/* Tabela 1 Admin */}
                          {temItensTab1 && (
                            <div className="bg-white rounded-2xl overflow-hidden border-2 border-slate-300 shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all duration-300">
                              <div style={{backgroundColor: '#0d5dfc'}} className="p-6 flex items-center justify-between flex-wrap gap-3">
                                <div className="flex items-center gap-3"><div className="w-1 h-8 bg-white/30 rounded-full"></div><h3 className="text-2xl font-bold text-white">TABELA 1</h3></div>
                                <div className="flex gap-3 text-white text-sm flex-wrap">
                                  <div className="text-right bg-white/10 px-3 py-2 rounded-lg hover:bg-white/20 transition-colors duration-200"><div className="text-white/80 text-[10px] uppercase">Total Venda</div><div className="text-base font-bold">R$ {formatarMoeda(valorTotalTab1)}</div></div>
                                  <div className="text-right bg-white/10 px-3 py-2 rounded-lg hover:bg-white/20 transition-colors duration-200"><div className="text-white/80 text-[10px] uppercase">Lucro</div><div className="text-base font-bold">R$ {formatarMoeda(lucroTab1)}</div></div>
                                  <div className="text-right bg-white/10 px-3 py-2 rounded-lg hover:bg-white/20 transition-colors duration-200"><div className="text-white/80 text-[10px] uppercase">Margem</div><div className="text-base font-bold">{margemTab1.toFixed(2).replace('.', ',')}%</div></div>
                                  <div className="text-right bg-white/10 px-3 py-2 rounded-lg hover:bg-white/20 transition-colors duration-200"><div className="text-white/80 text-[10px] uppercase">Pontuação</div><div className="text-base font-bold">{pontuacaoTab1.toFixed(2).replace('.', ',')}</div></div>
                                </div>
                              </div>
                              <div className="p-6 bg-slate-50">
                                {adminResumoTab1.length > 0 ? (
                                  <div className="overflow-x-auto bg-white rounded-xl border border-slate-200 shadow-sm">
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="bg-slate-100 text-slate-700 border-b-2 border-slate-300">
                                          <th className="py-4 px-3 text-left font-bold uppercase text-xs whitespace-nowrap">Produto</th>
                                          <th className="py-4 px-3 text-right font-bold uppercase text-xs whitespace-nowrap">Preço</th>
                                          <th className="py-4 px-3 text-right font-bold uppercase text-xs whitespace-nowrap">QTD/M²</th>
                                          <th className="py-4 px-3 text-right font-bold uppercase text-xs whitespace-nowrap">Total Venda</th>
                                          <th className="py-4 px-3 text-right font-bold uppercase text-xs whitespace-nowrap">Custo Un.</th>
                                          <th className="py-4 px-3 text-right font-bold uppercase text-xs whitespace-nowrap">Custo Total</th>
                                          <th className="py-4 px-3 text-right font-bold uppercase text-xs whitespace-nowrap">Lucro</th>
                                          <th className="py-4 px-3 text-right font-bold uppercase text-xs whitespace-nowrap">Margem</th>
                                          <th className="py-4 px-3 text-right font-bold uppercase text-xs whitespace-nowrap">Pontuação</th>
                                        </tr>
                                      </thead>
                                      <tbody className="text-slate-700">
                                        {adminResumoTab1.map((r, i) => {
                                          const lucroItem = r.lucro || 0;
                                          const margemAtingida = r.margemAtingida || 0;
                                          const pontuacao = r.pontuacao !== undefined ? r.pontuacao : 0;
                                          return (
                                            <tr key={i} className="border-b border-slate-200 hover:bg-blue-50 transition-colors duration-200">
                                              <td className="py-4 px-3 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                  {r.isAcabamento ? (
                                                    <span className="text-gray-400 text-sm font-bold ml-2">↳</span>
                                                  ) : (
                                                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                                                  )}
                                                  <span className={`font-semibold ${r.isAcabamento ? 'text-slate-600' : 'text-slate-800'}`}>{limparNomeProduto(r.produto)}</span>
                                                </div>
                                              </td>
                                              <td className="py-4 px-3 text-right text-slate-600 whitespace-nowrap">R$ {formatarMoeda(r.preco)}</td>
                                              <td className="py-4 px-3 text-right text-slate-600 whitespace-nowrap">{(r.qtdade || 0).toFixed(2).replace('.', ',')}</td>
                                              <td className="py-4 px-3 text-right font-bold text-slate-800 whitespace-nowrap">R$ {formatarMoeda(r.total)}</td>
                                              <td className="py-4 px-3 text-right text-slate-500 whitespace-nowrap">R$ {formatarMoeda(r.custoUnit)}</td>
                                              <td className="py-4 px-3 text-right text-slate-500 whitespace-nowrap">R$ {formatarMoeda(r.custoTotal)}</td>
                                              <td className="py-4 px-3 text-right font-bold whitespace-nowrap">{lucroItem >= 0 ? <span className="text-emerald-600">R$ {formatarMoeda(lucroItem)}</span> : <span className="text-red-600">R$ {formatarMoeda(lucroItem)}</span>}</td>
                                              <td className="py-4 px-3 text-right font-bold whitespace-nowrap">{margemAtingida >= 0 ? <span className="text-emerald-600">{margemAtingida.toFixed(2).replace('.', ',')}%</span> : <span className="text-red-600">{margemAtingida.toFixed(2).replace('.', ',')}%</span>}</td>
                                              <td className="py-4 px-3 text-right font-bold whitespace-nowrap">
                                                {pontuacao >= 0 
                                                  ? <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700"><TrendingUp size={14} />+{pontuacao.toFixed(2).replace('.', ',')}</span>
                                                  : <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700"><TrendingDown size={14} />{pontuacao.toFixed(2).replace('.', ',')}</span>
                                                }
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (<div className="text-center py-12 bg-white rounded-xl border border-slate-200"><p className="text-slate-500 text-lg font-medium">Nenhum item</p></div>)}
                              </div>
                            </div>
                          )}

                          {/* Tabela 2 Admin - Só aparece se tiver itens */}
                          {temItensTab2 && adminResumoTab2.length > 0 && (
                            <div className="bg-white rounded-2xl overflow-hidden border-2 border-slate-300 shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all duration-300">
                              <div style={{backgroundColor: '#00BEFF'}} className="p-6 flex items-center justify-between flex-wrap gap-3">
                                <div className="flex items-center gap-3"><div className="w-1 h-8 bg-white/30 rounded-full"></div><h3 className="text-2xl font-bold text-white">TABELA 2</h3></div>
                                <div className="flex gap-3 text-white text-sm flex-wrap">
                                  <div className="text-right bg-white/10 px-3 py-2 rounded-lg hover:bg-white/20 transition-colors duration-200"><div className="text-white/80 text-[10px] uppercase">Total Venda</div><div className="text-base font-bold">R$ {formatarMoeda(valorTotalTab2)}</div></div>
                                  <div className="text-right bg-white/10 px-3 py-2 rounded-lg hover:bg-white/20 transition-colors duration-200"><div className="text-white/80 text-[10px] uppercase">Lucro</div><div className="text-base font-bold">R$ {formatarMoeda(lucroTab2)}</div></div>
                                  <div className="text-right bg-white/10 px-3 py-2 rounded-lg hover:bg-white/20 transition-colors duration-200"><div className="text-white/80 text-[10px] uppercase">Margem</div><div className="text-base font-bold">{margemTab2.toFixed(2).replace('.', ',')}%</div></div>
                                  <div className="text-right bg-white/10 px-3 py-2 rounded-lg hover:bg-white/20 transition-colors duration-200"><div className="text-white/80 text-[10px] uppercase">Pontuação</div><div className="text-base font-bold">{pontuacaoTab2.toFixed(2).replace('.', ',')}</div></div>
                                </div>
                              </div>
                              <div className="p-6 bg-slate-50">
                                <div className="overflow-x-auto bg-white rounded-xl border border-slate-200 shadow-sm">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="bg-slate-100 text-slate-700 border-b-2 border-slate-300">
                                        <th className="py-4 px-3 text-left font-bold uppercase text-xs whitespace-nowrap">Produto</th>
                                        <th className="py-4 px-3 text-right font-bold uppercase text-xs whitespace-nowrap">Preço</th>
                                        <th className="py-4 px-3 text-right font-bold uppercase text-xs whitespace-nowrap">QTD/M²</th>
                                        <th className="py-4 px-3 text-right font-bold uppercase text-xs whitespace-nowrap">Total Venda</th>
                                        <th className="py-4 px-3 text-right font-bold uppercase text-xs whitespace-nowrap">Custo Un.</th>
                                        <th className="py-4 px-3 text-right font-bold uppercase text-xs whitespace-nowrap">Custo Total</th>
                                        <th className="py-4 px-3 text-right font-bold uppercase text-xs whitespace-nowrap">Lucro</th>
                                        <th className="py-4 px-3 text-right font-bold uppercase text-xs whitespace-nowrap">Margem</th>
                                        <th className="py-4 px-3 text-right font-bold uppercase text-xs whitespace-nowrap">Pontuação</th>
                                      </tr>
                                    </thead>
                                    <tbody className="text-slate-700">
                                      {adminResumoTab2.map((r, i) => {
                                        const lucroItem = r.lucro || 0;
                                        const margemAtingida = r.margemAtingida || 0;
                                        const pontuacao = r.pontuacao !== undefined ? r.pontuacao : 0;
                                        return (
                                          <tr key={i} className="border-b border-slate-200 hover:bg-cyan-50 transition-colors duration-200">
                                            <td className="py-4 px-3 whitespace-nowrap">
                                              <div className="flex items-center gap-2">
                                                {r.isAcabamento ? (
                                                  <span className="text-gray-400 text-sm font-bold ml-2">↳</span>
                                                ) : (
                                                  <div className="w-2 h-2 rounded-full bg-cyan-600"></div>
                                                )}
                                                <span className={`font-semibold ${r.isAcabamento ? 'text-slate-600' : 'text-slate-800'}`}>{limparNomeProduto(r.produto)}</span>
                                              </div>
                                            </td>
                                            <td className="py-4 px-3 text-right text-slate-600 whitespace-nowrap">R$ {formatarMoeda(r.preco)}</td>
                                            <td className="py-4 px-3 text-right text-slate-600 whitespace-nowrap">{(r.qtdade || 0).toFixed(2).replace('.', ',')}</td>
                                            <td className="py-4 px-3 text-right font-bold text-slate-800 whitespace-nowrap">R$ {formatarMoeda(r.total)}</td>
                                            <td className="py-4 px-3 text-right text-slate-500 whitespace-nowrap">R$ {formatarMoeda(r.custoUnit)}</td>
                                            <td className="py-4 px-3 text-right text-slate-500 whitespace-nowrap">R$ {formatarMoeda(r.custoTotal)}</td>
                                            <td className="py-4 px-3 text-right font-bold whitespace-nowrap">{lucroItem >= 0 ? <span className="text-emerald-600">R$ {formatarMoeda(lucroItem)}</span> : <span className="text-red-600">R$ {formatarMoeda(lucroItem)}</span>}</td>
                                            <td className="py-4 px-3 text-right font-bold whitespace-nowrap">{margemAtingida >= 0 ? <span className="text-emerald-600">{margemAtingida.toFixed(2).replace('.', ',')}%</span> : <span className="text-red-600">{margemAtingida.toFixed(2).replace('.', ',')}%</span>}</td>
                                            <td className="py-4 px-3 text-right font-bold whitespace-nowrap">
                                              {pontuacao >= 0 
                                                ? <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700"><TrendingUp size={14} />+{pontuacao.toFixed(2).replace('.', ',')}</span>
                                                : <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700"><TrendingDown size={14} />{pontuacao.toFixed(2).replace('.', ',')}</span>
                                              }
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}

                    {/* RESUMO TOTAL DO PEDIDO */}
                    {(temItensTab1 || temItensTab2) && (
                      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl overflow-hidden border-2 border-blue-400 shadow-xl">
                        <div className="p-6">
                          <div className="flex items-center justify-between flex-wrap gap-4">
                            <div className="flex items-center gap-3">
                              <div className="w-1 h-8 bg-white/30 rounded-full"></div>
                              <h3 className="text-2xl font-bold text-white">RESUMO DO PEDIDO</h3>
                            </div>
                            {(() => {
                              const totalVendaGeral = valorTotalTab1 + valorTotalTab2;
                              const lucroGeral = lucroTab1 + lucroTab2;
                              
                              // Pontuação de cada tabela com cap de 50
                              const pontTab1 = valorTotalTab1 > 0 ? Math.min(pontuacaoTab1, 50) : 0;
                              const pontTab2 = valorTotalTab2 > 0 ? Math.min(pontuacaoTab2, 50) : 0;
                              
                              // Pontuação geral = SOMA das pontuações (cada uma limitada a 50)
                              const pontuacaoGeral = pontTab1 + pontTab2;
                              const _valProdEsp3 = parseFloat(dadosCompletos.valor_produtos_especiais) || 0;
                              const statusGeral = (() => {
                                if (totalVendaGeral === 0 && _valProdEsp3 > 0) {
                                  if (dadosCompletos.status_geral) return dadosCompletos.status_geral;
                                  if (dadosCompletos.pontuacao_geral != null) return getStatusByScore(parseFloat(dadosCompletos.pontuacao_geral) || 0);
                                }
                                return totalVendaGeral === 0 ? 'INVIÁVEL' : getStatusByScore(pontuacaoGeral);
                              })();
                              const configGeral = getStatusConfig(statusGeral);
                              
                              return (
                                <div className="flex gap-3 text-white text-sm flex-wrap">
                                  <div className="text-right bg-white/10 px-3 py-2 rounded-xl hover:bg-white/20 transition-colors duration-200">
                                    <div className="text-white/80 text-[10px] uppercase">Total Venda</div>
                                    <div className="text-lg font-bold">R$ {formatarMoeda(totalVendaGeral)}</div>
                                  </div>
                                  <div className="text-right bg-white/10 px-3 py-2 rounded-xl hover:bg-white/20 transition-colors duration-200">
                                    <div className="text-white/80 text-[10px] uppercase">Lucro Total</div>
                                    <div className="text-lg font-bold">R$ {formatarMoeda(lucroGeral)}</div>
                                  </div>
                                  <div className="text-right bg-white/10 px-3 py-2 rounded-xl hover:bg-white/20 transition-colors duration-200">
                                    <div className="text-white/80 text-[10px] uppercase">Margem</div>
                                    <div className="text-lg font-bold">{totalVendaGeral > 0 ? ((lucroGeral / totalVendaGeral) * 100).toFixed(2).replace('.', ',') : '0,00'}%</div>
                                  </div>
                                  <div className="text-right bg-white/10 px-3 py-2 rounded-xl hover:bg-white/20 transition-colors duration-200">
                                    <div className="text-white/80 text-[10px] uppercase">Pontuação</div>
                                    <div className="text-lg font-bold">{pontuacaoGeral.toFixed(2).replace('.', ',')}</div>
                                  </div>
                                  <div className={`flex items-center justify-center gap-2 px-6 py-2 rounded-xl bg-gradient-to-r ${configGeral.bgGradient}`}>
                                    {configGeral.icon && <configGeral.icon size={20} className="text-white" />}
                                    <div className="text-lg font-black text-white">{statusGeral}</div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ========================================
              7. OBSERVAÇÃO
          ======================================== */}
          <div className="w-full max-w-6xl mx-auto mb-6">
            <div className="w-full h-px bg-gray-200 mb-6"></div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 group cursor-default">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                  <FileText size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Observação</h2>
                  <p className="text-xs text-gray-400">Detalhes e anotações do pedido</p>
                </div>
              </div>
              <button 
                onClick={() => toggleExpandir('observacao')} 
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-300"
              >
                <ChevronDown size={20} className={`transition-transform duration-300 ${expandido.observacao ? 'rotate-180' : ''}`} />
                {expandido.observacao ? 'Recolher' : 'Expandir'}
              </button>
            </div>
          </div>

          {expandido.observacao && (
            <div className={`w-full max-w-6xl mx-auto bg-white rounded-2xl shadow-xl p-6 border ${modoEdicao ? 'border-orange-300 ring-2 ring-orange-200' : 'border-gray-100'} mb-6 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300`}>
              {/* Modo Edição: Campo editável */}
              {modoEdicao ? (
                <div>
                  <label className="block text-sm font-bold text-orange-600 uppercase tracking-wide mb-3">
                    Observação do Pedido
                  </label>
                  <textarea
                    value={dadosEdicao.observacao_pedido}
                    onChange={(e) => setDadosEdicao(prev => ({ ...prev, observacao_pedido: e.target.value }))}
                    className="w-full p-4 border-2 border-gray-200 rounded-xl h-32 resize-none focus:border-orange-400 focus:outline-none"
                    placeholder="Observações gerais sobre o pedido..."
                  />
                </div>
              ) : (
                /* Modo Visualização: Somente leitura */
                dadosCompletos.observacao_pedido ? (
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4">
                    <p className="text-gray-700">{dadosCompletos.observacao_pedido}</p>
                  </div>
                ) : (
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4 text-center">
                    <p className="text-gray-400 italic">Nenhuma observação registrada para este pedido.</p>
                  </div>
                )
              )}
            </div>
          )}

          {/* ========================================
              8. CARDS DE TABELA 1 E TABELA 2
          ======================================== */}
          <div className="w-full max-w-6xl mx-auto mb-6">
            <div className="w-full h-px bg-gray-200 mb-6"></div>

            {/* ========================================
                9. TABELA 1 E TABELA 2 (Cards de resumo final)
            ======================================== */}
            
<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Tabela 1 */}
              {temItensTab1 && (
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300">
                  {/* Banner do tipo de documento */}
                  <div className="bg-slate-400 text-white text-center py-2 font-bold text-sm uppercase tracking-wider">
                    {dadosCompletos.tipo_documento === 'PEDIDO' ? 'Pedido' : 'Orçamento'}
                  </div>
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white" style={{backgroundColor: '#0d5dfc'}}>1</div>
                        <h3 className="font-bold text-slate-800 text-lg">Tabela 1</h3>
                      </div>
                      {statusTab1 && <StatusBadge status={statusTab1} tamanho="xs" />}
                    </div>
                    
                    {/* Valores - Valor Total, Frete e Total Geral */}
                    {(() => {
                      const taxaEntrega = dadosCompletos.tem_taxa_entrega ? (parseFloat(dadosCompletos.valor_taxa_entrega) || 0) : 0;
                      const totalGeral = valorTotalTab1 + taxaEntrega;
                      // Mostrar taxa de entrega para Vendas, Diretoria, Admin, Administrativo
                      const dep = (usuarioLogado?.departamento || '').toLowerCase();
                      const mostrarTaxaEntrega = dep === 'vendas' || dep === 'diretoria' || dep === 'admin' || dep === 'administrativo';
                      
                      return (
                        <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 mb-4">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500 text-sm font-medium">Valor Total</span>
                            <span className="text-2xl font-bold text-slate-800">R$ {formatarMoeda(valorTotalTab1)}</span>
                          </div>
                          {mostrarTaxaEntrega && taxaEntrega > 0 && (
                            <>
                              <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200">
                                <span className="text-slate-500 text-sm font-medium">Valor Frete</span>
                                <span className="text-lg font-semibold text-slate-700">R$ {formatarMoeda(taxaEntrega)}</span>
                              </div>
                              <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-300">
                                <span className="text-gray-800 text-sm font-bold">Total Geral</span>
                                <span className="text-2xl font-black text-gray-800">R$ {formatarMoeda(totalGeral)}</span>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })()}

                  </div>

                  {/* Ações */}
                  <div className="p-4 bg-slate-50 flex items-center justify-center gap-3">
                    {podeImprimirProposta() ? (
                      <button 
                        onClick={() => handleImprimirProposta(1)} 
                        className="flex-1 text-white px-4 py-3 rounded-xl flex items-center justify-center gap-2 font-bold text-sm hover:shadow-lg hover:scale-[1.02] transition-all duration-300" 
                        style={{backgroundColor: '#0d5dfc'}}
                      >
                        <Printer size={18} />
                        Imprimir {dadosCompletos.tipo_documento === 'PEDIDO' ? 'Pedido' : 'Orçamento'}
                      </button>
                    ) : (
                      <div className="flex-1 bg-gray-100 text-gray-400 px-4 py-3 rounded-xl flex items-center justify-center gap-2 font-medium text-sm">
                        <Clock size={18} />
                        Aguardando Liberação
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tabela 2 */}
              {temItensTab2 ? (
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300">
                  {/* Banner do tipo de documento */}
                  <div className="bg-slate-400 text-white text-center py-2 font-bold text-sm uppercase tracking-wider">
                    {dadosCompletos.tipo_documento === 'PEDIDO' ? 'Pedido' : 'Orçamento'}
                  </div>
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white" style={{backgroundColor: '#00BEFF'}}>2</div>
                        <h3 className="font-bold text-slate-800 text-lg">Tabela 2</h3>
                      </div>
                      {statusTab2 && <StatusBadge status={statusTab2} tamanho="xs" />}
                    </div>
                    
                    {/* Valores - Valor Total, Frete e Total Geral */}
                    {(() => {
                      const taxaEntrega = dadosCompletos.tem_taxa_entrega ? (parseFloat(dadosCompletos.valor_taxa_entrega) || 0) : 0;
                      const totalGeral = valorTotalTab2 + taxaEntrega;
                      // Mostrar taxa de entrega para Vendas, Diretoria, Admin, Administrativo
                      const dep = (usuarioLogado?.departamento || '').toLowerCase();
                      const mostrarTaxaEntrega = dep === 'vendas' || dep === 'diretoria' || dep === 'admin' || dep === 'administrativo';
                      
                      return (
                        <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 mb-4">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500 text-sm font-medium">Valor Total</span>
                            <span className="text-2xl font-bold text-slate-800">R$ {formatarMoeda(valorTotalTab2)}</span>
                          </div>
                          {mostrarTaxaEntrega && taxaEntrega > 0 && (
                            <>
                              <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200">
                                <span className="text-slate-500 text-sm font-medium">Valor Frete</span>
                                <span className="text-lg font-semibold text-slate-700">R$ {formatarMoeda(taxaEntrega)}</span>
                              </div>
                              <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-300">
                                <span className="text-gray-800 text-sm font-bold">Total Geral</span>
                                <span className="text-2xl font-black text-gray-800">R$ {formatarMoeda(totalGeral)}</span>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })()}

                  </div>

                  {/* Ações */}
                  <div className="p-4 bg-slate-50 flex items-center justify-center gap-3">
                    {podeImprimirProposta() ? (
                      <button 
                        onClick={() => handleImprimirProposta(2)} 
                        className="flex-1 text-white px-4 py-3 rounded-xl flex items-center justify-center gap-2 font-bold text-sm hover:shadow-lg hover:scale-[1.02] transition-all duration-300" 
                        style={{backgroundColor: '#00BEFF'}}
                      >
                        <Printer size={18} />
                        Imprimir {dadosCompletos.tipo_documento === 'PEDIDO' ? 'Pedido' : 'Orçamento'}
                      </button>
                    ) : (
                      <div className="flex-1 bg-gray-100 text-gray-400 px-4 py-3 rounded-xl flex items-center justify-center gap-2 font-medium text-sm">
                        <Clock size={18} />
                        Aguardando Liberação
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Card de Tabela 2 não utilizada */
                temItensTab1 && (
                  <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-center hover:shadow-lg hover:-translate-y-2 hover:border-slate-300 transition-all duration-300">
                    <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center mb-3">
                      <FileText size={24} className="text-slate-400" />
                    </div>
                    <h3 className="font-semibold text-slate-600 mb-1">Tabela 2 não utilizada</h3>
                    <p className="text-sm text-slate-400">Este pedido possui apenas uma tabela de preços</p>
                  </div>
                )
              )}
            </div>

            {/* Card para pedidos que têm APENAS Produtos Especiais (sem forros/acessórios) */}
            {!temItensTab1 && !temItensTab2 && dadosCompletos.produtosEspeciais && dadosCompletos.produtosEspeciais.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300">
                  {/* Banner do tipo de documento */}
                  <div className="bg-slate-400 text-white text-center py-2 font-bold text-sm uppercase tracking-wider">
                    {dadosCompletos.tipo_documento === 'PEDIDO' ? 'Pedido' : 'Orçamento'}
                  </div>
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white bg-gradient-to-br from-gray-500 to-gray-600">
                          <Package size={20} />
                        </div>
                        <h3 className="font-bold text-slate-800 text-lg">Produtos Especiais</h3>
                      </div>
                      <StatusBadge status="VIÁVEL" tamanho="xs" />
                    </div>
                    
                    {/* Valores */}
                    {(() => {
                      const valorProdutosEspeciais = (dadosCompletos.produtosEspeciais || []).reduce((sum, p) => sum + (parseFloat(p.total) || 0), 0);
                      const taxaEntrega = dadosCompletos.tem_taxa_entrega ? (parseFloat(dadosCompletos.valor_taxa_entrega) || 0) : 0;
                      const totalGeral = valorProdutosEspeciais + taxaEntrega;
                      const dep = (usuarioLogado?.departamento || '').toLowerCase();
                      const mostrarTaxaEntrega = dep === 'vendas' || dep === 'diretoria' || dep === 'admin' || dep === 'administrativo';
                      
                      return (
                        <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 mb-4">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500 text-sm font-medium">Valor Total</span>
                            <span className="text-2xl font-bold text-slate-800">R$ {formatarMoeda(valorProdutosEspeciais)}</span>
                          </div>
                          {mostrarTaxaEntrega && taxaEntrega > 0 && (
                            <>
                              <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200">
                                <span className="text-slate-500 text-sm font-medium">Valor Frete</span>
                                <span className="text-lg font-semibold text-slate-700">R$ {formatarMoeda(taxaEntrega)}</span>
                              </div>
                              <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-300">
                                <span className="text-gray-800 text-sm font-bold">Total Geral</span>
                                <span className="text-2xl font-black text-gray-800">R$ {formatarMoeda(totalGeral)}</span>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Ações */}
                  <div className="p-4 bg-slate-50 flex items-center justify-center gap-3">
                    <button 
                      onClick={() => handleImprimirProposta(1)} 
                      className="flex-1 text-white px-4 py-3 rounded-xl flex items-center justify-center gap-2 font-bold text-sm hover:shadow-lg hover:scale-[1.02] transition-all duration-300" 
                      style={{backgroundColor: '#6b7280'}}
                    >
                      <Printer size={18} />
                      Imprimir {dadosCompletos.tipo_documento === 'PEDIDO' ? 'Pedido' : 'Orçamento'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Linha Divisória Final */}
            <div className="w-full h-px bg-gray-200 mt-6"></div>
            
            {/* ========================================
                SEÇÃO FAZER PEDIDO - Design moderno (padrão NovoPedido)
            ======================================== */}
            {(dadosCompletos.fase_atual === 'ORCAMENTO_APROVADO' || dadosCompletos.fase_atual === 'APROVADO_COM_AJUSTES') && (
              <div 
                id="secao-fazer-pedido" 
                className="relative mt-6 bg-white rounded-2xl py-6 px-8 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden"
              >
                {/* Barra gradiente no topo */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 via-green-400 to-emerald-400"></div>
                
                {/* Conteúdo em linha */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-green-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-200 rotate-3 hover:rotate-0 transition-transform duration-300 flex-shrink-0">
                      <ShoppingCart size={28} className="text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-xl font-bold text-gray-800">Confirmar Pedido</h3>
                      <p className="text-gray-500 text-sm">O pedido será enviado para processamento</p>
                    </div>
                  </div>
                  
                  {/* Botões de ação */}
                  <div className="flex items-center gap-3">
                    {/* Botão Cancelar */}
                    <button
                      onClick={(e) => { e.stopPropagation(); cancelarOrcamento(); }}
                      disabled={processandoCancelar}
                      className={`flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-full text-sm font-bold shadow-lg shadow-red-200 hover:scale-105 active:scale-95 transition-all duration-300 ${processandoCancelar ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      <X size={16} />
                      {processandoCancelar ? 'CANCELANDO...' : 'CANCELAR'}
                    </button>
                    
                    {/* Botão Confirmar */}
                    <button
                      onClick={(e) => { e.stopPropagation(); fazerPedido(); }}
                      disabled={processandoFazerPedido}
                      className={`flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-full text-sm font-bold shadow-lg shadow-emerald-200 hover:scale-105 active:scale-95 transition-all duration-300 ${processandoFazerPedido ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                      {processandoFazerPedido ? 'PROCESSANDO...' : 'CONFIRMAR PEDIDO'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ========================================
                SEÇÃO EDITAR PEDIDO - Quando solicitada edição
            ======================================== */}
            {dadosCompletos.fase_atual === 'SOLICITAR_EDICAO' && (
              <div 
                id="secao-editar-pedido" 
                className="relative mt-6 bg-white rounded-2xl py-6 px-8 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden"
              >
                {/* Barra gradiente no topo */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-orange-400"></div>
                
                {!modoEdicao ? (
                  /* Card inicial para iniciar edição */
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-200 rotate-3 hover:rotate-0 transition-transform duration-300 flex-shrink-0">
                        <Edit2 size={28} className="text-white" />
                      </div>
                      <div className="text-left">
                        <h3 className="text-xl font-bold text-gray-800">Edição Solicitada!</h3>
                        <p className="text-gray-500 text-sm">O administrativo solicitou alterações neste orçamento. Clique para editar os dados.</p>
                      </div>
                    </div>
                    
                    <button
                      onClick={iniciarEdicaoReal}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full text-sm font-bold shadow-lg shadow-orange-200 hover:scale-105 active:scale-95 transition-all duration-300"
                    >
                      <Edit2 size={18} />
                      EDITAR PEDIDO
                      <ArrowRight size={18} />
                    </button>
                  </div>
                ) : (
                  /* Modo de edição ativo - Mostrar apenas os botões de ação */
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-200 animate-pulse flex-shrink-0">
                        <Edit2 size={28} className="text-white" />
                      </div>
                      <div className="text-left">
                        <h3 className="text-xl font-bold text-orange-600">Modo de Edição Ativo</h3>
                        <p className="text-gray-500 text-sm">Edite os dados nos campos destacados em laranja acima e depois salve as alterações.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <button
                        onClick={cancelarEdicao}
                        className="flex items-center gap-2 px-5 py-2.5 bg-gray-200 text-gray-700 rounded-full text-sm font-bold hover:bg-gray-300 hover:scale-105 transition-all"
                      >
                        <X size={16} />
                        Cancelar
                      </button>
                      <button
                        onClick={salvarEdicao}
                        disabled={processandoEdicao}
                        className={`flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full text-sm font-bold shadow-lg shadow-orange-200 hover:scale-105 active:scale-95 transition-all ${processandoEdicao ? 'opacity-70 cursor-not-allowed' : ''}`}
                      >
                        {processandoEdicao ? (
                          <>
                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            Salvando...
                          </>
                        ) : (
                          <>
                            <Check size={16} />
                            Salvar e Reenviar para Aprovação
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ========================================
                SEÇÃO SALVAR EDIÇÃO - Para edições em qualquer fase (exceto SOLICITAR_EDICAO que tem seu próprio)
            ======================================== */}
            {modoEdicao && dadosCompletos.fase_atual !== 'SOLICITAR_EDICAO' && (
              <div 
                id="secao-salvar-edicao" 
                className="relative mt-6 bg-white rounded-2xl py-6 px-8 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden"
              >
                {/* Barra gradiente no topo */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-orange-400"></div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-200 animate-pulse flex-shrink-0">
                      <Edit2 size={28} className="text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-xl font-bold text-orange-600">Modo de Edição Ativo</h3>
                      <p className="text-gray-500 text-sm">
                        {verificarAlteracoes() 
                          ? 'Você fez alterações no pedido. Clique em "Salvar Edição" para confirmar.'
                          : 'Nenhuma alteração detectada ainda. Modifique os dados acima para habilitar o salvamento.'
                        }
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <button
                      onClick={cancelarEdicao}
                      className="flex items-center gap-2 px-5 py-2.5 bg-gray-200 text-gray-700 rounded-full text-sm font-bold hover:bg-gray-300 hover:scale-105 transition-all"
                    >
                      <X size={16} />
                      Cancelar
                    </button>
                    <button
                      onClick={salvarEdicao}
                      disabled={processandoEdicao || !verificarAlteracoes()}
                      className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold shadow-lg transition-all ${
                        verificarAlteracoes() 
                          ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-orange-200 hover:scale-105 active:scale-95' 
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      } ${processandoEdicao ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {processandoEdicao ? (
                        <>
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                          Salvando...
                        </>
                      ) : (
                        <>
                          <Check size={16} />
                          Salvar Edição
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    );
  }

  // ==================== TELA DE LISTA DE PEDIDOS ====================
  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {loadingDetalhes && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fadeIn">
          <Loading texto="Carregando pedido..." tamanho="lg" fullScreen={false} />
        </div>
      )}

      {/* Modal de Confirmação de Edição */}
      {mostrarConfirmacaoEdicao && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden transform animate-slideIn">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Edit2 size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Confirmar Edição</h3>
                  <p className="text-sm text-white/80">Ação necessária</p>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle size={20} className="text-amber-600" />
                </div>
                <div>
                  <p className="text-gray-700 font-medium mb-2">
                    Você tem certeza que deseja editar este pedido?
                  </p>
                  <p className="text-sm text-gray-500">
                    Ao entrar no modo de edição, você poderá alterar os dados do documento. 
                    <span className="font-medium text-blue-600"> Lembre-se: somente alterações serão salvas.</span>
                  </p>
                </div>
              </div>
              
              {/* Buttons */}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setMostrarConfirmacaoEdicao(false)}
                  className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                >
                  Não, Cancelar
                </button>
                <button
                  onClick={confirmarEIniciarEdicao}
                  className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-medium hover:from-blue-600 hover:to-cyan-600 transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
                >
                  <Edit2 size={18} />
                  Sim, Editar Pedido
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto">
        {/* Cabeçalho - Gradiente Azul/Ciano Original */}
        <div 
          className="rounded-2xl shadow-lg p-8 mb-6 text-white hover:shadow-2xl transition-all duration-300 cursor-default relative overflow-hidden group"
          style={{ 
            background: 'linear-gradient(135deg, #0D5DFC 0%, #0099cc 25%, #0D5DFC 50%, #0099cc 75%, #0D5DFC 100%)', 
            backgroundSize: '400% 400%',
            animation: 'gradientShift 8s ease infinite'
          }}
        >
          {/* Efeito de brilho no hover */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
          </div>
          
          <div className="flex items-center justify-between mb-2 relative z-10">
            <div>
              <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
                <ClipboardList size={36} />
                Consulta de Pedidos
              </h1>
              <p className="text-blue-100">{podeVerTodos() ? 'Visualizando todos os pedidos do sistema' : `Visualizando pedidos de: ${getNomeUsuario()}`}</p>
            </div>
            <button
              onClick={() => carregarPedidos()}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 border border-white/30 rounded-xl text-white font-semibold transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RotateCcw size={18} className={loading ? 'animate-spin' : ''} />
              Atualizar
            </button>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-7 gap-2 mt-6 relative z-10">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20 hover:bg-white/20 hover:scale-105 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-default group"><div className="flex items-center gap-2 mb-2"><FileText size={16} className="text-white group-hover:rotate-12 transition-transform duration-300" /><span className="text-xs font-medium text-white/80">Total</span></div><div className="text-2xl font-bold">{loading ? '...' : totalPedidos}</div></div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20 hover:bg-white/20 hover:scale-105 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-default group"><div className="flex items-center gap-2 mb-2"><DollarSign size={16} className="text-white group-hover:scale-110 transition-transform duration-300" /><span className="text-xs font-medium text-white/80">Valor Total</span></div><div className="text-lg font-bold whitespace-nowrap">{loading ? '...' : `R$ ${totalGeral.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}</div></div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20 hover:bg-white/20 hover:scale-105 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-default group"><div className="flex items-center gap-2 mb-2"><PackageCheck size={16} className="text-white group-hover:rotate-12 transition-transform duration-300" /><span className="text-xs font-medium text-white/80">Faturados</span></div><div className="text-2xl font-bold">{loading ? '...' : faturados}</div></div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20 hover:bg-white/20 hover:scale-105 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-default group"><div className="flex items-center gap-2 mb-2"><CheckCircle size={16} className="text-white group-hover:rotate-12 transition-transform duration-300" /><span className="text-xs font-medium text-white/80">Confirmados</span></div><div className="text-2xl font-bold">{loading ? '...' : confirmados}</div></div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20 hover:bg-white/20 hover:scale-105 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-default group"><div className="flex items-center gap-2 mb-2"><Clock size={16} className="text-white group-hover:rotate-12 transition-transform duration-300" /><span className="text-xs font-medium text-white/80">Aguardando</span></div><div className="text-2xl font-bold">{loading ? '...' : aguardando}</div></div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20 hover:bg-white/20 hover:scale-105 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-default group"><div className="flex items-center gap-2 mb-2"><XCircle size={16} className="text-white group-hover:rotate-12 transition-transform duration-300" /><span className="text-xs font-medium text-white/80">Cancelados</span></div><div className="text-2xl font-bold">{loading ? '...' : cancelados}</div></div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20 hover:bg-white/20 hover:scale-105 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-default group"><div className="flex items-center gap-2 mb-2"><Archive size={16} className="text-white group-hover:rotate-12 transition-transform duration-300" /><span className="text-xs font-medium text-white/80">Arquivados</span></div><div className="text-2xl font-bold">{loading ? '...' : arquivados}</div></div>
          </div>
        </div>

        {/* Linha divisória */}
        <div className="w-full h-px bg-gray-200 my-6"></div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6 hover:shadow-xl hover:scale-[1.01] hover:-translate-y-1 transition-all duration-300">
          <div className="flex gap-3 mb-4">
            <div className="flex-1 relative group">
              <Search className="absolute left-4 top-3.5 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-300" size={20} />
              <input type="text" placeholder="Buscar por cliente, CPF/CNPJ, proposta ou responsável..." value={filtros.busca} onChange={(e) => setFiltros({ ...filtros, busca: e.target.value })} className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-blue-300 hover:shadow-sm transition-all duration-300" />
            </div>
            <button onClick={() => setMostrarFiltros(!mostrarFiltros)} className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 hover:scale-105 hover:shadow-lg flex items-center gap-2 font-semibold transition-all duration-300 shadow-md group"><Filter size={20} className="group-hover:rotate-180 transition-transform duration-500" />Filtros{mostrarFiltros ? <ChevronUp size={18} className="group-hover:-translate-y-0.5 transition-transform duration-300" /> : <ChevronDown size={18} className="group-hover:translate-y-0.5 transition-transform duration-300" />}</button>
          </div>
          {mostrarFiltros && (
            <div className="border-t-2 border-gray-100 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Tipo</label><select value={filtros.tipo} onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })} className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer"><option value="">Todos os tipos</option><option value="ORCAMENTO">Orçamento</option><option value="PEDIDO">Pedido</option></select></div>
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Status do Pedido</label><select value={filtros.fase} onChange={(e) => setFiltros({ ...filtros, fase: e.target.value })} className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer"><option value="">Todos os status</option><option value="AGUARDANDO_PROXIMA_ETAPA">Aguardando Próxima Etapa</option><option value="PEDIDO_CONFIRMADO">Pedido Confirmado</option><option value="PEDIDO_FATURADO">Pedido Faturado</option><option value="CANCELADO">Cancelado</option><option value="PEDIDO_CANCELADO_POS_FATURAMENTO">  → Apenas Cancelado Após Faturamento</option><option value="ARQUIVADO">Arquivado</option></select></div>
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Classificação do Pedido</label><select value={filtros.scoreVenda} onChange={(e) => setFiltros({ ...filtros, scoreVenda: e.target.value })} className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer"><option value="">Todas as classificações</option><option value="EXCELENTE">Excelente</option><option value="BOA VENDA">Boa Venda</option><option value="VIÁVEL">Viável</option><option value="EM ANÁLISE">Em Análise</option><option value="INVIÁVEL">Inviável</option></select></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Data Início</label><input type="date" value={filtros.dataInicio} onChange={(e) => setFiltros({ ...filtros, dataInicio: e.target.value })} className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer" /></div>
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Data Fim</label><input type="date" value={filtros.dataFim} onChange={(e) => setFiltros({ ...filtros, dataFim: e.target.value })} className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer" /></div>
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Responsável</label><select value={filtros.responsavel} onChange={(e) => setFiltros({ ...filtros, responsavel: e.target.value })} className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer"><option value="">Todos os responsáveis</option>{listaResponsaveis.map((resp, idx) => <option key={idx} value={resp}>{resp}</option>)}</select></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Data Faturamento Início</label><input type="date" value={filtros.dataFaturamentoInicio} onChange={(e) => setFiltros({ ...filtros, dataFaturamentoInicio: e.target.value })} className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer" /></div>
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Data Faturamento Fim</label><input type="date" value={filtros.dataFaturamentoFim} onChange={(e) => setFiltros({ ...filtros, dataFaturamentoFim: e.target.value })} className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer" /></div>
              </div>
              {/* Filtro de Produtos Especiais - apenas para Admin, Diretoria, Administrativo, Vendas */}
              {(() => {
                const dep = (usuarioLogado?.departamento || '').toLowerCase().trim();
                const podeVerFiltroProdutosEspeciais = ['admin', 'diretoria', 'administrativo', 'vendas'].includes(dep);
                return podeVerFiltroProdutosEspeciais && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div><label className="block text-sm font-semibold text-gray-700 mb-2">Produtos Especiais</label><select value={filtros.produtosEspeciais} onChange={(e) => setFiltros({ ...filtros, produtosEspeciais: e.target.value })} className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer"><option value="">Todos</option><option value="SIM">Com Produtos Especiais</option><option value="NAO">Sem Produtos Especiais</option></select></div>
                  </div>
                );
              })()}
              <button onClick={() => setFiltros({ busca: '', tipo: '', fase: '', scoreVenda: '', responsavel: '', dataInicio: '', dataFim: '', dataFaturamentoInicio: '', dataFaturamentoFim: '', produtosEspeciais: '' })} className="bg-gray-500 text-white px-6 py-2.5 rounded-xl hover:bg-gray-600 hover:scale-105 hover:shadow-lg flex items-center gap-2 font-medium transition-all duration-300 group"><X size={18} className="group-hover:rotate-90 transition-transform duration-300" />Limpar Filtros</button>
            </div>
          )}
        </div>

        {/* Linha divisória */}
        <div className="w-full h-px bg-gray-200 my-6"></div>

        {/* Lista */}
        {loading ? (
          <Loading texto="Carregando pedidos..." tamanho="lg" fullScreen={false} />
        ) : erroCarregamento ? (
          /* Falha de carregamento é diferente de "não há pedidos". Antes as duas
             situações mostravam a mesma mensagem e o usuário achava que os
             pedidos dele tinham sumido do sistema. */
          <div className="bg-white rounded-xl shadow-md p-12 text-center border border-red-100">
            <AlertCircle size={48} className="mx-auto text-red-400 mb-4" />
            <p className="text-gray-700 text-lg font-medium mb-2">Não foi possível carregar seus pedidos</p>
            <p className="text-gray-500 mb-1">{erroCarregamento}</p>
            <p className="text-gray-400 text-sm mb-6">Seus pedidos continuam salvos — foi apenas esta consulta que falhou.</p>
            <button
              onClick={() => carregarPedidos()}
              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-700 hover:shadow-lg transition-all duration-300 inline-flex items-center gap-2"
            >
              <RotateCcw size={18} />
              Tentar novamente
            </button>
          </div>
        ) : pedidosFiltrados.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center hover:shadow-xl hover:scale-[1.01] hover:-translate-y-1 transition-all duration-300 cursor-default"><FileText size={48} className="mx-auto text-gray-300 mb-4" /><p className="text-gray-500 text-lg font-medium mb-2">Nenhum pedido encontrado</p><p className="text-gray-400">Tente ajustar os filtros de busca</p></div>
        ) : (
          <>
          <div className="space-y-4">
            {pedidosFiltrados.slice(0, quantidadeVisivel).map((pedido) => {
              const clienteNome = pedido.clientes?.nome || 'Cliente não informado';
              const temTab1 = pedido.valor_tabela1 > 0;
              const temTab2 = pedido.valor_tabela2 > 0;
              const valorTab1 = parseFloat(pedido.valor_tabela1) || 0;
              const valorTab2 = parseFloat(pedido.valor_tabela2) || 0;
              const valorProdutosEspeciais = parseFloat(pedido.valor_produtos_especiais) || 0;
              const temProdutosEspeciais = valorProdutosEspeciais > 0;
              const valorTotalGeral = valorTab1 + valorTab2 + valorProdutosEspeciais;
              
              // Usar status_geral do banco se disponível, senão calcular
              const statusGeral = calcularStatusGeralPedido(pedido);
              
              return (
                <div key={pedido.id} onClick={() => carregarDetalhesPedido(pedido)} className="bg-white rounded-2xl shadow-md hover:shadow-2xl hover:scale-[1.01] transition-all duration-300 border border-gray-100 hover:border-blue-200 cursor-pointer group overflow-hidden">
                  <div className="p-5">
                    {/* Header do Card */}
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-2.5 py-0.5 rounded-lg font-bold text-xs shadow-sm">#{pedido.id}</span>
                          <span className="bg-gray-100 text-gray-600 px-2.5 py-0.5 rounded-lg font-medium text-xs">{pedido.numero_proposta}</span>
                        </div>
                        <h3 className="text-lg font-bold text-gray-800 mb-1">{clienteNome}</h3>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><User size={12} />{pedido.vendedor}</span>
                          <span className="flex items-center gap-1"><Calendar size={12} /><span className="font-medium text-gray-600">Data Pedido:</span>{formatarData(pedido.data || pedido.created_at)} {formatarHora(pedido.hora)}</span>
                          <span className="flex items-center gap-1"><Calendar size={12} /><span className="font-medium text-gray-600">Data Faturamento:</span>{pedido.faturado_data ? formatarDataHora(pedido.faturado_data) : '--'}</span>
                          {podeVerLimiteExcedido() && pedido.extrapolou_limite_credito && (
                            <span className="flex items-center gap-1 bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-bold text-[10px] uppercase tracking-wide border border-red-200">
                              <AlertTriangle size={10} />Limite Excedido
                            </span>
                          )}
                          {temProdutosEspeciais && (
                            <span className="flex items-center gap-1 bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-bold text-[10px] uppercase tracking-wide border border-slate-200">
                              <Package size={10} />Venda Produtos Especiais
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
                        <span className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-4 py-2 rounded-xl flex items-center gap-2 font-semibold text-sm shadow-lg hover:shadow-xl transition-all duration-200">
                          <Eye size={16} />Ver Detalhes
                        </span>
                      </div>
                    </div>
                    
                    {/* Conteúdo: Tabelas + Status Geral */}
                    <div className="flex gap-4 mt-3">
                      {/* Lado Esquerdo: Tabela 1 e Tabela 2 empilhadas */}
                      <div className="flex-1 space-y-2">
                        {/* Tabela 1 */}
                        <div className={`flex items-center justify-between px-4 py-2.5 rounded-xl ${temTab1 ? 'bg-blue-50 border border-blue-100' : 'bg-gray-50 border border-gray-100'}`}>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white text-sm shadow-sm" style={{backgroundColor: '#0d5dfc'}}>1</div>
                            <span className="font-semibold text-gray-700 text-sm">Tabela 1</span>
                          </div>
                          <div className="flex items-center gap-4">
                            {temTab1 ? (
                              <>
                                <span className="font-bold text-gray-800 min-w-[120px] text-right">R$ {valorTab1.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                                <StatusBadge status={getStatusPontuacaoCorreto(pedido.status_tabela1, pedido.pontuacao_tabela1)} tamanho="xs" />
                              </>
                            ) : (
                              <span className="text-gray-400 text-sm">Sem itens</span>
                            )}
                          </div>
                        </div>
                        
                        {/* Tabela 2 */}
                        <div className={`flex items-center justify-between px-4 py-2.5 rounded-xl ${temTab2 ? 'bg-cyan-50 border border-cyan-100' : 'bg-gray-50 border border-gray-100'}`}>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white text-sm shadow-sm" style={{backgroundColor: '#00BEFF'}}>2</div>
                            <span className="font-semibold text-gray-700 text-sm">Tabela 2</span>
                          </div>
                          <div className="flex items-center gap-4">
                            {temTab2 ? (
                              <>
                                <span className="font-bold text-gray-800 min-w-[120px] text-right">R$ {valorTab2.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                                <StatusBadge status={getStatusPontuacaoCorreto(pedido.status_tabela2, pedido.pontuacao_tabela2)} tamanho="xs" />
                              </>
                            ) : (
                              <span className="text-gray-400 text-sm">Sem itens</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Lado Direito: Dois cards lado a lado */}
                      <div className="flex gap-3">
                        {/* Card Total do Pedido */}
                        <div className="w-52 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 p-4 flex flex-col items-center justify-center">
                          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Total do Pedido</span>
                          <span className="text-xl font-bold text-slate-800 mb-2">R$ {valorTotalGeral.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                          <StatusBadge status={statusGeral} tamanho="xs" />
                        </div>
                        
                        {/* Card Status do Pedido (Tipo + Fase) */}
                        <div className="w-52 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 p-4 flex flex-col items-center justify-center">
                          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Status do Pedido</span>
                          <span className="px-4 py-1.5 rounded-full text-xs font-bold mb-2 bg-white border border-slate-200 text-slate-700 shadow-sm">
                            {/* Tipo do documento direto do banco */}
                            {pedido.tipo_documento === 'PEDIDO' ? 'Pedido' : 'Orçamento'}
                          </span>
                          <FaseBadge fase={pedido.fase_atual} tamanho="xs" fullLabel={true} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Botão Carregar Mais ou Mensagem para usar filtros */}
          {pedidosFiltrados.length > quantidadeVisivel && (
            <div className="mt-6">
              {quantidadeVisivel < 20 ? (
                <button 
                  onClick={() => setQuantidadeVisivel(prev => prev + 10)}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-700 hover:scale-[1.01] hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 group"
                >
                  <ChevronDown size={20} className="group-hover:translate-y-0.5 transition-transform duration-300" />
                  Carregar mais pedidos
                  <span className="bg-white/20 px-2 py-0.5 rounded-lg text-sm">+10</span>
                </button>
              ) : (
                <button 
                  onClick={() => {
                    setMostrarFiltros(true);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="w-full bg-gradient-to-r from-gray-500 to-gray-600 text-white py-4 rounded-xl font-semibold hover:from-gray-600 hover:to-gray-700 hover:scale-[1.01] hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 group"
                >
                  <Filter size={20} className="group-hover:rotate-12 transition-transform duration-300" />
                  Use os filtros para encontrar mais pedidos
                  <ChevronUp size={20} className="group-hover:-translate-y-0.5 transition-transform duration-300" />
                </button>
              )}
            </div>
          )}
          
          {/* Indicador de pedidos exibidos */}
          <div className="mt-4 text-center text-sm text-gray-500">
            Exibindo {Math.min(quantidadeVisivel, pedidosFiltrados.length)} de {pedidosFiltrados.length} pedidos
          </div>
          </>
        )}
        
        {/* Linha divisória final */}
        <div className="w-full h-px bg-gray-200 my-8"></div>
      </div>
    </div>
  );
};

export default ConsultaPedidos;